import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Filter,
  Settings,
  MoreHorizontal,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  ArrowRight,
  Loader2,
  Printer,
  Mail,
  Trash2,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  AlertTriangle,
  CreditCard,
  Banknote,
  Edit,
  Repeat,
  FileDown,
  FileUp,
  Download,
  Upload,
  MessageSquare,
  User,
  Calendar,
  X,
  Check,
  Link as LinkIcon,
  QrCode,
  Copy,
  Bell,
  Wallet,
  Undo,
  Tag,
  Package,
  Truck,
  UserCheck,
  Camera,
  Signature,
  RotateCcw,
  FileSpreadsheet,
  Sparkles
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import SignatureCanvas from 'react-signature-canvas';
import { generateInvoicePdf } from '../lib/invoicePdf';
import * as XLSX from 'xlsx';
import { cn, getPortalUrl } from '../lib/utils';
import { getDefaultCurrency, formatCurrency } from '../lib/currency';
import { CustomSelect } from './ui/CustomSelect';
import { SearchableSelect } from './ui/SearchableSelect';
import { Logger } from '../services/historyService';
import DiscussionsView from './DiscussionsView';
import DocumentChatterPanel from './DocumentChatterPanel';
import { StatusBadge } from './workflow/StatusBadge';
import Papa from 'papaparse';
import { InvoiceRemindersSettings } from '../types';
import {
  db,
  auth,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  arrayUnion,
  increment,
  storage,
  ref,
  uploadBytes,
  getDownloadURL
} from '../firebase';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  const isPermissionDenied = error instanceof Error && error.message.includes('Missing or insufficient permissions');
  const isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  if (isLocalhost && isPermissionDenied) {
    console.warn(`Ignoring Firestore permission error at ${path} during local development.`);
    return;
  }
  throw new Error(JSON.stringify(errInfo));
}
import { DocumentPrintLayout } from './ui/DocumentPrintLayout';
import { postInvoiceJournal, postPaymentJournal, postCreditNoteJournal, reverseAutoJournalsForSource } from '../services/accountingService';
import { notify } from '../services/notify';
import { derivePaymentStatus, normalizeDocumentStatus } from '../lib/documentStatus';

// Utility for tailwind classes
// Moved to src/lib/utils.ts

interface ActionLogEntry {
  action: string;
  user: string;
  email: string;
  timestamp: any;
  details?: string;
  deliveryNoteId?: string;
}

interface DeliveryNoteItem {
  itemId: string;
  itemCode: string;
  name: string;
  description: string;
  quantityOrdered: number;
  quantityShipped: number;
  qtyToShipNow?: number;
  unitPrice: number;
  lotNumber?: string;
  expiryDate?: string;
}

interface DeliveryNote {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  orgId: string;
  contactId: string;
  contactName: string;
  items: DeliveryNoteItem[];
  assignedPersonnelId?: string;
  assignedPersonnelName?: string;
  assignedPersonnelEmail?: string;
  status: 'Draft' | 'Assigned' | 'Shipped' | 'Delivered';
  receiverName?: string;
  signatureUrl?: string;
  photoUrl?: string;
  notes?: string;
  createdAt: any;
  shippedAt?: any;
  deliveredAt?: any;
  updatedAt?: any;
  brandingTheme?: string;
  warehouseId?: string;
  warehouseName?: string;
  storeId?: string;
}

interface Invoice {
  id: string;
  number: string;
  contactId: string;
  contactName?: string;
  contactEmail?: string;
  date: string;
  dueDate: string;
  reference?: string;
  currency: string;
  brandingTheme: string;
  amountsAre: 'Tax Inclusive' | 'Tax Exclusive' | 'No Tax';
  totalAmount: number;
  status: 'Draft' | 'Awaiting Approval' | 'Awaiting Payment' | 'Paid' | 'Void' | 'Deleted' | 'draft' | 'done' | 'cancelled';
  legacyStatus?: string;
  items: any[];
  termsAndConditions?: string;
  billingAddress?: string;
  deliveryAddress?: string;
  createdBy?: string;
  createdAt?: any;
  createdByName?: string;
  createdByEmail?: string;
  deletedBy?: string;
  deletedByName?: string;
  deletedByEmail?: string;
  deletedAt?: any;
  actionDetails?: string;
  actionLog?: ActionLogEntry[];
  source?: string;
  quoteId?: string;
  sourceQuoteId?: string;
  sourceQuoteNumber?: string;
  purchaseOrderId?: string;
  paymentReceived?: number;
  amountDue?: number;
  payments?: any[];
  paymentDate?: string;
  paymentAccount?: string;
  paymentReference?: string;
  type: 'quote' | 'invoice' | 'purchase_order' | 'bill' | 'repeating_invoice' | 'credit_note';
  threads?: any[];
  attachments?: { name: string, url: string, type: string, size: number, createdAt: string }[];
  time?: string;
  notes?: string;
  trackingCategory?: string;
  tags?: string[];
  linkedInvoiceId?: string;
  appliedInvoiceId?: string;
  appliedInvoiceNumber?: string;
  appliedAmount?: number;
  repeatingConfig?: {
    frequency: number;
    frequencyUnit: 'Week' | 'Month';
    startDate: string;
    endDate?: string;
    dueDateDays: number;
    dueDateType: 'daysAfterInvoiceDate' | 'dayOfFollowingMonth' | 'dayOfCurrentMonth' | 'daysAfterEndOfInvoiceMonth';
    approveStatus: 'Draft' | 'Approved' | 'ApprovedForSending';
    lastGeneratedDate?: string;
  };
}

const hasLocalPostgresSession = () =>
  typeof window !== 'undefined' && !!localStorage.getItem('app_auth_token');

const localApiHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('app_auth_token') || ''}`,
});

const getInvoiceLineUom = (line: any, item?: any): string =>
  line.uom || line.uomName || line.salesUomName || item?.salesUomName || item?.uom || item?.unitOfMeasure || '';

export default function InvoicesView({  sourceFilter, orgId, orgSettings: providedOrgSettings, onNavigate, initialInvoiceId, onClearInitial , language = 'en' }: {  sourceFilter?: string, orgId: string, orgSettings?: any, onNavigate?: (tab: string, id?: string) => void, initialInvoiceId?: string | null, onClearInitial?: () => void , language?: string }) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [itemsList, setItemsList] = useState<any[]>([]);
  const [accountsList, setAccountsList] = useState<any[]>([]);
  const [bankAccountsList, setBankAccountsList] = useState<any[]>([]);
  const [brandingThemes, setBrandingThemes] = useState<any[]>([]);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    number: true,
    reference: true,
    contact: true,
    date: true,
    dueDate: true,
    paid: true,
    due: true,
    total: true,
    status: true,
    tag1: true,
    tag2: true,
    tag3: true,
    tag4: true,
    tag5: true,
  });
  const [columnOrder, setColumnOrder] = useState<string[]>([
    'number', 'reference', 'contact', 'date', 'dueDate', 'paid', 'due', 'total', 'status', 'tag1', 'tag2', 'tag3', 'tag4', 'tag5'
  ]);
  const [showColumnGear, setShowColumnGear] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportOptionsOpen, setIsImportOptionsOpen] = useState(false);
  const [isExportOptionsOpen, setIsExportOptionsOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [docToPrint, setDocToPrint] = useState<any>(null);
  const [printType, setPrintType] = useState<'invoice' | 'quote' | 'delivery_note' | 'purchase_order'>('invoice');
  const [organization, setOrganization] = useState<any>(null);

  useEffect(() => {
    if (!orgId) return;

    const fetchOrg = async () => {
      if (hasLocalPostgresSession()) {
        try {
          const response = await fetch(`/api/organizations/${orgId}`, { headers: localApiHeaders() });
          if (!response.ok) throw new Error('Could not load organization');
          setOrganization(await response.json());
        } catch (error) {
          console.warn('Local organization load failed:', error);
        }
        return;
      }

      const docSnap = await getDoc(doc(db, 'organizations', orgId));
      if (docSnap.exists()) {
        setOrganization({ id: docSnap.id, ...docSnap.data() });
      }
    };
    fetchOrg();
  }, [orgId]);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showGetLinkModal, setShowGetLinkModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showHistoryNotes, setShowHistoryNotes] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const handleSelectInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    onNavigate?.('invoices', invoice.id);
  };
  const handleCloseInvoice = () => {
    setSelectedInvoice(null);
    onNavigate?.('invoices');
  };
  const [isEditing, setIsEditing] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [showThread, setShowThread] = useState<string | null>(null);

  const handleExportExcel = () => {
    const data = filteredInvoices.map(invoice => {
      const contact = contacts.find(c => c.id === invoice.contactId);
      return {
        'Number': invoice.number,
        'Ref': invoice.reference || '',
        'Customer': contact?.name || invoice.contactName || 'Walk-in Customer',
        'Date': invoice.date,
        'Due Date': invoice.dueDate,
        'Paid': invoice.paymentReceived || 0,
        'Due': invoice.totalAmount - (invoice.paymentReceived || 0),
        'Total': invoice.totalAmount,
        'Status': invoice.status,
        'Tag 1': invoice.tags?.[0] || '',
        'Tag 2': invoice.tags?.[1] || '',
        'Tag 3': invoice.tags?.[2] || '',
        'Tag 4': invoice.tags?.[3] || '',
        'Tag 5': invoice.tags?.[4] || '',
        'Last Action By': invoice.actionLog?.[0]?.user || invoice.createdByName || invoice.createdBy || 'System',
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");
    XLSX.writeFile(workbook, `Invoices_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
    notify.confirm("Excel file exported successfully");
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length || !selectedInvoice) return;

    const file = e.target.files[0];
    if (file.size > 5 * 1024 * 1024) {
      notify.error('File size must be less than 5MB');
      return;
    }

    if (hasLocalPostgresSession()) {
      setIsUploading(true);
      setUploadProgress(10);

      try {
        const targetPath = `attachments/${selectedInvoice.id}/${Date.now()}_${file.name}`;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('targetPath', targetPath);

        const uploadResponse = await fetch('/api/files/upload', {
          method: 'POST',
          headers: localApiHeaders(),
          body: formData,
        });
        if (!uploadResponse.ok) throw new Error('Could not upload file');

        const uploadResult = await uploadResponse.json();
        const uploadedFile = uploadResult.file || uploadResult;
        const url = uploadedFile.url || uploadedFile.downloadUrl || uploadedFile.downloadURL;
        if (!url) throw new Error('Upload did not return a file URL');

        const attachment = {
          ...(uploadedFile.id ? { id: uploadedFile.id } : {}),
          name: uploadedFile.name || file.name,
          url,
          type: uploadedFile.type || file.type || 'application/octet-stream',
          size: uploadedFile.size || file.size,
          createdAt: uploadedFile.createdAt || new Date().toISOString(),
        };
        const attachments = [...(selectedInvoice.attachments || []), attachment];
        const documentResponse = await fetch(`/api/organizations/${orgId}/documents/${selectedInvoice.id}`, {
          method: 'PATCH',
          headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ attachments }),
        });
        if (!documentResponse.ok) throw new Error('Could not attach file to invoice');

        const updatedInvoice = await documentResponse.json() as Invoice;
        setSelectedInvoice(updatedInvoice);
        setInvoices(prev => prev.map(invoice => invoice.id === updatedInvoice.id ? updatedInvoice : invoice));
        notify.confirm('File attached successfully');
      } catch (error: any) {
        console.error('Local upload error:', error);
        notify.error(`Failed to upload file: ${error.message || 'Unknown error'}`);
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
        if (e.target) e.target.value = '';
      }
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);

    const tryUpload = async () => {
      const storagePath = `attachments/${Date.now()}_${file.name}`;
      const fileRef = ref(storage, storagePath);

      try {
        const snapshot = await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        const attachment = {
          name: file.name,
          url: downloadURL,
          type: file.type || 'application/octet-stream',
          size: file.size,
          createdAt: new Date().toISOString()
        };

        await updateDoc(doc(db, 'documents', selectedInvoice.id), {
          attachments: arrayUnion(attachment),
          actionLog: arrayUnion({
            action: 'File Attached',
            user: auth.currentUser?.displayName || 'Unknown User',
            email: auth.currentUser?.email || '',
            timestamp: new Date().toISOString(),
            details: `Attached file: ${file.name}`
          })
        });
        await Logger.log(orgId, 'ADD', 'Invoices', selectedInvoice.id, selectedInvoice.number, {
          details: `Attached file: ${file.name}`,
          fileName: file.name,
        });

        setSelectedInvoice(prev => prev ? {
          ...prev,
          attachments: [...(prev.attachments || []), attachment]
        } as Invoice : null);

        notify.confirm('File attached successfully');
      } catch (error: any) {
        console.error("Upload error:", error);
        notify.error(`Failed to upload file: ${error.message || 'Unknown error'}`);
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
        if (e.target) e.target.value = '';
      }
    };

    tryUpload();
  };

  const handleRemoveAttachment = async (attachmentUrl: string) => {
    if (!selectedInvoice) return;

    if (hasLocalPostgresSession()) {
      try {
        const updatedAttachments = selectedInvoice.attachments?.filter((a: any) => a.url !== attachmentUrl) || [];
        const response = await fetch(`/api/organizations/${orgId}/documents/${selectedInvoice.id}`, {
          method: 'PATCH',
          headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ attachments: updatedAttachments }),
        });
        if (!response.ok) throw new Error('Could not remove attachment');

        const updatedInvoice = await response.json() as Invoice;
        setSelectedInvoice(updatedInvoice);
        setInvoices(prev => prev.map(invoice => invoice.id === updatedInvoice.id ? updatedInvoice : invoice));
        notify.confirm('Attachment removed');
      } catch (error) {
        console.error('Failed to remove local attachment:', error);
        notify.error('Failed to remove attachment');
      }
      return;
    }

    try {
      const updatedAttachments = selectedInvoice.attachments?.filter((a: any) => a.url !== attachmentUrl) || [];
      await updateDoc(doc(db, 'documents', selectedInvoice.id), {
        attachments: updatedAttachments,
        actionLog: arrayUnion({
          action: 'File Removed',
          user: auth.currentUser?.displayName || 'Unknown User',
          email: auth.currentUser?.email || '',
          timestamp: new Date().toISOString(),
          details: 'Removed an attachment'
        })
      });
      await Logger.log(orgId, 'REMOVE', 'Invoices', selectedInvoice.id, selectedInvoice.number, {
        details: 'Removed an attachment',
      });

      setSelectedInvoice(prev => prev ? {
        ...prev,
        attachments: updatedAttachments
      } as Invoice : null);

      notify.confirm('Attachment removed');
    } catch (error) {
      console.error('Failed to remove attachment:', error);
      notify.error('Failed to remove attachment');
    }
  };

  const isOverdue = (dueDate: string, status: string) => {
    if (status === 'Paid' || status === 'Void' || status === 'Deleted') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  const handleBulkAction = async (action: 'submit' | 'approve' | 'delete' | 'print' | 'email') => {
    if (selectedInvoices.length === 0) return;

    try {
      if (hasLocalPostgresSession() && (action === 'delete' || action === 'approve' || action === 'submit')) {
        const localStatus = action === 'delete'
          ? 'Void'
          : action === 'approve'
            ? 'Awaiting Payment'
            : 'Awaiting Approval';
        const updatedDocuments: Invoice[] = [];
        let skippedCount = 0;

        for (const id of selectedInvoices) {
          const invoice = invoices.find(i => i.id === id);
          if (!invoice) continue;
          if (action === 'delete' && (invoice.paymentReceived || 0) > 0) {
            skippedCount++;
            continue;
          }

          const response = await fetch(`/api/organizations/${orgId}/documents/${id}`, {
            method: 'PATCH',
            headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: localStatus })
          });
          if (!response.ok) throw new Error(`Could not update invoice ${invoice.number}`);
          updatedDocuments.push(await response.json());
        }

        if (updatedDocuments.length > 0) {
          const updatedById = new Map(updatedDocuments.map(document => [document.id, document]));
          setInvoices(prev => prev.map(invoice => updatedById.get(invoice.id) || invoice));
        }

        if (action === 'delete') {
          if (updatedDocuments.length > 0) notify.confirm(`${updatedDocuments.length} invoices voided`);
          if (skippedCount > 0) notify.error(`${skippedCount} invoices skipped because they have payments`);
        } else if (action === 'approve') {
          notify.confirm(`${updatedDocuments.length} invoices approved`);
          notify.confirm('Journal posting will be handled server-side separately.');
        } else {
          notify.confirm(`${updatedDocuments.length} invoices submitted for approval`);
        }
        setSelectedInvoices([]);
        return;
      }

      if (action === 'delete') {
        let voidedCount = 0;
        let skippedCount = 0;
        const voidedInvoiceIds: string[] = [];

        for (const id of selectedInvoices) {
          const invoice = invoices.find(i => i.id === id);
          if (invoice && (invoice.paymentReceived || 0) > 0) {
            skippedCount++;
            continue;
          }
          // If it's already approved (Awaiting Payment), we should call it "Void"
          // But the user wants them to show in the voided section, so we update status to 'Void'
          await updateDoc(doc(db, 'documents', id), { status: 'Void' });
          if (invoice && invoice.status !== 'Draft') {
            await reverseAutoJournalsForSource(orgId, id, auth.currentUser?.uid || 'system', `Invoice ${invoice.number} voided`);
          }
          voidedInvoiceIds.push(id);
          voidedCount++;
        }

        if (voidedInvoiceIds.length > 0) {
          setInvoices(current => current.map(invoice =>
            voidedInvoiceIds.includes(invoice.id) ? { ...invoice, status: 'Void' } : invoice
          ));
          setSelectedInvoice(current =>
            current && voidedInvoiceIds.includes(current.id) ? { ...current, status: 'Void' } : current
          );
        }

        if (voidedCount > 0) notify.confirm(`${voidedCount} invoices voided`);
        if (skippedCount > 0) notify.error(`${skippedCount} invoices skipped because they have payments`);
      } else if (action === 'approve') {
        for (const id of selectedInvoices) {
          await updateDoc(doc(db, 'documents', id), { status: 'Awaiting Payment' });
          const invoice = invoices.find(i => i.id === id);
          if (invoice) {
            await postInvoiceJournal(orgId, invoice, auth.currentUser?.uid || 'system');
          }
        }
        notify.confirm(`${selectedInvoices.length} invoices approved`);
      } else if (action === 'submit') {
        for (const id of selectedInvoices) {
          await updateDoc(doc(db, 'documents', id), { status: 'Awaiting Approval' });
        }
        notify.confirm(`${selectedInvoices.length} invoices submitted for approval`);
      } else if (action === 'print') {
        notify.confirm("Printing functionality for multiple invoices is being prepared.");
      } else if (action === 'email') {
        notify.confirm("Email functionality for multiple invoices is being prepared.");
      }

      setSelectedInvoices([]);
    } catch (error) {
      console.error("Error performing bulk action:", error);
      notify.error("Failed to perform bulk action");
    }
  };
  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [selectedTagToAdd, setSelectedTagToAdd] = useState('');
  const [invoiceToDelete, setInvoiceToDelete] = useState<string | null>(null);
  const [invoicePayments, setInvoicePayments] = useState<any[]>([]);
  const [paymentToDelete, setPaymentToDelete] = useState<any | null>(null);

  // Repeating Invoice Modal state
  const [isRepeatingModalOpen, setIsRepeatingModalOpen] = useState(false);
  const [repeatingConfig, setRepeatingConfig] = useState({
    frequency: 1,
    frequencyUnit: 'Month' as 'Week' | 'Month',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    dueDateDays: 7,
    dueDateType: 'daysAfterInvoiceDate' as 'daysAfterInvoiceDate' | 'dayOfFollowingMonth' | 'dayOfCurrentMonth' | 'daysAfterEndOfInvoiceMonth',
    approveStatus: 'Draft' as 'Draft' | 'Approved' | 'ApprovedForSending'
  });

  // Credit Note Modal state
  const [isCreditNoteModalOpen, setIsCreditNoteModalOpen] = useState(false);
  const [isApplyCreditMode, setIsApplyCreditMode] = useState(false);
  const [creditApplicationAmount, setCreditApplicationAmount] = useState(0);
  const [creditApplicationMax, setCreditApplicationMax] = useState(0);
  const [applyCreditTargetId, setApplyCreditTargetId] = useState<string | null>(null);

  // Sales History Specific Filters
  const [historyTab, setHistoryTab] = useState<'all' | 'return' | 'continue'>('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showTimeRange, setShowTimeRange] = useState(false);
  const [timeRange, setTimeRange] = useState({ start: '', end: '' });
  const [customerSearch, setCustomerSearch] = useState('');
  const [receiptNote, setReceiptNote] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [refFilter, setRefFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [minTotalAmount, setMinTotalAmount] = useState('');
  const [maxTotalAmount, setMaxTotalAmount] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | 'none'>('none');
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailData, setEmailData] = useState({ to: '', subject: '', body: '' });

  // Payment Modal state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    accountId: '',
    reference: ''
  });

  // New Contact Modal state
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '', type: 'customer' });

  // Batch Deposit state
  const [isBatchDepositModalOpen, setIsBatchDepositModalOpen] = useState(false);
  const [batchDepositInvoices, setBatchDepositInvoices] = useState<Invoice[]>([]);
  const [selectedInvoicesForBatch, setSelectedInvoicesForBatch] = useState<Record<string, number>>({});
  const [batchSearch, setBatchSearch] = useState('');
  const [batchStatusFilter, setBatchStatusFilter] = useState('all');
  const [batchDepositSettings, setBatchDepositSettings] = useState({
    accountId: '',
    date: new Date().toISOString().split('T')[0],
    reference: `BATCH-${Math.floor(Math.random() * 10000)}`,
    note: ''
  });

  // Currencies state
  const [currencies, setCurrencies] = useState(['EGP', 'USD', 'EUR', 'GBP', 'AED']);
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);
  const [customCurrency, setCustomCurrency] = useState('');
  const [orgSettings, setOrgSettings] = useState<any>(providedOrgSettings || null);
  const organizationCurrency = getDefaultCurrency(orgSettings);

  useEffect(() => {
    if (providedOrgSettings) setOrgSettings(providedOrgSettings);
  }, [providedOrgSettings]);
  const [isRemindersModalOpen, setIsRemindersModalOpen] = useState(false);
  const [isEditReminderModalOpen, setIsEditReminderModalOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState<any>(null);
  const [deliveryNotes, setDeliveryNotes] = useState<DeliveryNote[]>([]);
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [activeDeliveryNote, setActiveDeliveryNote] = useState<DeliveryNote | null>(null);
  const [deliveryPersonnel, setDeliveryPersonnel] = useState<any[]>([]);
  const [lotNumbers, setLotNumbers] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<Record<string, any>>({});
  const [isPODModalOpen, setIsPODModalOpen] = useState(false);
  const [showLinkedDeliveries, setShowLinkedDeliveries] = useState(false);
  const [allQuotes, setAllQuotes] = useState<any[]>([]);
  const sigCanvas = React.useRef<SignatureCanvas>(null);
  const [isSubmittingPOD, setIsSubmittingPOD] = useState(false);
  const [podData, setPodData] = useState({
    receiverName: '',
    signature: '',
    photo: null as File | null,
    notes: ''
  });
  const [remindersSettings, setRemindersSettings] = useState<InvoiceRemindersSettings>({
    enabled: false,
    reminders: [
      { id: '1', days: 7, active: true },
      { id: '2', days: 14, active: true },
      { id: '3', days: 21, active: true }
    ],
    includeOnlineButton: true,
    includePdfLink: true,
    minimumAmount: 1.00,
    replyToEmail: auth.currentUser?.email || ''
  });
  const [trackingCategories, setTrackingCategories] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [taxRates, setTaxRates] = useState<any[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);

  const getTaxRateValue = (rateStr: string) => {
    if (!rateStr) return 0;
    const dynamicTax = taxRates.find(t => t.name === rateStr);
    if (dynamicTax) return dynamicTax.rate;
    const match = rateStr.match(/(\d+(\.\d+)?)%/);
    return match ? parseFloat(match[1]) : (parseFloat(rateStr) || 0);
  };

  // Form state
  const [newInvoice, setNewInvoice] = useState<Omit<Invoice, 'id'> & { trackingCategory?: string; contactEmail?: string; contactName?: string; tags?: string[]; linkedInvoiceId?: string; creditApplicationAmount?: number }>({
    number: '',
    contactId: '',
    contactName: '',
    contactEmail: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reference: '',
    currency: getDefaultCurrency(providedOrgSettings),
    brandingTheme: 'Standard',
    amountsAre: 'Tax Exclusive',
    items: [{
      item: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      discountType: 'percentage',
      taxAmount: 0,
      total: 0,
      account: '',
      taxRate: '0%',
      trackingCategory: ''
    }],
    totalAmount: 0,
    status: 'Draft',
    termsAndConditions: '',
    billingAddress: '',
    deliveryAddress: '',
    trackingCategory: '',
    tags: [],
    type: 'invoice',
    linkedInvoiceId: '',
    notes: ''
  });

  useEffect(() => {
    const handleQuickAction = (e: any) => {
      if (e.detail?.action === 'new-invoice') {
        setIsEditing(false);
        setNewInvoice({
          number: '',
          contactId: e.detail.contactId || '',
          contactName: e.detail.contactName || '',
          contactEmail: e.detail.contactEmail || '',
          date: new Date().toISOString().split('T')[0],
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          reference: '',
          currency: organizationCurrency,
          brandingTheme: 'Standard',
          amountsAre: 'Tax Exclusive',
          totalAmount: 0,
          items: [],
          status: 'Draft',
          termsAndConditions: '',
          billingAddress: '',
          deliveryAddress: '',
          trackingCategory: '',
          tags: [],
          type: 'invoice',
          linkedInvoiceId: ''
        });
        setIsModalOpen(true);
      }
    };
    window.addEventListener('QUICK_ACTION', handleQuickAction as EventListener);
    return () => window.removeEventListener('QUICK_ACTION', handleQuickAction as EventListener);
  }, []);

  useEffect(() => {
    if (isCreditNoteModalOpen && orgSettings) {
      const prefix = orgSettings.creditNotePrefix || 'CN-';
      const nextNum = orgSettings.nextCreditNoteNumber || 1;
      setNewInvoice(prev => ({
        ...prev,
        number: `${prefix}${String(nextNum).padStart(3, '0')}`
      }));
    }
  }, [isCreditNoteModalOpen, orgSettings]);

  useEffect(() => {
    if (hasLocalPostgresSession()) {
      if (!orgId) return;
      Promise.all([
        fetch(`/api/organizations/${orgId}/documents?types=invoice,credit_note`, { headers: localApiHeaders() }),
        fetch(`/api/organizations/${orgId}/contacts`, { headers: localApiHeaders() }),
        fetch(`/api/organizations/${orgId}/items`, { headers: localApiHeaders() }),
        fetch(`/api/organizations/${orgId}/chart-of-accounts`, { headers: localApiHeaders() }),
        fetch(`/api/organizations/${orgId}/bank-accounts`, { headers: localApiHeaders() }),
        fetch(`/api/organizations/${orgId}/tax-rates`, { headers: localApiHeaders() }),
        fetch(`/api/organizations/${orgId}/settings`, { headers: localApiHeaders() }),
        fetch(`/api/organizations/${orgId}/inventory-locations`, { headers: localApiHeaders() }),
        fetch(`/api/organizations/${orgId}/users`, { headers: localApiHeaders() }),
        fetch(`/api/organizations/${orgId}/documents?types=quote,repeating_quote`, { headers: localApiHeaders() }),
      ])
        .then(async ([documentsResponse, contactsResponse, itemsResponse, accountsResponse, bankAccountsResponse, taxRatesResponse, settingsResponse, locationsResponse, usersResponse, quotesResponse]) => {
          if (![documentsResponse, contactsResponse, itemsResponse, accountsResponse, bankAccountsResponse, taxRatesResponse, settingsResponse, locationsResponse, usersResponse, quotesResponse].every(response => response.ok)) {
            throw new Error('Could not load local invoice data');
          }
          const [documents, localContacts, localItems, localAccounts, localBankAccounts, localTaxRates, localSettings, localLocations, localUsers, localQuotes] = await Promise.all([
            documentsResponse.json(), contactsResponse.json(), itemsResponse.json(), accountsResponse.json(), bankAccountsResponse.json(), taxRatesResponse.json(), settingsResponse.json(), locationsResponse.json(), usersResponse.json(), quotesResponse.json(),
          ]);
          const normalizedSettings = localSettings ? {
            ...localSettings,
            ...(localSettings.data && typeof localSettings.data === 'object' && !Array.isArray(localSettings.data) ? localSettings.data : {}),
            defaultCurrency: getDefaultCurrency(localSettings),
            defaultPaymentTerms: localSettings.defaultPaymentTerms ?? localSettings.default_payment_terms,
            allowedCurrencies: localSettings.allowedCurrencies ?? localSettings.allowed_currencies ?? [],
            defaultInvoiceBrandingTheme: localSettings.defaultInvoiceBrandingTheme ?? localSettings.default_invoice_branding_theme,
            invoiceReminders: localSettings.invoiceReminders ?? localSettings.data?.invoiceReminders,
            invoicePrefix: localSettings.invoicePrefix ?? localSettings.invoice_prefix ?? 'INV-',
            nextInvoiceNumber: Number(localSettings.nextInvoiceNumber ?? localSettings.next_invoice_number) || 1,
            creditNotePrefix: localSettings.creditNotePrefix ?? localSettings.data?.creditNotePrefix ?? 'CN-',
            nextCreditNoteNumber: Number(localSettings.nextCreditNoteNumber ?? localSettings.data?.nextCreditNoteNumber) || 1,
          } : null;
          setInvoices(documents as Invoice[]);
          setContacts(localContacts.filter((contact: any) => contact.type === 'Customer' || contact.type === 'Both'));
          setItemsList(localItems);
          setAccountsList(localAccounts);
          setBankAccountsList(localBankAccounts);
          setTaxRates(localTaxRates);
          setWarehouses(Array.isArray(localLocations) ? localLocations : []);
          setStores([]);
          setDeliveryPersonnel(Array.isArray(localUsers) ? localUsers : []);
          setAllQuotes(Array.isArray(localQuotes) ? localQuotes : []);
          if (normalizedSettings) {
            setOrgSettings(normalizedSettings);
            if (normalizedSettings.invoiceReminders) {
              setRemindersSettings(normalizedSettings.invoiceReminders);
            } else {
              setRemindersSettings(prev => ({ ...prev, replyToEmail: auth.currentUser?.email || '' }));
            }
            setCurrencies(normalizedSettings.allowedCurrencies.length > 0
              ? [...new Set([normalizedSettings.defaultCurrency, ...normalizedSettings.allowedCurrencies])]
              : [normalizedSettings.defaultCurrency]);
          }
          setNewInvoice(prev => ({
            ...prev,
            number: prev.number || `INV-DRAFT-${Math.floor(1000 + Math.random() * 9000)}`,
            currency: normalizedSettings?.defaultCurrency || prev.currency,
          }));
        })
        .catch(error => {
          console.error('Local invoice load failed:', error);
          notify.error('Failed to load invoices');
        })
        .finally(() => setLoading(false));
      return;
    }

    // Fetch organization settings
    const fetchOrgSettings = async () => {
      if (!orgId) return;
      try {
        const docRef = doc(db, 'settings', orgId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setOrgSettings(data);

          if (data.invoiceReminders) {
            setRemindersSettings(data.invoiceReminders);
          } else {
            setRemindersSettings(prev => ({ ...prev, replyToEmail: auth.currentUser?.email || '' }));
          }

          // Update initial form state with settings
          const prefix = data.invoicePrefix || 'INV-';
          let nextNum = data.nextInvoiceNumber || 1;

          setNewInvoice(prev => ({
            ...prev,
            number: `${prefix}${nextNum}`,
            currency: getDefaultCurrency(data),
            brandingTheme: data.layouts?.invoiceThemeId || data.defaultInvoiceBrandingTheme || 'Standard',
            dueDate: new Date(Date.now() + (data.defaultPaymentTerms || 30) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          }));

          if (data.allowedCurrencies && data.allowedCurrencies.length > 0) {
            const allCurrencies = [...new Set([getDefaultCurrency(data), ...data.allowedCurrencies])];
            setCurrencies(allCurrencies);
          } else if (data.defaultCurrency) {
            setCurrencies([data.defaultCurrency]);
          }
        } else {
          // Set a default draft number if no settings exist
          setNewInvoice(prev => ({
            ...prev,
            number: `INV-DRAFT-${Math.floor(1000 + Math.random() * 9000)}`
          }));
        }
      } catch (error) {
        console.error('Error fetching org settings:', error);
      }
    };

    fetchOrgSettings();

    if (!orgId) return;

    const q = query(
      collection(db, 'documents'),
      where('type', 'in', ['invoice', 'repeating_invoice', 'credit_note', 'RECEIPT']),
      where('orgId', '==', orgId),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Invoice));
      setInvoices(docs);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'documents');
    });

    // Fetch quotes so invoices converted from a quote can link back to it.
    const quotesQ = query(
      collection(db, 'documents'),
      where('type', 'in', ['quote', 'repeating_quote']),
      where('orgId', '==', orgId)
    );
    const unsubscribeQuotes = onSnapshot(quotesQ, (snapshot) => {
      setAllQuotes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as any[]);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'documents');
    });

    // Fetch contacts for the dropdown
    const contactsQ = query(
      collection(db, 'contacts'),
      where('orgId', '==', orgId),
      where('type', 'in', ['Customer', 'Both'])
    );
    const unsubscribeContacts = onSnapshot(contactsQ, (snapshot) => {
      setContacts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'contacts');
    });

    // Fetch items for the dropdown
    const itemsQ = query(
      collection(db, 'items'),
      where('orgId', '==', orgId),
      orderBy('name', 'asc')
    );
    const unsubscribeItems = onSnapshot(itemsQ, (snapshot) => {
      setItemsList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'items');
    });

    // Fetch accounts for the dropdown
    const accountsQ = query(
      collection(db, 'chart_of_accounts'),
      where('orgId', '==', orgId),
      orderBy('code', 'asc')
    );
    const unsubscribeAccounts = onSnapshot(accountsQ, (snapshot) => {
      setAccountsList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'chart_of_accounts');
    });

    // Fetch bank accounts for the dropdown
    const bankAccountsQ = query(
      collection(db, 'bank_accounts'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );
    const unsubscribeBankAccounts = onSnapshot(bankAccountsQ, (snapshot) => {
      setBankAccountsList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'bank_accounts');
    });

    // Fetch Tracking Categories
    const trackingQ = query(
      collection(db, 'tracking_categories'),
      where('orgId', '==', orgId),
      where('status', '==', 'active')
    );
    const unsubscribeTracking = onSnapshot(trackingQ, (snapshot) => {
      setTrackingCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error("Error fetching tracking categories:", error);
    });

    // Fetch Branding Themes
    const themesQ = query(
      collection(db, 'branding_themes'),
      where('orgId', '==', orgId)
    );
    const unsubscribeThemes = onSnapshot(themesQ, (snapshot) => {
      setBrandingThemes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'branding_themes');
    });

    // Fetch Tax Rates
    const taxRatesQ = query(
      collection(db, 'tax_rates'),
      where('orgId', '==', orgId)
    );
    const unsubscribeTaxRates = onSnapshot(taxRatesQ, (snapshot) => {
      setTaxRates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tax_rates');
    });

    // Fetch Tags
    const tagsQ = query(
      collection(db, 'tags'),
      where('orgId', '==', orgId),
      where('type', 'in', ['sales', null]),
      orderBy('createdAt', 'desc')
    );
    const unsubscribeTags = onSnapshot(tagsQ, (snapshot) => {
      setAvailableTags(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tags');
    });

    // Fetch Delivery Notes
    if (hasLocalPostgresSession()) {
      let cancelled = false;
      void fetch(`/api/organizations/${orgId}/documents?types=delivery`, { headers: localApiHeaders() })
        .then(response => response.ok ? response.json() : Promise.reject(new Error('Could not load delivery notes')))
        .then((documents) => {
          if (!cancelled) {
            const normalized = (Array.isArray(documents) ? documents : []).map((entry: any) => ({
              ...entry,
              invoiceId: entry.invoiceId || entry.metadata?.invoiceId,
              invoiceNumber: entry.invoiceNumber || entry.metadata?.invoiceNumber,
              contactName: entry.contactName || entry.metadata?.contactName,
              warehouseId: entry.warehouseId || entry.metadata?.warehouseId,
              warehouseName: entry.warehouseName || entry.metadata?.warehouseName,
              assignedPersonnelId: entry.assignedPersonnelId || entry.metadata?.assignedPersonnelId,
              assignedPersonnelName: entry.assignedPersonnelName || entry.metadata?.assignedPersonnelName,
            }));
            setDeliveryNotes(normalized);
          }
        })
        .catch(error => { if (!cancelled) console.error('Local delivery notes load failed:', error); });
      return () => { cancelled = true; };
    }
    const deliveryNotesQ = query(
      collection(db, 'delivery_notes'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );
    const unsubscribeDeliveryNotes = onSnapshot(deliveryNotesQ, (snapshot) => {
      setDeliveryNotes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DeliveryNote)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'delivery_notes');
    });

    // Fetch Org Users (for assignments)
    const orgUsersQ = query(
      collection(db, 'org_users'),
      where('orgId', '==', orgId),
      where('suspended', '==', false)
    );
    const unsubscribeOrgUsers = onSnapshot(orgUsersQ, (snapshot) => {
      const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDeliveryPersonnel(users);

      // Also update allUsers for display names/profile data
      const userMap: Record<string, any> = {};
      users.forEach((u: any) => {
        if (u.email) {
          userMap[u.email.toLowerCase()] = u;
        }
      });
      setAllUsers(prev => ({ ...prev, ...userMap }));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'org_users');
    });

    const lotQ = query(
      collection(db, 'lot_numbers'),
      where('orgId', '==', orgId)
    );
    const unsubLots = onSnapshot(lotQ, (snapshot) => {
      setLotNumbers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'lot_numbers'));

    const warehousesQ = query(collection(db, 'warehouses'), where('orgId', '==', orgId));
    const unsubscribeWarehouses = onSnapshot(warehousesQ, (snapshot) => {
      setWarehouses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'warehouses'));

    const storesQ = query(collection(db, 'stores'), where('orgId', '==', orgId));
    const unsubscribeStores = onSnapshot(storesQ, (snapshot) => {
      setStores(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'stores'));

    // Instead of fetching ALL users, we only need to map emails from org_users
    // since all users in this org must be in org_users anyway.
    // If we really need users, we should query them individually or by a set of IDs.
    // Removing the broad collection(db, 'users') listener to avoid permission-denied.
    const unsubscribeUsers = () => {};

      return () => {
        unsubscribe();
        unsubscribeQuotes();
        unsubscribeContacts();
        unsubscribeItems();
        unsubscribeAccounts();
        unsubscribeBankAccounts();
        unsubscribeTracking();
        unsubscribeThemes();
        unsubscribeTaxRates();
        unsubscribeTags();
        unsubscribeDeliveryNotes();
        unsubscribeOrgUsers();
        unsubscribeUsers();
        unsubLots();
        unsubscribeWarehouses();
        unsubscribeStores();
      };
  }, [orgId]);

  useEffect(() => {
    if (initialInvoiceId) {
      if (initialInvoiceId.startsWith('new-invoice-for:')) {
        const contactId = initialInvoiceId.split(':')[1];
        setIsEditing(false);
        setNewInvoice({
          number: `INV-${Date.now().toString().slice(-6)}`,
          contactId: contactId,
          date: new Date().toISOString().split('T')[0],
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          reference: '',
          currency: getDefaultCurrency(orgSettings),
          brandingTheme: 'Standard',
          amountsAre: 'Tax Exclusive',
          items: [],
          totalAmount: 0,
          status: 'Draft',
          termsAndConditions: '',
          billingAddress: '',
          deliveryAddress: '',
          type: 'invoice',
          notes: '',
          tags: []
        });
        setIsModalOpen(true);
        if (onClearInitial) onClearInitial();
      } else if (invoices.length > 0) {
        const invoice = invoices.find(i => i.id === initialInvoiceId);
        if (invoice) {
          setSelectedInvoice(invoice);
          if (onClearInitial) onClearInitial();
        }
      }
    }
  }, [initialInvoiceId, invoices, orgSettings, orgId]);

  useEffect(() => {
    if (selectedInvoice) {
      const updated = invoices.find(i => i.id === selectedInvoice.id);
      if (updated && JSON.stringify(updated) !== JSON.stringify(selectedInvoice)) {
        setSelectedInvoice(updated);
      }
    }
  }, [invoices, selectedInvoice?.id]);

  useEffect(() => {
    if (!selectedInvoice || !orgId) {
      setInvoicePayments([]);
      return;
    }

    if (hasLocalPostgresSession()) {
      let cancelled = false;

      const fetchPayments = async () => {
        try {
          const response = await fetch(`/api/organizations/${orgId}/documents/${selectedInvoice.id}/payments`, {
            headers: localApiHeaders(),
          });
          if (!response.ok) throw new Error('Could not load payment history');

          const payments = await response.json();
          if (!cancelled) setInvoicePayments(payments);
        } catch (error) {
          if (!cancelled) {
            console.error('Local payment history fetch failed:', error);
            notify.error('Failed to load payment history');
          }
        }
      };

      fetchPayments();
      return () => {
        cancelled = true;
      };
    }

    const q = query(
      collection(db, 'transactions'),
      where('orgId', '==', orgId),
      where('invoiceId', '==', selectedInvoice.id),
      orderBy('date', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setInvoicePayments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'transactions');
    });
    return () => unsubscribe();
  }, [selectedInvoice?.id]);

  const handleContactChange = (contactId: string) => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      setNewInvoice(prev => ({
        ...prev,
        contactId,
        contactName: contact.name || '',
        contactEmail: contact.email || '',
        currency: contact.salesCurrency || contact.currency || prev.currency,
        brandingTheme: contact.salesBrandingTheme || prev.brandingTheme,
        billingAddress: contact.billingAddress || contact.address || '',
        deliveryAddress: contact.deliveryAddress || contact.address || '',
        termsAndConditions: contact.salesPaymentTerms ? `Payment Terms: ${contact.salesPaymentTerms}` : (contact.paymentTerms ? `Payment Terms: ${contact.paymentTerms}` : prev.termsAndConditions),
        items: (prev.items || []).map((item, index) => index === 0 ? {
          ...item,
          account: contact.salesAccount || item.account,
          taxRate: contact.salesTaxRate || item.taxRate
        } : item)
      }));
    } else {
      setNewInvoice(prev => ({ ...prev, contactId }));
    }
  };

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContact.name.trim() || !orgId) return;

    try {
      const contact = hasLocalPostgresSession()
        ? await (async () => {
            const response = await fetch(`/api/organizations/${orgId}/contacts`, {
              method: 'POST',
              headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: newContact.name.trim(),
                email: newContact.email || '',
                type: newContact.type || 'Customer',
              }),
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || 'Could not create contact');
            return result;
          })()
        : await (async () => {
            const contactData = {
              name: newContact.name.trim(),
              email: newContact.email || '',
              orgId,
              type: newContact.type || 'customer',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              createdBy: auth.currentUser?.uid || 'unknown'
            };
            const docRef = await addDoc(collection(db, 'contacts'), contactData);
            return { id: docRef.id, ...contactData };
          })();

      if (!contact?.id) throw new Error('Contact was created without an ID');

      setContacts(prev => [contact, ...prev.filter(existing => existing.id !== contact.id)]);
      setNewInvoice(prev => ({
        ...prev,
        contactId: contact.id,
        contactName: contact.name,
        contactEmail: contact.email || (Array.isArray(contact.emails) ? contact.emails[0] || '' : '')
      }));

      setIsContactModalOpen(false);
      setNewContact({ name: '', email: '', type: 'customer' });
      notify.confirm('Contact added successfully');
    } catch (error) {
      console.error('Error adding contact:', error);
      notify.error('Failed to add contact');
    }
  };

  const calculateDueDate = (invoiceDate: string, config: any) => {
    const date = new Date(invoiceDate);
    switch (config.dueDateType) {
      case 'daysAfterInvoiceDate':
        date.setDate(date.getDate() + config.dueDateDays);
        break;
      case 'dayOfFollowingMonth':
        date.setMonth(date.getMonth() + 1);
        date.setDate(config.dueDateDays);
        break;
      case 'dayOfCurrentMonth':
        date.setDate(config.dueDateDays);
        break;
      case 'daysAfterEndOfInvoiceMonth':
        date.setMonth(date.getMonth() + 1);
        date.setDate(0); // Last day of month
        date.setDate(date.getDate() + config.dueDateDays);
        break;
    }
    return date.toISOString().split('T')[0];
  };

  const handleCopyInvoice = async (targetType: 'invoice' | 'quote') => {
    if (!selectedInvoice || (!auth.currentUser && !hasLocalPostgresSession())) return;

    setIsSaving(true);
    try {
      if (hasLocalPostgresSession()) {
        const result = await fetch(`/api/organizations/${orgId}/documents/${selectedInvoice.id}/copy-to`, {
          method: 'POST',
          headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ targetType }),
        });
        const payload = await result.json().catch(() => null);
        if (!result.ok) throw new Error(payload?.error || 'Could not copy document');
        notify.confirm(`Successfully copied as draft ${targetType}`);
        setShowCopyModal(false);
        onNavigate?.(targetType === 'invoice' ? 'invoices' : 'quotes', payload?.documentId || payload?.id);
        return;
      }

      const settingsRef = doc(db, 'settings', orgId);
      const settingsSnap = await getDoc(settingsRef);
      const settingsData = settingsSnap.data() || {};

      let newNumber = '';
      if (targetType === 'invoice') {
        const prefix = settingsData.invoicePrefix || 'INV-';
        const nextNum = settingsData.nextInvoiceNumber || 1;
        newNumber = `${prefix}${nextNum}`;
        await updateDoc(settingsRef, { nextInvoiceNumber: increment(1) });
      } else {
        const prefix = settingsData.quotePrefix || 'QT-';
        const nextNum = settingsData.nextQuoteNumber || 1;
        newNumber = `${prefix}${nextNum}`;
        await updateDoc(settingsRef, { nextQuoteNumber: increment(1) });
      }

      const copyActionDetails = `Copied from Invoice ${selectedInvoice.number}`;
      const copyActionEntry: ActionLogEntry = {
        action: 'Created',
        user: auth.currentUser?.displayName || 'Unknown User',
        email: auth.currentUser?.email || 'No Email',
        timestamp: new Date().toISOString(),
        details: copyActionDetails
      };

      const newDocData = {
        number: newNumber,
        contactId: selectedInvoice.contactId,
        contactName: selectedInvoice.contactName || '',
        contactEmail: selectedInvoice.contactEmail || '',
        date: new Date().toISOString().split('T')[0],
        dueDate: calculateDueDate(new Date().toISOString().split('T')[0], providedOrgSettings?.paymentTerms || { dueDateType: 'daysAfterInvoiceDate', dueDateDays: 30 }),
        reference: '',
        currency: selectedInvoice.currency,
        brandingTheme: selectedInvoice.brandingTheme || 'Standard',
        amountsAre: selectedInvoice.amountsAre || 'Tax Exclusive',
        subtotal: (selectedInvoice as any).subtotal || 0,
        taxTotal: (selectedInvoice as any).taxTotal || (selectedInvoice as any).taxAmount || 0,
        totalAmount: selectedInvoice.totalAmount,
        amountPaid: 0,
        amountDue: selectedInvoice.totalAmount,
        payments: [],
        status: 'Draft',
        items: (selectedInvoice.items || []).map(item => ({ ...item })),
        termsAndConditions: selectedInvoice.termsAndConditions || '',
        type: targetType,
        orgId,
        createdBy: auth.currentUser.uid,
        createdByName: auth.currentUser.displayName || 'Unknown User',
        createdByEmail: auth.currentUser.email || 'No Email',
        actionDetails: copyActionDetails,
        actionLog: [copyActionEntry],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'documents'), newDocData);
      await Logger.log(orgId, 'CREATE', targetType === 'invoice' ? 'Invoices' : 'Quotes', docRef.id, newNumber, {
        after: newDocData,
        details: copyActionDetails,
      });

      notify.confirm(`Successfully copied as draft ${targetType}`);
      setShowCopyModal(false);

      if (onNavigate) {
        onNavigate(targetType === 'invoice' ? 'invoices' : 'quotes');
      }
    } catch (error) {
      console.error("Copy error:", error);
      notify.error(`Failed to copy to ${targetType}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDeliveryNote = async () => {
    if (!activeDeliveryNote || !orgId) return;

    // Validation: Check if quantities to ship are valid
    const hasValidQtys = (activeDeliveryNote?.items || []).some(item => (item.qtyToShipNow || 0) > 0);
    if (!hasValidQtys) {
      notify.error("Please specify at least one quantity to ship.");
      return;
    }

    setIsSaving(true);
    try {
      const deliveryNoteData: any = {
        invoiceId: activeDeliveryNote.invoiceId,
        invoiceNumber: activeDeliveryNote.invoiceNumber,
        orgId,
        contactId: activeDeliveryNote.contactId,
        contactName: activeDeliveryNote.contactName,
        items: (activeDeliveryNote.items || []).map(item => ({
          itemId: item.itemId,
          itemCode: item.itemCode,
          name: item.name,
          description: item.description,
          quantityOrdered: item.quantityOrdered,
          quantityShipped: item.qtyToShipNow || 0,
          lotNumber: item.lotNumber || '',
          expiryDate: item.expiryDate || ''
        })).filter(i => i.quantityShipped > 0),
        assignedPersonnelId: activeDeliveryNote.assignedPersonnelId || '',
        assignedPersonnelName: activeDeliveryNote.assignedPersonnelName || '',
        warehouseId: activeDeliveryNote.warehouseId || '',
        warehouseName: activeDeliveryNote.warehouseName || '',
        status: activeDeliveryNote.assignedPersonnelId ? 'Assigned' : 'Draft',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (hasLocalPostgresSession() && !activeDeliveryNote.id) {
        const response = await fetch(`/api/organizations/${orgId}/delivery-notes`, {
          method: 'POST',
          headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(deliveryNoteData),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Could not create delivery note');
        setDeliveryNotes(current => [...current, result.delivery as DeliveryNote]);
        setInvoices(current => current.map(invoice => invoice.id === activeDeliveryNote.invoiceId ? { ...invoice, shipmentStatus: result.shipmentStatus } : invoice));
        notify.confirm('Delivery note created successfully');
      } else if (activeDeliveryNote.id) {
        await updateDoc(doc(db, 'delivery_notes', activeDeliveryNote.id), deliveryNoteData);
        await Logger.log(orgId, 'EDIT', 'Delivery', activeDeliveryNote.id, undefined, {
          details: `Delivery Note #${activeDeliveryNote.id.slice(-6).toUpperCase()} updated.`,
        });
        notify.confirm('Delivery note updated');
      } else {
        const docRef = await addDoc(collection(db, 'delivery_notes'), deliveryNoteData);
        await Logger.log(orgId, 'CREATE', 'Delivery', docRef.id, undefined, {
          details: `Delivery Note #${docRef.id.slice(-6).toUpperCase()} created from Invoice ${activeDeliveryNote.invoiceNumber}.`,
        });

        // Log to invoice action log
        await updateDoc(doc(db, 'documents', activeDeliveryNote.invoiceId), {
          actionLog: arrayUnion({
            action: 'Delivery Note Created',
            user: auth.currentUser?.displayName || 'Unknown User',
            email: auth.currentUser?.email || '',
            timestamp: new Date().toISOString(),
            details: `Delivery Note #${docRef.id.slice(-6).toUpperCase()} created with ${deliveryNoteData.items.length} items.`,
            deliveryNoteId: docRef.id
          })
        });
        notify.confirm('Delivery note created successfully');
      }

      setIsDeliveryModalOpen(false);
      setActiveDeliveryNote(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'delivery_notes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmPOD = async () => {
    if (!activeDeliveryNote || !orgId) return;
    if (!podData.receiverName) {
      notify.error("Receiver name is mandatory");
      return;
    }
    if (sigCanvas.current?.isEmpty()) {
      notify.error("Signature is mandatory");
      return;
    }

    // Local Postgres path: route through the deliveries API so inventory_quants /
    // inventory_movements are updated server-side and the invoice shipment
    // status stays in sync. Mirrors DeliveriesView.handleConfirmPOD.
    if (hasLocalPostgresSession()) {
      setIsSubmittingPOD(true);
      try {
        if (!activeDeliveryNote.warehouseId && !activeDeliveryNote.storeId) {
          notify.error('A warehouse or store location is required to complete this delivery');
          return;
        }

        const signatureUrl = sigCanvas.current!.getCanvas().toDataURL('image/png');
        let photoUrl: string | null = null;
        if (podData.photo) {
          const formData = new FormData();
          formData.append('file', podData.photo);
          formData.append('targetPath', `delivery_pod/${activeDeliveryNote.id}/${Date.now()}_${podData.photo.name}`);
          const uploadResponse = await fetch('/api/files/upload', {
            method: 'POST',
            headers: localApiHeaders(),
            body: formData,
          });
          const upload = await uploadResponse.json().catch(() => null);
          if (!uploadResponse.ok) throw new Error(upload?.error || 'Failed to upload proof-of-delivery photo');
          const file = upload?.file || upload;
          photoUrl = file?.url || file?.downloadUrl || file?.downloadURL || null;
        }

        // The backend /pod endpoint requires the delivery to be Shipped first
        // (stock is reserved during checkout). Auto-check-out Assigned notes so
        // the invoice-side "Confirm Delivery" button can complete the flow.
        if (activeDeliveryNote.status !== 'Shipped' && activeDeliveryNote.status !== 'Delivered') {
          const checkoutResponse = await fetch(`/api/organizations/${orgId}/delivery-notes/${activeDeliveryNote.id}/checkout`, {
            method: 'POST',
            headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
          });
          const checkoutResult = await checkoutResponse.json().catch(() => null);
          if (!checkoutResponse.ok) throw new Error(checkoutResult?.error || 'Could not check out delivery');
        }

        const result = await fetch(`/api/organizations/${orgId}/delivery-notes/${activeDeliveryNote.id}/pod`, {
          method: 'POST',
          headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            receiverName: podData.receiverName,
            signatureUrl,
            ...(photoUrl ? { photoUrl } : {}),
            notes: podData.notes || '',
          }),
        }).then(r => r.json().then(body => ({ ok: r.ok, body })));
        if (!result.ok) throw new Error(result.body?.error || 'Failed to confirm delivery');

        const updatedDelivery = result.body?.delivery as DeliveryNote | undefined;
        const shipmentStatus = result.body?.shipmentStatus as string | undefined;
        if (updatedDelivery) {
          setDeliveryNotes(current => current.map(dn => (dn.id === updatedDelivery.id ? { ...dn, ...updatedDelivery } : dn)));
        }
        if (shipmentStatus) {
          setInvoices(current => current.map(inv => (inv.id === activeDeliveryNote.invoiceId ? { ...inv, shipmentStatus } : inv)));
          setSelectedInvoice(prev => (prev && prev.id === activeDeliveryNote.invoiceId ? { ...prev, shipmentStatus } : prev));
        }

        notify.confirm('Delivery confirmed and inventory updated');
        setIsPODModalOpen(false);
        setActiveDeliveryNote(null);
        setPodData({ receiverName: '', signature: '', photo: null, notes: '' });
        return;
      } catch (error) {
        console.error('POD Error:', error);
        notify.error('Failed to confirm delivery');
        return;
      } finally {
        setIsSubmittingPOD(false);
      }
    }

    if (!auth.currentUser) return;

    setIsSubmittingPOD(true);
    try {
      let signatureUrl = '';
      let photoUrl = '';

      // 1. Signature (Base64)
      signatureUrl = sigCanvas.current!.getCanvas().toDataURL('image/png');

      // 2. Photo (Base64 + Compressed)
      if (podData.photo) {
        photoUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              const MAX_WIDTH = 800;
              const MAX_HEIGHT = 800;
              let width = img.width;
              let height = img.height;

              if (width > height) {
                if (width > MAX_WIDTH) {
                  height *= MAX_WIDTH / width;
                  width = MAX_WIDTH;
                }
              } else {
                if (height > MAX_HEIGHT) {
                  width *= MAX_HEIGHT / height;
                  height = MAX_HEIGHT;
                }
              }

              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              ctx?.drawImage(img, 0, 0, width, height);
              resolve(canvas.toDataURL('image/jpeg', 0.6));
            }
            img.src = e.target?.result as string;
          }
          reader.readAsDataURL(podData.photo!);
        });
      }

      // 3. Update Delivery Note
      const updateData: any = {
        status: 'Delivered',
        deliveredAt: serverTimestamp(),
        receiverName: podData.receiverName,
        signatureUrl,
        notes: podData.notes,
        updatedAt: serverTimestamp()
      };
      if (photoUrl) updateData.photoUrl = photoUrl;

      await updateDoc(doc(db, 'delivery_notes', activeDeliveryNote.id), updateData);

      // 4. Deduct Inventory
      for (const item of activeDeliveryNote.items) {
        const itemDoc = itemsList.find(i => i.id === item.itemId || i.code === item.itemCode);
        if (itemDoc && itemDoc.trackInventory) {
          await updateDoc(doc(db, 'items', itemDoc.id), {
            quantity: increment(-item.quantityShipped)
          });
        }
      }

      // 5. Update Invoice shipmentStatus
      const invoice = invoices.find(inv => inv.id === activeDeliveryNote.invoiceId);
      if (invoice) {
        const allDNsForInvoice = deliveryNotes.filter(dn => dn.invoiceId === invoice.id || dn.id === activeDeliveryNote.id);
        const allShippedQtys: Record<string, number> = {};

        [...allDNsForInvoice].forEach(dn => {
          const dnStatus = dn.id === activeDeliveryNote.id ? 'Delivered' : dn.status;
          if (dnStatus === 'Delivered' || dnStatus === 'Shipped' || dnStatus === 'Assigned') {
            (dn.items || []).forEach(it => {
              allShippedQtys[it.itemId || it.itemCode] = (allShippedQtys[it.itemId || it.itemCode] || 0) + it.quantityShipped;
            });
          }
        });

        const isFullyShipped = (invoice.items || []).every(invItem => {
          const shipped = allShippedQtys[invItem.item] || 0;
          return shipped >= invItem.quantity;
        });

        const newShipmentStatus = isFullyShipped ? 'Fully Shipped' : 'Partially Shipped';

        await updateDoc(doc(db, 'documents', invoice.id), {
          shipmentStatus: newShipmentStatus,
          actionLog: arrayUnion({
            action: 'Delivery Confirmed',
            user: auth.currentUser?.displayName || 'Unknown User',
            email: auth.currentUser?.email || '',
            timestamp: new Date().toISOString(),
            details: `Delivery Note #${activeDeliveryNote.id.slice(-6).toUpperCase()} confirmed. Shipment Status: ${newShipmentStatus}`,
            deliveryNoteId: activeDeliveryNote.id
          })
        });
      }

      notify.confirm('Delivery confirmed and inventory updated');
      setIsPODModalOpen(false);
      setActiveDeliveryNote(null);
      setPodData({ receiverName: '', signature: '', photo: null, notes: '' });
    } catch (error) {
      console.error("POD Error:", error);
      notify.error('Failed to confirm delivery');
    } finally {
      setIsSubmittingPOD(false);
    }
  };

  const handleUpdateDeliveryStatus = async (dnId: string, status: 'Shipped' | 'Assigned', details?: string) => {
    try {
      if (hasLocalPostgresSession() && status === 'Shipped') {
        const response = await fetch(`/api/organizations/${orgId}/delivery-notes/${dnId}/checkout`, {
          method: 'POST',
          headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
        });
        const result = await response.json().catch(() => null);
        if (!response.ok) throw new Error(result?.error || 'Could not check out delivery');
        const updated = result?.delivery as DeliveryNote | undefined;
        if (updated) {
          setDeliveryNotes(current => current.map(dn => (dn.id === updated.id ? { ...dn, ...updated } : dn)));
        }
        notify.confirm(`Delivery status updated to ${status}`);
        return;
      }
      await updateDoc(doc(db, 'delivery_notes', dnId), {
        status,
        shippedAt: status === 'Shipped' ? serverTimestamp() : null,
        updatedAt: serverTimestamp()
      });
      notify.confirm(`Delivery status updated to ${status}`);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'delivery_notes');
    }
  };

  const handleAddInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!auth.currentUser && !hasLocalPostgresSession()) || isSaving) return;

    if (!newInvoice.contactId) {
      alert("Please select a contact before saving.");
      return;
    }

    const hasValidItems = (newInvoice.items || []).some(item => item.description.trim() !== '');
    if (!hasValidItems) {
      alert("Please add at least one item with a description.");
      return;
    }

    setIsSaving(true);

    const itemsWithTotal = (newInvoice.items || []).map(item => {
      const taxRateValue = getTaxRateValue(item.taxRate);
      const subtotalBeforeDiscount = item.quantity * item.unitPrice;
      const discountAmount = (item as any).discountType === 'amount'
        ? (item.discount || 0)
        : (subtotalBeforeDiscount * (item.discount || 0) / 100);
      const subtotal = subtotalBeforeDiscount - discountAmount;
      let taxAmount = 0;
      let lineTotal = subtotal;

      if (newInvoice.amountsAre === 'Tax Exclusive') {
        taxAmount = subtotal * (taxRateValue / 100);
        lineTotal = subtotal + taxAmount;
      } else if (newInvoice.amountsAre === 'Tax Inclusive') {
        taxAmount = subtotal - (subtotal / (1 + taxRateValue / 100));
        lineTotal = subtotal; // subtotal is already inclusive
      } else {
        taxAmount = 0;
        lineTotal = subtotal;
      }

      return {
        ...item,
        taxAmount,
        total: lineTotal
      };
    });

const totalAmount = itemsWithTotal.reduce((sum, item) => sum + item.total, 0);
    const requestedCreditAmount = isApplyCreditMode
      ? Math.min(Math.max(0, creditApplicationAmount), creditApplicationMax)
      : totalAmount;

    const statusOverride = (window as any)._invoiceStatusOverride;
    delete (window as any)._invoiceStatusOverride;

    const contact = contacts.find(c => c.id === newInvoice.contactId);
    const contactName = contact?.name || newInvoice.contactName || 'Walk-in Customer';
    const contactEmail = contact?.email || newInvoice.contactEmail || '';
    const actionDetails = isEditing
      ? `Invoice ${newInvoice.number} updated by ${auth.currentUser?.displayName || 'Unknown User'}`
      : `Invoice ${newInvoice.number} to ${contactName} for ${(totalAmount || 0).toLocaleString()}`;

    const actionEntry: ActionLogEntry = {
      action: isEditing ? 'Edited' : 'Created',
      user: auth.currentUser?.displayName || 'Unknown User',
      email: auth.currentUser?.email || 'No Email',
      timestamp: new Date().toISOString(),
      details: actionDetails
    };

    try {
      const finalStatus = statusOverride || newInvoice.status;
      const requiresCreditApplication = isEditing && selectedInvoice &&
        selectedInvoice.status !== 'Awaiting Payment' && selectedInvoice.status !== 'Paid' &&
        (finalStatus === 'Awaiting Payment' || finalStatus === 'Paid') &&
        newInvoice.type === 'credit_note' && !!newInvoice.linkedInvoiceId;
      const canSaveLocally = hasLocalPostgresSession();

      if (canSaveLocally) {
        const isLocalEdit = isEditing && !!selectedInvoice;
        const localDocumentType = isCreditNoteModalOpen ? 'credit_note' : (isRepeatingModalOpen ? 'repeating_invoice' : newInvoice.type);
        const localDocumentStatus = isRepeatingModalOpen ? 'Draft' : finalStatus;
        // Drafts must not reuse the next approved invoice number. Approval reserves
        // that number server-side, while this identifier only needs to be unique.
        const localDraftNumber = !isLocalEdit && localDocumentStatus === 'Draft' && ['invoice', 'repeating_invoice'].includes(localDocumentType)
          ? `INV-DRAFT-${crypto.randomUUID()}`
          : newInvoice.number;
        const localDocument = {
          ...newInvoice,
          number: localDraftNumber,
          type: localDocumentType,
          status: localDocumentStatus,
          contactName,
          contactEmail,
          items: itemsWithTotal,
          totalAmount: requestedCreditAmount,
          actionDetails,
          ...(isApplyCreditMode ? { creditApplicationAmount: requestedCreditAmount } : {}),
          ...(isRepeatingModalOpen ? { repeatingConfig } : {})
        };
        const response = await fetch(
          isLocalEdit
            ? `/api/organizations/${orgId}/documents/${selectedInvoice!.id}`
            : `/api/organizations/${orgId}/documents`,
          {
            method: isLocalEdit ? 'PATCH' : 'POST',
            headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(localDocument)
          }
        );
        if (!response.ok) {
          let message = `Could not ${isLocalEdit ? 'update' : 'create'} invoice`;
          try {
            const errorBody = await response.json();
            if (typeof errorBody?.error === 'string' && errorBody.error.trim()) message = errorBody.error;
          } catch {
            // Keep the operation-specific fallback when the API returns no JSON body.
          }
          throw new Error(message);
        }
        const savedDocument = await response.json() as Invoice;
        let generatedInvoice: Invoice | null = null;

        if (isRepeatingModalOpen) {
          const generateResponse = await fetch(
            `/api/organizations/${orgId}/documents/${savedDocument.id}/generate-recurring`,
            {
              method: 'POST',
              headers: localApiHeaders()
            }
          );
          if (!generateResponse.ok) throw new Error('Could not generate recurring invoice');
          generatedInvoice = await generateResponse.json() as Invoice;
        }

        let updatedCreditNote = savedDocument;
        let updatedTargetInvoice: Partial<Invoice> | null = null;
        const creditTargetId = applyCreditTargetId || newInvoice.linkedInvoiceId;
        const shouldApplyCreditLocally = !!creditTargetId && newInvoice.type === 'credit_note' && (
          (isLocalEdit && requiresCreditApplication) ||
          (!isLocalEdit && statusOverride === 'Awaiting Payment' && isCreditNoteModalOpen)
        );

        if (shouldApplyCreditLocally) {
          const applyResponse = await fetch(
            `/api/organizations/${orgId}/credit-notes/${savedDocument.id}/apply`,
            {
              method: 'POST',
              headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
              body: JSON.stringify({ invoiceId: creditTargetId, amount: isApplyCreditMode ? requestedCreditAmount : undefined })
            }
          );
          if (!applyResponse.ok) throw new Error('Could not apply credit note');

          const application = await applyResponse.json() as {
            creditNote: Partial<Invoice>;
            invoice: Partial<Invoice> & { paidAmount?: number };
            paymentId: string;
            appliedAmount: number;
          };
          updatedCreditNote = { ...updatedCreditNote, ...application.creditNote };
          const paymentReceived = application.invoice.paymentReceived ?? application.invoice.paidAmount;
          updatedTargetInvoice = {
            ...application.invoice,
            ...(paymentReceived !== undefined ? { paymentReceived } : {})
          };
          setInvoicePayments(prev => [...prev, {
            id: application.paymentId,
            amount: application.appliedAmount,
            date: new Date().toISOString(),
            reference: updatedCreditNote.number,
            accountId: 'credit_note_applied',
            accountName: 'Applied Credit',
            sourceId: updatedCreditNote.id,
            description: `Credit applied from ${updatedCreditNote.number}`,
          }]);
        }

        setInvoices(prev => {
          const documents = isLocalEdit
            ? prev.map(invoice => invoice.id === updatedCreditNote.id ? updatedCreditNote : invoice)
            : [updatedCreditNote, ...prev];
          const documentsWithGeneratedInvoice = generatedInvoice
            ? [generatedInvoice, ...documents.filter(invoice => invoice.id !== generatedInvoice!.id)]
            : documents;

          return updatedTargetInvoice
            ? documentsWithGeneratedInvoice.map(invoice => invoice.id === creditTargetId ? { ...invoice, ...updatedTargetInvoice } : invoice)
            : documentsWithGeneratedInvoice;
        });
        setSelectedInvoice(generatedInvoice || updatedCreditNote);
        notify.confirm(shouldApplyCreditLocally ? 'Credit note created and applied successfully' : 'Invoice saved successfully');
      } else if (isEditing && selectedInvoice) {
        const finalStatus = statusOverride || newInvoice.status;
        const wasApproved = selectedInvoice.status === 'Awaiting Payment' || selectedInvoice.status === 'Paid';
        const isNowApproved = finalStatus === 'Awaiting Payment' || finalStatus === 'Paid';

        const updatedInvoiceData = {
          ...newInvoice,
          status: finalStatus,
          contactName,
          contactEmail,
          items: itemsWithTotal,
          totalAmount,
          updatedAt: serverTimestamp(),
          actionDetails,
          actionLog: arrayUnion(actionEntry)
        };
        await updateDoc(doc(db, 'documents', selectedInvoice.id), updatedInvoiceData);
        await Logger.log(
          orgId,
          selectedInvoice.status !== finalStatus ? 'STATUS_CHANGE' : 'EDIT',
          'Invoices',
          selectedInvoice.id,
          selectedInvoice.number,
          {
            from: selectedInvoice.status,
            to: finalStatus,
            before: selectedInvoice,
            after: { ...newInvoice, status: finalStatus, contactName, contactEmail, items: itemsWithTotal, totalAmount },
            details: actionDetails,
          }
        );

        // If it just became approved and is a credit note with a linked invoice, apply it
        if (!wasApproved && isNowApproved && newInvoice.type === 'credit_note' && newInvoice.linkedInvoiceId) {
          const targetInvoice = invoices.find(inv => inv.id === newInvoice.linkedInvoiceId);
          if (targetInvoice) {
            const paymentDataForInvoice = {
              orgId,
              accountId: 'credit_note_applied',
              accountName: 'Applied Credit',
              type: 'Payment Received',
              date: newInvoice.date,
              description: `Payment from Credit Note ${newInvoice.number}`,
              amount: totalAmount,
              reference: newInvoice.number,
              sourceId: selectedInvoice.id,
              invoiceId: targetInvoice.id,
              createdAt: serverTimestamp(),
              createdBy: auth.currentUser?.uid,
              updatedAt: serverTimestamp()
            };
            await addDoc(collection(db, 'transactions'), paymentDataForInvoice);

            const newTotalPaid = (targetInvoice.paymentReceived || 0) + totalAmount;
            const newStatus = newTotalPaid >= targetInvoice.totalAmount ? 'Paid' : 'Awaiting Payment';

            await updateDoc(doc(db, 'documents', targetInvoice.id), {
              paymentReceived: newTotalPaid,
              status: newStatus,
              updatedAt: serverTimestamp(),
              actionLog: arrayUnion({
                action: 'Credit Applied',
                user: auth.currentUser?.displayName || 'Unknown User',
                email: auth.currentUser?.email || 'No Email',
                timestamp: new Date().toISOString(),
                details: `Credit of ${(totalAmount || 0).toLocaleString()} applied from Credit Note ${newInvoice.number}`
              })
            });
            await Logger.log(orgId, 'FINANCIAL_ACTION', 'Invoices', targetInvoice.id, targetInvoice.number, {
              type: 'PAYMENT_RECEIVED',
              amount: totalAmount,
              account: 'Applied Credit',
              reference: newInvoice.number,
              date: newInvoice.date,
              details: `Credit of ${(totalAmount || 0).toLocaleString()} applied from Credit Note ${newInvoice.number}`,
            });
          }
        }

        // Post journal if just approved
        if (!wasApproved && isNowApproved) {
          if (newInvoice.type === 'credit_note') {
            await postCreditNoteJournal(orgId, { ...newInvoice, id: selectedInvoice.id, items: itemsWithTotal, totalAmount }, auth.currentUser?.uid || 'system');
          } else {
            await postInvoiceJournal(orgId, { ...newInvoice, id: selectedInvoice.id, items: itemsWithTotal, totalAmount }, auth.currentUser?.uid || 'system');
          }
        }

        setSelectedInvoice(prev => prev ? {
          ...prev,
          ...newInvoice,
          status: finalStatus,
          items: itemsWithTotal,
          totalAmount,
          actionLog: prev.actionLog ? [...prev.actionLog, actionEntry] : [actionEntry]
        } : null);
      } else {
        const docData: any = {
          ...newInvoice,
          contactName,
          contactEmail,
          status: statusOverride || newInvoice.status,
          items: itemsWithTotal,
          totalAmount: requestedCreditAmount,
          type: isCreditNoteModalOpen ? 'credit_note' : (isRepeatingModalOpen ? 'repeating_invoice' : 'invoice'),
          createdBy: auth.currentUser.uid,
          createdByName: auth.currentUser.displayName || 'Unknown User',
          createdByEmail: auth.currentUser.email || 'No Email',
          orgId,
          actionDetails,
          actionLog: [actionEntry],
          createdAt: serverTimestamp()
        };

        if (isCreditNoteModalOpen) {
          const prefix = orgSettings?.creditNotePrefix || 'CN-';
          const nextNum = orgSettings?.nextCreditNoteNumber || 1;
          docData.number = `${prefix}${String(nextNum).padStart(3, '0')}`;

          // Increment next number in settings
          const settingsRef = doc(db, 'settings', orgId);
          await updateDoc(settingsRef, {
            nextCreditNoteNumber: increment(1)
          });
        }

        if (statusOverride === 'Awaiting Payment' && !isCreditNoteModalOpen) {
          // Assign the next sequential number if it was a draft
          if (orgSettings) {
            const prefix = orgSettings.invoicePrefix || 'INV-';
            const nextNum = orgSettings.nextInvoiceNumber || 1;
            docData.number = `${prefix}${nextNum}`;

            // Increment next number in settings
            const settingsRef = doc(db, 'settings', orgId);
            await updateDoc(settingsRef, {
              nextInvoiceNumber: increment(1)
            });
          }

          // Post Journal Entry
          const docRef = await addDoc(collection(db, 'documents'), docData);
          await postInvoiceJournal(orgId, { ...docData, id: docRef.id }, auth.currentUser.uid);
          await Logger.log(orgId, 'CREATE', 'Invoices', docRef.id, docData.number, {
            after: docData,
            details: actionDetails,
          });
          await Logger.log(orgId, 'APPROVE', 'Invoices', docRef.id, docData.number, {
            to: docData.status,
            details: `Invoice ${docData.number} approved`,
          });

          if (!isEditing) {
            setSelectedInvoice({ ...docData, id: docRef.id, createdAt: new Date().toISOString() } as any);
          }
        } else if (statusOverride === 'Awaiting Payment' && isCreditNoteModalOpen) {
          docData.status = applyCreditTargetId ? 'Paid' : 'Awaiting Payment';

          const docRef = await addDoc(collection(db, 'documents'), docData);
          await postCreditNoteJournal(orgId, { ...docData, id: docRef.id }, auth.currentUser.uid);
          const creditNoteDetails = `Credit Note ${docData.number} created and approved`;
          await Logger.log(orgId, 'CREATE', 'CreditNote', docRef.id, docData.number, {
            after: docData,
            details: creditNoteDetails,
          });
          await Logger.log(orgId, 'APPROVE', 'CreditNote', docRef.id, docData.number, {
            to: docData.status,
            details: creditNoteDetails,
          });

          if (applyCreditTargetId) {
             const targetInvoice = invoices.find(inv => inv.id === applyCreditTargetId);
             if (targetInvoice) {
               const paymentDataForInvoice = {
                 orgId,
                 accountId: 'credit_note_applied',
                 accountName: 'Applied Credit',
                 type: 'Payment Received',
                 date: docData.date,
                 description: `Payment from Credit Note ${docData.number}`,
                 amount: totalAmount,
                 reference: docData.number,
                 sourceId: docRef.id,
                 invoiceId: targetInvoice.id,
                 createdAt: serverTimestamp(),
                 createdBy: auth.currentUser?.uid,
                 updatedAt: serverTimestamp()
               };
               await addDoc(collection(db, 'transactions'), paymentDataForInvoice);

               const newTotalPaid = (targetInvoice.paymentReceived || 0) + totalAmount;
               const newStatus = newTotalPaid >= targetInvoice.totalAmount ? 'Paid' : 'Awaiting Payment';

               const actionEntry: ActionLogEntry = {
                 action: 'Credit Applied',
                 user: auth.currentUser?.displayName || 'Unknown User',
                 email: auth.currentUser?.email || 'No Email',
                 timestamp: new Date().toISOString(),
                 details: `Credit of ${(totalAmount || 0).toLocaleString()} applied from Credit Note ${docData.number}`
               };

               await updateDoc(doc(db, 'documents', targetInvoice.id), {
                 paymentReceived: newTotalPaid,
                 status: newStatus,
                 updatedAt: serverTimestamp(),
                 actionLog: arrayUnion(actionEntry)
               });
               await Logger.log(orgId, 'FINANCIAL_ACTION', 'Invoices', targetInvoice.id, targetInvoice.number, {
                 type: 'PAYMENT_RECEIVED',
                 amount: totalAmount,
                 account: 'Applied Credit',
                 reference: docData.number,
                 date: docData.date,
                 details: `Credit of ${(totalAmount || 0).toLocaleString()} applied from Credit Note ${docData.number}`,
               });
             }
             setApplyCreditTargetId(null);
          }
        } else if (isRepeatingModalOpen) {
          docData.repeatingConfig = repeatingConfig;
          docData.status = 'Draft'; // Repeating invoices are templates

          // Catch-up logic for past dates
          const startDate = new Date(repeatingConfig.startDate);
          const endDate = repeatingConfig.endDate ? new Date(repeatingConfig.endDate) : null;
          const today = new Date();

          let currentGenDate = new Date(startDate);
          let nextNum = orgSettings?.nextInvoiceNumber || 1;
          const prefix = orgSettings?.invoicePrefix || 'INV-';

          while (currentGenDate <= today && (!endDate || currentGenDate <= endDate)) {
            const genDateStr = currentGenDate.toISOString().split('T')[0];
            const isDraft = repeatingConfig.approveStatus === 'Draft';

            const genInvoiceData = {
              ...docData,
              type: 'invoice',
              status: isDraft ? 'Draft' : 'Awaiting Payment',
              date: genDateStr,
              dueDate: calculateDueDate(genDateStr, repeatingConfig),
              number: isDraft
                ? `DRAFT-${Math.floor(1000 + Math.random() * 9000)}`
                : `${prefix}${nextNum}`,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              actionLog: [{
                action: 'Generated',
                user: 'System',
                email: 'system@accountex.com',
                timestamp: new Date().toISOString(),
                details: `Generated from repeating invoice template`
              }]
            };

            if (!isDraft) {
              nextNum++;
            }

            delete genInvoiceData.repeatingConfig;
            const generatedRef = await addDoc(collection(db, 'documents'), genInvoiceData);
            await Logger.log(orgId, 'CREATE', 'Invoices', generatedRef.id, genInvoiceData.number, {
              after: genInvoiceData,
              details: 'Generated from repeating invoice template',
            });
            if (!isDraft) {
              await Logger.log(orgId, 'APPROVE', 'Invoices', generatedRef.id, genInvoiceData.number, {
                to: genInvoiceData.status,
                details: `Generated invoice ${genInvoiceData.number} approved`,
              });
            }

            // Advance date
            if (repeatingConfig.frequencyUnit === 'Week') {
              currentGenDate.setDate(currentGenDate.getDate() + (repeatingConfig.frequency * 7));
            } else {
              currentGenDate.setMonth(currentGenDate.getMonth() + repeatingConfig.frequency);
            }
          }

          // Update next number in settings if we used any
          if (nextNum > (orgSettings?.nextInvoiceNumber || 1)) {
            await updateDoc(doc(db, 'settings', orgId), {
              nextInvoiceNumber: nextNum
            });
          }

          const docRef = await addDoc(collection(db, 'documents'), docData);
          if (!isEditing) {
            setSelectedInvoice({ ...docData, id: docRef.id, createdAt: new Date().toISOString() } as any);
          }
        } else {
          const docRef = await addDoc(collection(db, 'documents'), docData);
          await Logger.log(orgId, 'CREATE', 'Invoices', docRef.id, docData.number, {
            after: docData,
            details: `Invoice ${docData.number} created`,
          });

          if (!isRepeatingModalOpen && !isCreditNoteModalOpen) {
            // Increment next invoice number in settings
            const settingsRef = doc(db, 'settings', orgId);
            await updateDoc(settingsRef, {
              nextInvoiceNumber: increment(1)
            });
          }

          if (!isEditing) {
            setSelectedInvoice({ ...docData, id: docRef.id, createdAt: new Date().toISOString() } as any);
          }
        }
      }
      setIsModalOpen(false);
      setIsRepeatingModalOpen(false);
      setIsCreditNoteModalOpen(false);
      setIsApplyCreditMode(false);
      setCreditApplicationAmount(0);
      setCreditApplicationMax(0);
      setApplyCreditTargetId(null);
      setIsEditing(false);
      setIsSaving(false);
      setNewInvoice({
        number: `INV-${Math.floor(1000 + Math.random() * 9000)}`,
        contactId: '',
        contactName: '',
        contactEmail: '',
        date: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        reference: '',
        currency: currencies[0],
        brandingTheme: 'Standard',
        amountsAre: 'Tax Exclusive',
        totalAmount: 0,
        items: [{
          item: '',
          description: '',
          quantity: 1,
          unitPrice: 0,
          discount: 0,
          discountType: 'percentage',
          taxAmount: 0,
          total: 0,
          account: '',
          taxRate: '0%',
          trackingCategory: ''
        }],
        status: 'Draft',
        termsAndConditions: '',
        billingAddress: '',
        deliveryAddress: '',
        trackingCategory: '',
        tags: [],
        type: 'invoice',
        linkedInvoiceId: ''
      });

      if (statusOverride === 'Awaiting Payment') {
        notify.confirm('Invoice approved');
      } else {
        notify.confirm(isEditing ? 'Invoice updated' : 'Invoice saved as draft');
      }
    } catch (error) {
      if (hasLocalPostgresSession()) {
        console.error('Local invoice save failed:', error);
        notify.error(error instanceof Error ? error.message : 'Failed to save invoice');
        setIsSaving(false);
        return;
      }
      handleFirestoreError(error, isEditing ? OperationType.UPDATE : OperationType.CREATE, 'documents');
      setIsSaving(false);
    }
  };

  const handleImportCSV = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: async (results: any) => {
            const data = results.data as any[];
            const newInvoices: any[] = [];

            // Group by InvoiceNumber
            const grouped: Record<string, any[]> = data.reduce((acc: Record<string, any[]>, row: any) => {
              const invNum = row['*InvoiceNumber'];
              if (invNum) {
                if (!acc[invNum]) acc[invNum] = [];
                acc[invNum].push(row);
              }
              return acc;
            }, {});

            for (const [invNum, rows] of Object.entries(grouped)) {
              const firstRow = rows[0];
              const contactName = firstRow['*ContactName'];
              const reference = firstRow['Reference'] || '';
              const date = firstRow['*InvoiceDate'] || new Date().toISOString().split('T')[0];
              const dueDate = firstRow['*DueDate'] || date;

              const items = (rows as any[]).map((row: any) => ({
                description: row['*Description'],
                quantity: parseFloat(row['*Quantity']) || 1,
                unitPrice: parseFloat(row['*UnitAmount']) || 0,
                account: row['*AccountCode'] || '',
                taxRate: '0%',
                total: (parseFloat(row['*Quantity']) || 1) * (parseFloat(row['*UnitAmount']) || 0)
              }));

              const totalAmount = items.reduce((sum, item) => sum + item.total, 0);

              // Find contact or create a placeholder
              let contact = contacts.find(c => c.name.toLowerCase() === contactName.toLowerCase());
              let contactId = contact?.id || '';

              newInvoices.push({
                number: invNum,
                contactId,
                contactName,
                contactEmail: contact?.email || '',
                date,
                dueDate,
                reference,
                items,
                totalAmount,
                status: 'Draft',
                type: 'invoice',
                orgId,
                currency: getDefaultCurrency(orgSettings),
                brandingTheme: 'Standard',
                amountsAre: 'Tax Exclusive',
                createdBy: auth.currentUser?.uid,
                createdByName: auth.currentUser?.displayName || 'Unknown User',
                createdByEmail: auth.currentUser?.email || 'No Email',
                createdAt: serverTimestamp()
              });
            }

            try {
              for (const inv of newInvoices) {
                const invoiceRef = await addDoc(collection(db, 'documents'), inv);
                await Logger.log(orgId, 'CREATE', 'Invoices', invoiceRef.id, inv.number, {
                  after: inv,
                  details: `Invoice ${inv.number} imported`,
                });
              }
              notify.confirm(`Successfully imported ${newInvoices.length} invoices`);
            } catch (err) {
              console.error(err);
              notify.error("Failed to import invoices");
            }
          }
        });
      }
    };
    input.click();
  };

  const handleExportCSV = () => {
    const exportData = filteredInvoices.flatMap(inv =>
      (inv.items || []).map(item => ({
        '*ContactName': inv.contactName || '',
        '*InvoiceNumber': inv.number,
        'Reference': inv.reference || '',
        '*InvoiceDate': inv.date,
        '*DueDate': inv.dueDate,
        '*Description': item.description,
        '*Quantity': item.quantity,
        '*UnitAmount': item.unitPrice,
        '*AccountCode': item.account,
        'Last Action By': inv.actionLog?.[0]?.user || inv.createdByName || inv.createdBy || 'System'
      }))
    );

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `invoices_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadTemplate = () => {
    const headers = ['*ContactName', '*InvoiceNumber', 'Reference', '*InvoiceDate', '*DueDate', '*Description', '*Quantity', '*UnitAmount', '*AccountCode'];
    const csv = Papa.unparse([headers]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'invoice_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setNewInvoice({
      number: invoice.number,
      contactId: invoice.contactId,
      contactName: invoice.contactName || '',
      contactEmail: (invoice as any).contactEmail || '',
      date: invoice.date,
      dueDate: invoice.dueDate,
      reference: invoice.reference || '',
      currency: invoice.currency,
      brandingTheme: invoice.brandingTheme || 'Standard',
      amountsAre: invoice.amountsAre || 'Tax Exclusive',
      totalAmount: invoice.totalAmount,
      items: (invoice.items || []).map(item => ({
        ...item,
        item: item.item || '',
        trackingCategory: item.trackingCategory || ''
      })),
      status: invoice.status,
      termsAndConditions: invoice.termsAndConditions || '',
      billingAddress: invoice.billingAddress || '',
      deliveryAddress: invoice.deliveryAddress || '',
      trackingCategory: invoice.trackingCategory || '',
      tags: invoice.tags || [],
      type: invoice.type,
      linkedInvoiceId: invoice.linkedInvoiceId || ''
    });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleUpdateStatus = async (invoiceId: string, newStatus: Invoice['status']) => {
    if (!auth.currentUser && !hasLocalPostgresSession()) return;

    try {
      const invoice = invoices.find(i => i.id === invoiceId);
      if (!invoice) return;

      const userName = auth.currentUser?.displayName || 'Unknown User';
      const actionDetails = `Invoice ${invoice.number} status changed to ${newStatus} by ${userName}`;
      let updatedInvoice: Invoice = { ...invoice, status: newStatus, actionDetails };

      if (hasLocalPostgresSession()) {
        const response = await fetch(`/api/organizations/${orgId}/documents/${invoiceId}`, {
          method: 'PATCH',
          headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus, actionDetails })
        });
        if (!response.ok) throw new Error(`Could not update invoice ${invoice.number}`);
        updatedInvoice = await response.json() as Invoice;
      } else {
        await updateDoc(doc(db, 'documents', invoiceId), {
          status: newStatus,
          updatedAt: serverTimestamp(),
          actionDetails,
          actionLog: arrayUnion({
            action: 'Status Updated',
            user: userName,
            email: auth.currentUser?.email || '',
            timestamp: new Date().toISOString(),
            details: actionDetails
          })
        });
        await Logger.log(orgId, 'STATUS_CHANGE', 'Invoices', invoiceId, invoice.number, {
          from: invoice.status,
          to: newStatus,
          details: actionDetails,
        });
        if (newStatus === 'Void' && invoice.status !== 'Draft') {
          await reverseAutoJournalsForSource(orgId, invoiceId, auth.currentUser.uid, `Invoice ${invoice.number} voided`);
        }
      }

      setInvoices(current => current.map(currentInvoice =>
        currentInvoice.id === invoiceId ? { ...currentInvoice, ...updatedInvoice, status: newStatus } : currentInvoice
      ));
      setSelectedInvoice(current =>
        current?.id === invoiceId ? { ...current, ...updatedInvoice, status: newStatus } : current
      );

      if (newStatus === 'Void') notify.confirm('Invoice voided successfully');
    } catch (error) {
      if (hasLocalPostgresSession()) {
        console.error('Local invoice status update failed:', error);
        notify.error('Failed to update invoice status');
      } else {
        handleFirestoreError(error, OperationType.UPDATE, 'documents');
      }
    }
  };

  const handlePrint = (doc: any, type: 'invoice' | 'quote' | 'delivery_note' | 'purchase_order' = 'invoice') => {
    setDocToPrint(doc);
    setPrintType(type);
    setIsPrintModalOpen(true);
  };

  const calculateItemSubtotal = (item: any) => {
    const subLine = item.quantity * item.unitPrice;
    const discAmount = item.discountType === 'amount' ? (item.discount || 0) : (subLine * (item.discount || 0) / 100);
    return subLine - discAmount;
  };

  const calculateItemTax = (item: any) => {
    const subtotal = calculateItemSubtotal(item);
    // Rough estimate but for portal we use a standard rate if not specified
    const rate = parseFloat(String(item?.taxRate || '').replace(/[^0-9.]/g, '') || '0') / 100;
    return subtotal * rate;
  };

  const calculateItemTotal = (item: any) => {
    return calculateItemSubtotal(item) + calculateItemTax(item);
  };

  const handleDownloadPDF = async () => {
    if (!selectedInvoice) return;

    try {
      await generateInvoicePdf({
        invoice: selectedInvoice,
        organization: { ...(orgSettings || {}), ...(organization || {}) },
        contact: contacts.find(contact => contact.id === selectedInvoice.contactId),
        brandingTheme: brandingThemes.find(theme => theme.id === selectedInvoice.brandingTheme || theme.name === selectedInvoice.brandingTheme),
        calculateSubtotal: calculateItemSubtotal,
        calculateTax: calculateItemTax,
      });
      notify.confirm('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF Generation Error:', err);
      notify.error('Failed to generate PDF');
    }
  };

  const handlePrintDeliveryNote = (dn: DeliveryNote) => {
    handlePrint(dn, 'delivery_note');
  };

  const handleSendEmail = () => {
    if (!selectedInvoice) return;
    const contact = contacts.find(c => c.id === selectedInvoice.contactId);

    const subject = `Invoice ${selectedInvoice.number} from Accountex`;
    const dueDate = new Date(selectedInvoice.dueDate).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    const body = `Hi ${contact?.name || 'Customer'},\n\n` +
                 `Here's invoice ${selectedInvoice.number} for ${selectedInvoice.currency} ${(selectedInvoice?.totalAmount || 0).toLocaleString()}.\n\n` +
                 `The amount outstanding of ${selectedInvoice.currency} ${(selectedInvoice?.totalAmount || 0).toLocaleString()} is due on ${dueDate}.\n\n` +
                 `View and pay your bill online: ${getPortalUrl('invoice', selectedInvoice.id)}\n\n` +
                 `From your online bill you can print a PDF, export a CSV, or create a free login and view your outstanding bills.\n\n` +
                 `If you have any questions, please let us know.\n\n` +
                 `Thanks,\nAccountex`;

    setEmailData({
      to: contact?.email || (contact?.emails && contact.emails.length > 0 ? contact.emails[0] : '') || selectedInvoice.contactEmail || '',
      subject,
      body
    });
    setIsEmailModalOpen(true);
  };

  const handleConfirmSendEmail = () => {
    if (!emailData.to) {
      notify.error("Please enter an email address");
      return;
    }

    window.location.href = `mailto:${emailData.to}?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(emailData.body)}`;
    setIsEmailModalOpen(false);
  };

  const handleRecordPayment = async () => {
    if (!selectedInvoice) {
      notify.error("Please select an invoice");
      return;
    }

    if (!paymentData.accountId) {
      notify.error("Please select a payment account");
      return;
    }

    if (paymentData.amount <= 0) {
      notify.error("Please enter a positive payment amount");
      return;
    }

    try {
      setIsSaving(true);

      if (hasLocalPostgresSession()) {
        const account = accountsList.find(a => a.id === paymentData.accountId) ||
          bankAccountsList.find(a => a.id === paymentData.accountId);
        const accountName = account?.accountName || account?.name || 'Bank';
        const response = await fetch(`/api/organizations/${orgId}/documents/${selectedInvoice.id}/payments`, {
          method: 'POST',
          headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: paymentData.amount,
            date: paymentData.date,
            accountId: paymentData.accountId,
            accountName,
            method: 'Manual',
            reference: paymentData.reference,
          }),
        });
        if (!response.ok) throw new Error('Could not record payment');

        const payment = await response.json() as { id: string; amount: number; paidAmount: number; status: Invoice['status']; journalId: string };
        const updatedInvoice = {
          ...selectedInvoice,
          paymentReceived: payment.paidAmount,
          status: payment.status,
        };
        // The detail view calculates Total Due from invoicePayments, so update it
        // immediately instead of waiting for the payment-history request to rerun.
        setInvoicePayments(prev => [{
          id: payment.id,
          amount: payment.amount,
          accountId: paymentData.accountId,
          accountName,
          type: 'received',
          method: 'Manual',
          reference: paymentData.reference,
          date: paymentData.date,
          status: 'posted',
        }, ...prev]);
        setSelectedInvoice(updatedInvoice);
        setInvoices(prev => prev.map(invoice => invoice.id === selectedInvoice.id ? {
          ...invoice,
          paymentReceived: payment.paidAmount,
          status: payment.status,
        } : invoice));
        setIsPaymentModalOpen(false);
        notify.confirm(payment.status === 'Paid' ? "Invoice fully paid" : "Partial payment recorded");
        return;
      }

const currentTotalPaid = invoicePayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
      const newTotalPaid = currentTotalPaid + paymentData.amount;
      const isFullyPaid = newTotalPaid >= selectedInvoice.totalAmount;
      const newStatus = isFullyPaid ? 'Paid' : 'Awaiting Payment';

      const account = accountsList.find(a => a.id === paymentData.accountId) ||
                      bankAccountsList.find(a => a.id === paymentData.accountId);
      const accountName = account?.accountName || account?.name || 'Bank';

      const actionEntry: ActionLogEntry = {
        action: 'Payment Added',
        user: auth.currentUser?.displayName || 'Unknown User',
        email: auth.currentUser?.email || 'No Email',
        timestamp: new Date().toISOString(),
        details: `Payment of ${(paymentData?.amount || 0).toLocaleString()} received via ${accountName}${paymentData.reference ? ` (Ref: ${paymentData.reference})` : ''}`
      };

      // Update invoice status and payment info
      await updateDoc(doc(db, 'documents', selectedInvoice.id), {
        status: newStatus,
        paymentReceived: newTotalPaid,
        paymentDate: paymentData.date,
        paymentAccount: paymentData.accountId,
        paymentReference: paymentData.reference,
        updatedAt: serverTimestamp(),
        actionLog: arrayUnion(actionEntry)
      });
      await Logger.log(orgId, 'FINANCIAL_ACTION', 'Invoices', selectedInvoice.id, selectedInvoice.number, {
        type: 'PAYMENT_RECEIVED',
        amount: paymentData.amount,
        account: accountName,
        reference: paymentData.reference,
        date: paymentData.date,
        isFullyPaid,
        details: `Payment of ${(paymentData?.amount || 0).toLocaleString()} received via ${accountName}${paymentData.reference ? ` (Ref: ${paymentData.reference})` : ''}`,
      });

      // Also record a transaction
      const transactionData = {
        date: paymentData.date,
        description: `Payment for Invoice ${selectedInvoice.number}`,
        category: 'Sales',
        amount: paymentData.amount,
        status: 'Completed',
        accountId: paymentData.accountId,
        reference: paymentData.reference,
        invoiceId: selectedInvoice.id,
        orgId,
        createdBy: auth.currentUser?.uid,
        createdAt: serverTimestamp()
      };
      const paymentDoc = await addDoc(collection(db, 'transactions'), transactionData);

      // Post Payment Journal
      await postPaymentJournal(orgId, { ...transactionData, id: paymentDoc.id }, selectedInvoice, auth.currentUser?.uid || 'system', 'invoice');

      notify.confirm(isFullyPaid ? "Invoice fully paid" : "Partial payment recorded");
      setIsPaymentModalOpen(false);
      setSelectedInvoice(prev => prev ? {
        ...prev,
        status: newStatus,
        paymentReceived: newTotalPaid,
        actionLog: prev.actionLog ? [...prev.actionLog, actionEntry] : [actionEntry]
      } : null);
    } catch (error) {
      if (hasLocalPostgresSession()) {
        console.error('Local payment submission failed:', error);
        notify.error('Failed to record payment');
      } else {
        handleFirestoreError(error, OperationType.UPDATE, 'documents');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemovePayment = async (payment: any) => {
    if (!selectedInvoice || isSaving) return;

    try {
      setIsSaving(true);

      if (hasLocalPostgresSession()) {
        const response = await fetch(`/api/organizations/${orgId}/documents/${selectedInvoice.id}/payments/${payment.id}`, {
          method: 'DELETE',
          headers: localApiHeaders(),
        });
        if (!response.ok) throw new Error('Could not remove payment');

        const result = await response.json() as {
          paymentId: string;
          paidAmount: number;
          status: Invoice['status'];
          reversalJournalId: string;
        };
        const updatedInvoice = {
          ...selectedInvoice,
          paymentReceived: result.paidAmount,
          status: result.status,
        };
        setInvoicePayments(prev => prev.filter(existingPayment => existingPayment.id !== result.paymentId));
        setSelectedInvoice(updatedInvoice);
        setInvoices(prev => prev.map(invoice => invoice.id === selectedInvoice.id ? {
          ...invoice,
          paymentReceived: result.paidAmount,
          status: result.status,
        } : invoice));
        setPaymentToDelete(null);
        notify.confirm('Payment removed successfully');
        return;
      }

      // Delete the transaction
      await deleteDoc(doc(db, 'transactions', payment.id));

      // Calculate new total paid
      const newTotalPaid = (selectedInvoice.paymentReceived || 0) - payment.amount;
      const newStatus = newTotalPaid >= selectedInvoice.totalAmount ? 'Paid' : 'Awaiting Payment';

      const actionEntry: ActionLogEntry = {
        action: 'Payment Removed',
        user: auth.currentUser?.displayName || 'Unknown User',
        email: auth.currentUser?.email || 'No Email',
        timestamp: new Date().toISOString(),
        details: `Payment of ${(payment?.amount || 0).toLocaleString()} removed`
      };

      // Update invoice status and payment info
      await updateDoc(doc(db, 'documents', selectedInvoice.id), {
        status: newStatus,
        paymentReceived: newTotalPaid,
        updatedAt: serverTimestamp(),
        actionLog: arrayUnion(actionEntry)
      });
      await Logger.log(orgId, 'FINANCIAL_ACTION', 'Invoices', selectedInvoice.id, selectedInvoice.number, {
        type: 'PAYMENT_REVERSED',
        amount: payment.amount,
        reason: 'Payment record deleted',
        details: `Payment of ${(payment?.amount || 0).toLocaleString()} removed from invoice ${selectedInvoice.number}`,
      });

      notify.confirm("Payment removed successfully");
      setPaymentToDelete(null);
      setSelectedInvoice(prev => prev ? {
        ...prev,
        status: newStatus,
        paymentReceived: newTotalPaid,
        actionLog: prev.actionLog ? [...prev.actionLog, actionEntry] : [actionEntry]
      } : null);
    } catch (error) {
      if (hasLocalPostgresSession()) {
        console.error('Local payment reversal failed:', error);
        notify.error('Failed to remove payment');
      } else {
        handleFirestoreError(error, OperationType.DELETE, 'transactions');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveReminders = async () => {
    if (!orgId) return;
    try {
      setIsSaving(true);
      if (hasLocalPostgresSession()) {
        const response = await fetch(`/api/settings/${orgId}`, {
          method: 'PATCH',
          headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoiceReminders: remindersSettings })
        });
        if (!response.ok) throw new Error(await response.text());
      } else {
        const docRef = doc(db, 'settings', orgId);
        await updateDoc(docRef, {
          invoiceReminders: remindersSettings,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.email
        });
      }
      setOrgSettings(prev => ({ ...prev, invoiceReminders: remindersSettings }));
      setIsRemindersModalOpen(false);
      notify.confirm('Invoice reminders updated successfully');
    } catch (error) {
      console.error('Error saving reminders:', error);
      notify.error('Failed to save reminder settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleReminder = (id: string) => {
    setRemindersSettings(prev => ({
      ...prev,
      reminders: prev.reminders.map(r =>
        r.id === id ? { ...r, active: !r.active } : r
      )
    }));
  };

  const handleAddReminder = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    const lastReminder = remindersSettings.reminders[remindersSettings.reminders.length - 1];
    const newDays = lastReminder ? lastReminder.days + 7 : 7;

    const newReminder = {
      id: newId,
      days: newDays,
      active: true,
      type: 'overdue' as const,
      subject: 'Bill [Invoice Number] from [Trading Name] is due',
      message: `Hi [Contact First Name],\n\nThanks for working with us. Your bill for [Currency Symbol][Amount Due Without Currency] was due on [Due Date].\n\nIf you've already paid it, please ignore this email and sorry for bothering you. If you've not paid it, please do so as soon as possible.\n\nTo view your bill visit [Online Invoice Link].\n\nIf you've got any questions, or want to arrange alternative payment don't hesitate to get in touch.\n\nThanks`
    };

    setRemindersSettings(prev => ({
      ...prev,
      reminders: [...prev.reminders, newReminder]
    }));
  };

  const handleEditReminder = (reminder: any) => {
    setEditingReminder({
      ...reminder,
      subject: reminder.subject || 'Bill [Invoice Number] from [Trading Name] is due',
      message: reminder.message || `Hi [Contact First Name],\n\nThanks for working with us. Your bill for [Currency Symbol][Amount Due Without Currency] was due on [Due Date].\n\nIf you've already paid it, please ignore this email and sorry for bothering you. If you've not paid it, please do so as soon as possible.\n\nTo view your bill visit [Online Invoice Link].\n\nIf you've got any questions, or want to arrange alternative payment don't hesitate to get in touch.\n\nThanks`
    });
    setIsEditReminderModalOpen(true);
  };

  const handleSaveEditReminder = () => {
    if (!editingReminder) return;
    setRemindersSettings(prev => ({
      ...prev,
      reminders: prev.reminders.map(r =>
        r.id === editingReminder.id ? editingReminder : r
      )
    }));
    setIsEditReminderModalOpen(false);
    setEditingReminder(null);
  };

  const handleSaveBatchDeposit = async () => {
    if (Object.keys(selectedInvoicesForBatch).length === 0 || !batchDepositSettings.accountId) {
      notify.error("Please select a deposit account and at least one invoice");
      return;
    }

    try {
      setIsSaving(true);
      const invoiceIds = Object.keys(selectedInvoicesForBatch);

      for (const id of invoiceIds) {
        const amount = selectedInvoicesForBatch[id];
        if (amount <= 0) continue;

        const invoice = invoices.find(i => i.id === id);
        if (!invoice) continue;

        const currentTotalPaid = invoice.paymentReceived || 0;
        const newTotalPaid = currentTotalPaid + amount;
        const isFullyPaid = newTotalPaid >= invoice.totalAmount;
        const newStatus = isFullyPaid ? 'Paid' : 'Awaiting Payment';

        const account = accountsList.find(a => a.id === batchDepositSettings.accountId) ||
                        bankAccountsList.find(a => a.id === batchDepositSettings.accountId);
        const accountName = account?.accountName || account?.name || 'Bank';

        const actionEntry: ActionLogEntry = {
          action: 'Batch Payment Recieved',
          user: auth.currentUser?.displayName || 'Unknown User',
          email: auth.currentUser?.email || 'No Email',
          timestamp: new Date().toISOString(),
          details: `Batch payment of ${amount.toLocaleString()} received via ${accountName}${batchDepositSettings.reference ? ` (Ref: ${batchDepositSettings.reference})` : ''}`
        };

        // Update invoice
        await updateDoc(doc(db, 'documents', id), {
          status: newStatus,
          paymentReceived: newTotalPaid,
          paymentDate: batchDepositSettings.date,
          paymentAccount: batchDepositSettings.accountId,
          paymentReference: batchDepositSettings.reference,
          updatedAt: serverTimestamp(),
          actionLog: arrayUnion(actionEntry)
        });

        await Logger.log(orgId, 'FINANCIAL_ACTION', 'Invoices', id, invoice.number, {
          type: 'PAYMENT_RECEIVED',
          amount,
          account: accountName,
          reference: batchDepositSettings.reference,
          date: batchDepositSettings.date,
          isFullyPaid,
          details: `Batch payment of ${amount.toLocaleString()} received via ${accountName}${batchDepositSettings.reference ? ` (Ref: ${batchDepositSettings.reference})` : ''}`,
        });

        // Record transaction
        const transactionData = {
          date: batchDepositSettings.date,
          description: `Payment for Invoice ${invoice.number} (Batch: ${batchDepositSettings.reference})`,
          category: 'Sales',
          amount: amount,
          status: 'Completed',
          accountId: batchDepositSettings.accountId,
          reference: batchDepositSettings.reference,
          invoiceId: id,
          orgId,
          createdBy: auth.currentUser?.uid,
          createdAt: serverTimestamp()
        };
        const paymentDoc = await addDoc(collection(db, 'transactions'), transactionData);

        // Post Journal
        await postPaymentJournal(orgId, { ...transactionData, id: paymentDoc.id }, invoice, auth.currentUser?.uid || 'system', 'invoice');
      }

      notify.confirm(`Batch deposit of ${invoiceIds.length} payments recorded successfully`);
      setIsBatchDepositModalOpen(false);
      setSelectedInvoicesForBatch({});
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'documents');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteReminder = (id: string) => {
    setRemindersSettings(prev => ({
      ...prev,
      reminders: prev.reminders.filter(r => r.id !== id)
    }));
    setIsEditReminderModalOpen(false);
    setEditingReminder(null);
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!auth.currentUser || isSaving) return;

    try {
      setIsSaving(true);
      const invoice = invoices.find(i => i.id === invoiceId);
      if (!invoice) return;

      const actionDetails = `Invoice ${invoice.number} deleted by ${auth.currentUser.displayName || 'Unknown User'} on ${new Date().toLocaleString()}`;

      const actionEntry: ActionLogEntry = {
        action: 'Deleted',
        user: auth.currentUser?.displayName || 'Unknown User',
        email: auth.currentUser?.email || 'No Email',
        timestamp: new Date().toISOString(),
        details: actionDetails
      };

      await updateDoc(doc(db, 'documents', invoiceId), {
        status: 'Void',
        updatedAt: serverTimestamp(),
        deletedBy: auth.currentUser.uid,
        deletedByName: auth.currentUser.displayName || 'Unknown User',
        deletedByEmail: auth.currentUser.email || 'No Email',
        deletedAt: serverTimestamp(),
        actionDetails: actionDetails,
        actionLog: arrayUnion(actionEntry)
      });
      await Logger.log(orgId, 'STATUS_CHANGE', 'Invoices', invoiceId, invoice.number, {
        from: invoice.status,
        to: 'Void',
        reason: 'Manually deleted/voided',
        details: actionDetails,
      });
      if (invoice.status !== 'Draft') {
        await reverseAutoJournalsForSource(orgId, invoiceId, auth.currentUser.uid, `Invoice ${invoice.number} voided`);
      }

      setInvoices(current => current.map(currentInvoice =>
        currentInvoice.id === invoiceId
          ? {
              ...currentInvoice,
              status: 'Void',
              deletedByName: auth.currentUser?.displayName || 'Unknown User',
              deletedByEmail: auth.currentUser?.email || 'No Email',
              actionDetails
            }
          : currentInvoice
      ));

      if (selectedInvoice?.id === invoiceId) {
        setSelectedInvoice(prev => prev ? {
          ...prev,
          status: 'Void',
          deletedByName: auth.currentUser?.displayName || 'Unknown User',
          deletedByEmail: auth.currentUser?.email || 'No Email',
          actionDetails: actionDetails
        } : null);
      }

      notify.confirm("Invoice deleted successfully");
      setInvoiceToDelete(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'documents');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredInvoices = invoices.filter(i => {
    const matchesFilter = filter === 'all'
      ? (i.status !== 'Deleted' && i.status !== 'Void' && i.type !== 'repeating_invoice')
      : (filter === 'void'
          ? (i.status === 'Void' || i.status === 'Deleted')
          : (filter === 'repeating'
              ? i.type === 'repeating_invoice'
              : (filter === 'awaiting payment'
                  ? i.status === 'Awaiting Payment'
                  : i.status.toLowerCase() === filter.toLowerCase())));
    const contact = contacts.find(c => c.id === i.contactId);
    const contactName = contact?.name || i.contactName || 'Walk-in Customer';
    const tagMatches = i.tags?.some(tagName => tagName.toLowerCase().includes(searchQuery.toLowerCase())) || false;
    const matchesSearch = contactName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         i.number.toLowerCase().includes(searchQuery.toLowerCase()) || tagMatches;
    const matchesSource = sourceFilter ? i.source === sourceFilter : true;
    const matchesDate = (!dateRange.start || i.date >= dateRange.start) &&
                       (!dateRange.end || i.date <= dateRange.end);

    // Sales History specific filters
    if (sourceFilter === 'POS') {
      const matchesHistoryTab = historyTab === 'all'
        ? true
        : (historyTab === 'return' ? i.status === 'Paid' : i.status === 'Awaiting Payment');

      const matchesDate = (!dateRange.start || i.date >= dateRange.start) &&
                         (!dateRange.end || i.date <= dateRange.end);

      let matchesTime = true;
      if (showTimeRange && (timeRange.start || timeRange.end)) {
        // Try to get time from createdAt timestamp if available, otherwise from a 'time' field
        let invoiceTime = '';
        if (i.createdAt && typeof i.createdAt.toDate === 'function') {
          const date = i.createdAt.toDate();
          invoiceTime = date.toTimeString().split(' ')[0].substring(0, 5); // HH:mm
        } else if (i.time) {
          invoiceTime = i.time;
        }

        if (invoiceTime) {
          const start = timeRange.start || '00:00';
          const end = timeRange.end || '23:59';
          matchesTime = invoiceTime >= start && invoiceTime <= end;
        } else {
          // If no time info, we can't filter by time, so we exclude it if a range is set
          matchesTime = false;
        }
      }

      const posMatchesCustomer = !customerSearch ||
                             contactName.toLowerCase().includes(customerSearch.toLowerCase());

      const posMatchesReceipt = !receiptNote ||
                            i.number.toLowerCase().includes(receiptNote.toLowerCase()) ||
                            (i.reference && i.reference.toLowerCase().includes(receiptNote.toLowerCase()));

      return matchesFilter && matchesSearch && matchesSource && matchesHistoryTab && matchesDate && matchesTime && posMatchesCustomer && posMatchesReceipt;
    }

    // Advanced search
    const matchesRef = !refFilter || (i.reference || '').toLowerCase().includes(refFilter.toLowerCase()) || i.number.toLowerCase().includes(refFilter.toLowerCase());
    const matchesCustomer = !customerFilter || contactName.toLowerCase().includes(customerFilter.toLowerCase());
    const matchesTag = !tagFilter || (i.tags && i.tags.includes(tagFilter));
    const matchesStatus = statusFilter === 'all' || i.status.toLowerCase() === statusFilter.toLowerCase();

    const linkedDelivery = deliveryNotes.find(dn => dn.invoiceId === i.id);
    const deliveryStatus = linkedDelivery ? linkedDelivery.status : 'Not Processed';
    const matchesDeliveryStatus = deliveryStatusFilter === 'all' || deliveryStatus.toLowerCase() === deliveryStatusFilter.toLowerCase();

    // Payment Status Logic
    let paymentStatus = i.status === 'Paid' ? 'Paid' : (i.paymentReceived && i.paymentReceived > 0 ? 'Partially Paid' : 'Unpaid');
    const matchesPaymentStatus = paymentStatusFilter === 'all' || paymentStatus.toLowerCase() === paymentStatusFilter.toLowerCase();

    const matchesTotalAmount = (!minTotalAmount || i.totalAmount >= parseFloat(minTotalAmount)) &&
                              (!maxTotalAmount || i.totalAmount <= parseFloat(maxTotalAmount));

    return matchesFilter && matchesSearch && matchesSource && matchesDate && matchesRef && matchesCustomer && matchesTag && matchesStatus && matchesDeliveryStatus && matchesPaymentStatus && matchesTotalAmount;
  }).sort((a, b) => {
    if (sortDirection === 'asc') {
      return a.number.localeCompare(b.number);
    } else if (sortDirection === 'desc') {
      return b.number.localeCompare(a.number);
    }
    return 0;
  });

  const getInvoiceStatusDetails = (invoice: Invoice) => {
    if (invoice.status === 'Paid') {
      return {
        label: language === 'ar' ? 'تم الدفع' : 'Paid',
        color: "bg-green-100 text-green-600",
        icon: <CheckCircle2 className="w-4 h-4" />
      };
    }
    if (invoice.status === 'Awaiting Payment') {
      const today = new Date();
      today.setHours(0,0,0,0);
      const due = new Date(invoice.dueDate);
      due.setHours(0,0,0,0);

      if (today > due) {
        return {
          label: language === 'ar' ? 'متأخرة' : 'Overdue',
          color: "bg-red-100 text-red-600",
          icon: <AlertTriangle className="w-4 h-4" />
        };
      } else {
        return {
          label: language === 'ar' ? 'في فترة الائتمان' : 'In Credit Period',
          color: "bg-blue-100 text-blue-600",
          icon: <Clock className="w-4 h-4" />
        };
      }
    }

    // Default status handling
    const labels: Record<string, string> = {
      'Draft': language === 'ar' ? 'مسودة' : 'Draft',
      'Awaiting Approval': language === 'ar' ? 'في انتظار الموافقة' : 'Awaiting Approval',
      'Void': language === 'ar' ? 'ملغي' : 'Void',
      'Deleted': language === 'ar' ? 'محذوف' : 'Deleted'
    };

    return {
      label: labels[invoice.status] || invoice.status,
      color: getStatusColor(invoice.status),
      icon: getStatusIcon(invoice.status)
    };
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Draft': return <Clock className="w-4 h-4" />;
      case 'Awaiting Approval': return <AlertCircle className="w-4 h-4" />;
      case 'Awaiting Payment': return <CreditCard className="w-4 h-4" />;
      case 'Paid': return <CheckCircle2 className="w-4 h-4" />;
      case 'Void': return <XCircle className="w-4 h-4" />;
      case 'Deleted': return <Trash2 className="w-4 h-4" />;
      default: return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft': return "bg-primary/10 text-primary";
      case 'Awaiting Approval': return "bg-amber-100 text-amber-600";
      case 'Awaiting Payment': return "bg-primary/20 text-primary";
      case 'Paid': return "bg-green-100 text-green-600";
      case 'Void': return "bg-red-100 text-red-600";
      case 'Deleted': return "bg-red-50 text-red-400 opacity-70";
      default: return "bg-primary/10 text-primary";
    }
  };

  const activeInvoiceTheme = selectedInvoice ? brandingThemes.find(t => t.id === selectedInvoice.brandingTheme) : null;
  const selectedInvoiceStatus = selectedInvoice ? normalizeDocumentStatus(selectedInvoice.status) : 'draft';
  const selectedInvoiceLegacyStatus = String(selectedInvoice?.legacyStatus || selectedInvoice?.status || '').trim().toLowerCase();
  const selectedInvoiceIsDraft = selectedInvoiceStatus === 'draft';
  const selectedInvoiceIsAwaitingApproval = selectedInvoiceIsDraft && selectedInvoiceLegacyStatus === 'awaiting approval';
  const selectedInvoicePaymentStatus = selectedInvoice
    ? derivePaymentStatus(
        selectedInvoice.totalAmount,
        selectedInvoice.paymentReceived ?? invoicePayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0),
      )
    : 'unpaid';
  const selectedInvoiceSourceQuoteId = selectedInvoice?.sourceQuoteId || selectedInvoice?.quoteId || '';
  const selectedInvoiceSourceQuote = selectedInvoiceSourceQuoteId
    ? allQuotes.find(quote => quote.id === selectedInvoiceSourceQuoteId)
    : null;
  const selectedInvoiceSourceQuoteNumber = selectedInvoice?.sourceQuoteNumber
    || (typeof selectedInvoiceSourceQuote?.number === 'string'
      ? selectedInvoiceSourceQuote.number
      : typeof selectedInvoiceSourceQuote?.number === 'number'
        ? String(selectedInvoiceSourceQuote.number)
        : '');
  const isInvoiceEditReadOnly = isEditing && normalizeDocumentStatus(newInvoice.status) === 'done';

  const getFilterCount = (f: string) => {
    if (f === 'all') return null;
    return invoices.filter(i => {
      if (f === 'void') return i.status === 'Void' || i.status === 'Deleted';
      if (f === 'repeating') return i.type === 'repeating_invoice';
      return i.status.toLowerCase() === f.toLowerCase();
    }).length;
  };

  return (
    <div className="space-y-6 pt-2 sm:pt-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {sourceFilter === 'POS' ? (language === 'ar' ? 'سجل المبيعات' : 'Sales History') : (language === 'ar' ? 'الفواتير' : 'Sales Invoices')}
          </h1>
          <p className="text-sm opacity-50 italic serif mt-1">
            {sourceFilter === 'POS' ? (language === 'ar' ? 'تتبع معاملات نقطة البيع الخاصة بك' : 'Track your point of sale transactions') : (language === 'ar' ? 'إدارة وتتبع فواتير العملاء' : 'Manage and track your customer invoices')}
          </p>
        </div>
        {!sourceFilter && (
          <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar pb-2 sm:pb-0 max-w-full">
            <button
              onClick={() => {
                setIsEditing(false);
                setIsModalOpen(true);
              }}
              className="flex items-center gap-1.5 bg-primary text-primary-foreground px-2.5 py-1.5 rounded-lg hover:opacity-90 transition-opacity font-bold text-[10px] whitespace-nowrap shadow-sm shrink-0"
            >
              <Plus className="w-3 h-3" />
              <span>{language === 'ar' ? 'فاتورة جديدة' : 'New Invoice'}</span>
            </button>

            <button
              onClick={() => {
                setIsEditing(false);
                setIsRepeatingModalOpen(true);
              }}
              className="flex items-center gap-1.5 bg-white border border-primary/20 text-primary px-2.5 py-1.5 rounded-lg hover:bg-primary/5 transition-all font-bold text-[10px] whitespace-nowrap shadow-sm shrink-0"
            >
              <Repeat className="w-3 h-3" />
              <span>{language === 'ar' ? 'متكررة' : 'Repeating'}</span>
            </button>

            {(filter === 'paid' || filter === 'awaiting payment') && (
              <button
                onClick={() => {
                  setIsEditing(false);
                  setIsCreditNoteModalOpen(true);
                }}
                className="flex items-center gap-1.5 bg-white border border-primary text-primary px-2.5 py-1.5 rounded-lg hover:bg-primary/5 transition-all font-bold text-[10px] whitespace-nowrap shadow-sm shrink-0"
              >
                <FileText className="w-3 h-3" />
                <span>{filter === 'paid' ? (language === 'ar' ? 'استرداد' : 'Refund') : (language === 'ar' ? 'تطبيق رصيد' : 'Apply Credit')}</span>
              </button>
            )}

            <button
              onClick={() => setIsRemindersModalOpen(true)}
              className="flex items-center gap-1.5 bg-white border border-primary text-primary px-2.5 py-1.5 rounded-lg hover:bg-primary/5 transition-all font-bold text-[10px] whitespace-nowrap shadow-sm shrink-0"
            >
              <Bell className="w-3 h-3" />
              <span>{language === 'ar' ? 'تذكيرات' : 'Reminders'}</span>
              {remindersSettings.enabled && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] font-black text-green-700">
                  <CheckCircle2 className="w-3 h-3" />
                  <span>{language === 'ar' ? 'مفعل' : 'On'}</span>
                </span>
              )}
            </button>

            <button
              onClick={() => setIsImportOptionsOpen(true)}
              className="flex items-center gap-1.5 bg-white border border-primary text-primary px-2.5 py-1.5 rounded-lg hover:bg-primary/5 transition-all font-bold text-[10px] whitespace-nowrap shadow-sm shrink-0"
            >
              <Upload className="w-3 h-3" />
              <span>{language === 'ar' ? 'استيراد' : 'Import'}</span>
            </button>

            <button
              onClick={() => setIsExportOptionsOpen(true)}
              className="flex items-center gap-1.5 bg-white border border-primary text-primary px-2.5 py-1.5 rounded-lg hover:bg-primary/5 transition-all font-bold text-[10px] whitespace-nowrap shadow-sm shrink-0"
            >
              <Download className="w-3 h-3" />
              <span>{language === 'ar' ? 'تصدير' : 'Export'}</span>
            </button>
          </div>
        )}
      </div>

      {isImportOptionsOpen && (
        <div
          className="fixed inset-0 z-[250] flex items-center justify-center bg-primary/40 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invoice-import-options-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsImportOptionsOpen(false);
          }}
        >
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-primary/20 bg-background text-primary shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-primary/10 p-6">
              <div>
                <h2 id="invoice-import-options-title" className="text-xl font-bold italic serif">
                  {language === 'ar' ? 'خيارات الاستيراد' : 'Import Options'}
                </h2>
                <p className="mt-1 text-xs opacity-50">
                  {language === 'ar' ? 'اختر طريقة استيراد الفواتير' : 'Choose how you want to import invoices'}
                </p>
              </div>
              <button
                onClick={() => setIsImportOptionsOpen(false)}
                className="rounded-full p-2 transition-colors hover:bg-primary/10"
                aria-label={language === 'ar' ? 'إغلاق' : 'Close'}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid gap-3 p-6">
              <button
                onClick={() => {
                  setIsImportOptionsOpen(false);
                  handleImportCSV();
                }}
                className="flex items-center gap-4 rounded-2xl border border-primary/15 p-4 text-left transition-all hover:border-primary/40 hover:bg-primary/5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <FileUp className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm font-bold">{language === 'ar' ? 'تحميل CSV' : 'Upload CSV'}</span>
                  <span className="mt-0.5 block text-xs opacity-50">{language === 'ar' ? 'استيراد الفواتير من ملف CSV' : 'Import invoices from a CSV file'}</span>
                </span>
              </button>
              <button
                onClick={() => {
                  setIsImportOptionsOpen(false);
                  downloadTemplate();
                }}
                className="flex items-center gap-4 rounded-2xl border border-primary/15 p-4 text-left transition-all hover:border-primary/40 hover:bg-primary/5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <Download className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm font-bold">{language === 'ar' ? 'تحميل النموذج' : 'Download Template'}</span>
                  <span className="mt-0.5 block text-xs opacity-50">{language === 'ar' ? 'تنزيل نموذج CSV للاستيراد' : 'Download the CSV import template'}</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {isExportOptionsOpen && (
        <div
          className="fixed inset-0 z-[250] flex items-center justify-center bg-primary/40 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invoice-export-options-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsExportOptionsOpen(false);
          }}
        >
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-primary/20 bg-background text-primary shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-primary/10 p-6">
              <div>
                <h2 id="invoice-export-options-title" className="text-xl font-bold italic serif">
                  {language === 'ar' ? 'خيارات التصدير' : 'Export Options'}
                </h2>
                <p className="mt-1 text-xs opacity-50">
                  {language === 'ar' ? 'اختر تنسيق ملف التصدير' : 'Choose the file format to export'}
                </p>
              </div>
              <button
                onClick={() => setIsExportOptionsOpen(false)}
                className="rounded-full p-2 transition-colors hover:bg-primary/10"
                aria-label={language === 'ar' ? 'إغلاق' : 'Close'}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid gap-3 p-6 sm:grid-cols-2">
              <button
                onClick={() => {
                  setIsExportOptionsOpen(false);
                  handleExportCSV();
                }}
                className="flex flex-col items-start gap-3 rounded-2xl border border-primary/15 p-4 text-left transition-all hover:border-primary/40 hover:bg-primary/5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                  <FileText className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm font-bold">{language === 'ar' ? 'تصدير CSV' : 'Export CSV'}</span>
                  <span className="mt-0.5 block text-xs opacity-50">{language === 'ar' ? 'ملف قيم مفصولة بفواصل' : 'Comma-separated values file'}</span>
                </span>
              </button>
              <button
                onClick={() => {
                  setIsExportOptionsOpen(false);
                  handleExportExcel();
                }}
                className="flex flex-col items-start gap-3 rounded-2xl border border-primary/15 p-4 text-left transition-all hover:border-primary/40 hover:bg-primary/5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                  <FileSpreadsheet className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm font-bold">{language === 'ar' ? 'تصدير إكسل' : 'Export Excel'}</span>
                  <span className="mt-0.5 block text-xs opacity-50">{language === 'ar' ? 'ملف مصنف Microsoft Excel' : 'Microsoft Excel workbook'}</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {sourceFilter === 'POS' && (
        <div className="space-y-6">
          {/* Sales History Tabs */}
          <div className="flex items-center gap-8 border-b border-primary/10">
            <button
              onClick={() => setHistoryTab('all')}
              className={cn(
                "pb-4 text-sm font-bold transition-all relative",
                historyTab === 'all' ? "text-primary" : "text-primary/40 hover:text-primary"
              )}
            >
              {language === 'ar' ? 'الكل' : 'All'}
              {historyTab === 'all' && <div className="absolute bottom-0 left-0 w-full h-1 bg-primary rounded-t-full" />}
            </button>
            <button
              onClick={() => setHistoryTab('return')}
              className={cn(
                "pb-4 text-sm font-bold transition-all relative",
                historyTab === 'return' ? "text-primary" : "text-primary/40 hover:text-primary"
              )}
            >
              {language === 'ar' ? 'بدء طلب إرجاع' : 'Process return'}
              {historyTab === 'return' && <div className="absolute bottom-0 left-0 w-full h-1 bg-primary rounded-t-full" />}
            </button>
            <button
              onClick={() => setHistoryTab('continue')}
              className={cn(
                "pb-4 text-sm font-bold transition-all relative",
                historyTab === 'continue' ? "text-primary" : "text-primary/40 hover:text-primary"
              )}
            >
              {language === 'ar' ? 'متابعة العملية' : 'Continue sale'}
              {historyTab === 'continue' && <div className="absolute bottom-0 left-0 w-full h-1 bg-primary rounded-t-full" />}
            </button>
          </div>

          <p className="text-sm opacity-60">
            View, edit and manage your sales all in one place. <a href="#" className="underline">Need help?</a>
          </p>

          {/* Filter Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-card p-6 rounded-2xl border border-border">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-bold opacity-60">{language === 'ar' ? 'التاريخ' : 'Date'}</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="w-full bg-white/50 border border-primary/10 px-3 py-2 rounded-lg text-xs font-bold outline-none focus:ring-2 ring-primary/20"
                  />
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="w-full bg-white/50 border border-primary/10 px-3 py-2 rounded-lg text-xs font-bold outline-none focus:ring-2 ring-primary/20"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="showTimeRange"
                    checked={showTimeRange}
                    onChange={(e) => setShowTimeRange(e.target.checked)}
                    className="w-3 h-3 accent-primary"
                  />
                  <label htmlFor="showTimeRange" className="text-xs font-bold opacity-60 cursor-pointer">Time range</label>
                </div>
                {showTimeRange && (
                  <div className="flex gap-2 animate-in fade-in slide-in-from-top-1 duration-200">
                    <input
                      type="time"
                      value={timeRange.start}
                      onChange={(e) => setTimeRange(prev => ({ ...prev, start: e.target.value }))}
                      className="w-full bg-white/50 border border-primary/10 px-3 py-2 rounded-lg text-xs font-bold outline-none focus:ring-2 ring-primary/20"
                    />
                    <input
                      type="time"
                      value={timeRange.end}
                      onChange={(e) => setTimeRange(prev => ({ ...prev, end: e.target.value }))}
                      className="w-full bg-white/50 border border-primary/10 px-3 py-2 rounded-lg text-xs font-bold outline-none focus:ring-2 ring-primary/20"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-bold opacity-60">Customer</label>
                <input
                  type="text"
                  placeholder="Enter a customer"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="w-full bg-white/50 border border-primary/10 px-3 py-2 rounded-lg text-xs font-bold outline-none focus:ring-2 ring-primary/20"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold opacity-60">Receipt or note</label>
                <input
                  type="text"
                  placeholder="Search receipt or note"
                  value={receiptNote}
                  onChange={(e) => setReceiptNote(e.target.value)}
                  className="w-full bg-white/50 border border-primary/10 px-3 py-2 rounded-lg text-xs font-bold outline-none focus:ring-2 ring-primary/20"
                />
              </div>
            </div>
            <div className="flex flex-col justify-end gap-4">
              <div className="flex items-center justify-end gap-4">
                <button
                  onClick={() => {
                    setDateRange({ start: '', end: '' });
                    setTimeRange({ start: '', end: '' });
                    setShowTimeRange(false);
                    setCustomerSearch('');
                    setReceiptNote('');
                  }}
                  className="text-xs font-bold underline opacity-60 hover:opacity-100"
                >
                  Clear filters
                </button>
                <button className="text-xs font-bold underline opacity-60 hover:opacity-100">
                  More filters
                </button>
              </div>
              <button className="w-full bg-primary text-white px-8 py-3 rounded-xl text-xs font-bold hover:bg-primary/80 transition-colors">
                Search
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      {sourceFilter !== 'POS' && (
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between bg-card border border-border p-4 rounded-2xl shadow-sm text-foreground">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0 w-full custom-scrollbar">
            {['all', 'draft', 'awaiting approval', 'awaiting payment', 'paid', 'void', 'repeating'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  "px-3.5 py-2 rounded-full text-xs font-bold capitalize transition-all whitespace-nowrap flex items-center gap-1.5 shrink-0",
                  filter === f ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-primary/5 opacity-60 text-primary"
                )}
              >
                <span>
                  {f === 'all' ? (language === 'ar' ? 'الكل' : 'all') :
                   f === 'draft' ? (language === 'ar' ? 'مسودة' : 'draft') :
                   f === 'awaiting approval' ? (language === 'ar' ? 'بانتظار الاعتماد' : 'awaiting approval') :
                   f === 'awaiting payment' ? (language === 'ar' ? 'بانتظار الدفع' : 'awaiting payment') :
                   f === 'paid' ? (language === 'ar' ? 'مدفوعة' : 'paid') :
                   f === 'void' ? (language === 'ar' ? 'ملغاة' : 'void') :
                   f === 'repeating' ? (language === 'ar' ? 'متكررة' : 'repeating') : f}
                </span>
                {getFilterCount(f) !== null && (
                  <span className={cn(
                    "px-1.5 py-0.5 rounded-full text-[10px]",
                    filter === f ? "bg-primary-foreground text-primary" : "bg-primary/10 text-primary"
                  )}>
                    {getFilterCount(f)}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 w-full lg:w-auto shrink-0">
            <div className="flex items-center gap-2 bg-white border border-[#141414]/10 px-3.5 py-2 rounded-full flex-1 md:w-64 shadow-sm min-w-0">
              <Search className="w-4 h-4 opacity-30 text-primary shrink-0" />
              <input
                type="text"
                placeholder={language === 'ar' ? 'البحث في الفواتير...' : 'Search invoices...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-none outline-none text-xs sm:text-sm w-full font-medium min-w-0"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                "p-2.5 rounded-full border transition-all shadow-sm shrink-0",
                showFilters || refFilter || customerFilter || tagFilter || statusFilter !== 'all' || deliveryStatusFilter !== 'all' || paymentStatusFilter !== 'all' || minTotalAmount || maxTotalAmount || dateRange.start || dateRange.end
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-white text-primary border-primary/20 hover:border-primary"
              )}
              title="Advanced Filters"
            >
              <Filter className="w-4 h-4" />
            </button>

            <div className="relative shrink-0">
              <button
                onClick={() => setShowColumnGear(!showColumnGear)}
                className="p-2.5 hover:bg-primary/5 rounded-full transition-all text-primary border border-primary/20 shrink-0"
                title="Column selection"
              >
                <Settings className="w-4 h-4 opacity-60 hover:opacity-100" />
              </button>

              {showColumnGear && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-[#141414] rounded-2xl p-4 shadow-xl z-50">
                    <h3 className="text-sm font-bold mb-3">Table Settings</h3>
                    <div className="space-y-1 max-h-80 overflow-y-auto pr-1 custom-scrollbar">
                      <p className="text-[10px] uppercase font-bold opacity-40 mb-2">Visible & Order</p>
                      {columnOrder.map((colId, index) => (
                        <div
                          key={colId}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('columnIndex', index.toString());
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const fromIndex = parseInt(e.dataTransfer.getData('columnIndex'));
                            const toIndex = index;
                            if (fromIndex === toIndex) return;

                            const newOrder = [...columnOrder];
                            const [movedItem] = newOrder.splice(fromIndex, 1);
                            newOrder.splice(toIndex, 0, movedItem);
                            setColumnOrder(newOrder);
                          }}
                          className="flex items-center gap-2 hover:bg-primary/5 p-2 rounded-xl cursor-move group transition-colors border border-transparent hover:border-primary/10"
                        >
                          <div className="opacity-20 group-hover:opacity-100 transition-opacity">
                            <MoreHorizontal className="w-4 h-4 rotate-90" />
                          </div>
                          <input
                            type="checkbox"
                            checked={columnVisibility[colId]}
                            onChange={(e) => {
                              e.stopPropagation();
                              setColumnVisibility(prev => ({ ...prev, [colId]: !prev[colId] }));
                            }}
                            className="rounded border-border accent-primary w-3 h-3 cursor-pointer"
                          />
                          <span className="text-[10px] font-bold text-primary capitalize flex-1 truncate">
                            {(() => {
                               if (colId === 'number') return 'Number';
                               if (colId === 'contact') return 'Customer';
                               if (colId === 'paid') return 'Paid';
                               if (colId === 'due') return 'Due';
                               if (colId.startsWith('tag')) return `Tag ${colId.slice(3)}`;
                               return String(colId || '').replace(/([A-Z])/g, ' $1').trim();
                            })()}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-[#141414]/10">
                      <p className="text-[8px] text-center uppercase font-bold opacity-30 tracking-widest">Drag to reorder columns</p>
                    </div>
                  </div>
                )}
              </div>
          </div>
        </div>
      )}

      {showFilters && sourceFilter !== 'POS' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 bg-white/95 border border-primary p-6 rounded-2xl animate-in slide-in-from-top-2 mb-4 mt-[-10px] shadow-xl backdrop-blur-sm">
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">{language === 'ar' ? 'المرجع' : 'Reference'}</label>
            <input
              type="text"
              placeholder="INV-001..."
              value={refFilter}
              onChange={e => setRefFilter(e.target.value)}
              className="w-full bg-white border border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-2 ring-primary/20 transition-all"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">{language === 'ar' ? 'اسم العميل' : 'Customer Name'}</label>
            <input
              type="text"
              placeholder={language === 'ar' ? 'البحث باسم العميل...' : 'Customer...'}
              value={customerFilter}
              onChange={e => setCustomerFilter(e.target.value)}
              className="w-full bg-white border border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-2 ring-primary/20 transition-all"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">{language === 'ar' ? 'التاريخ من' : 'Date From'}</label>
            <input
              type="date"
              className="w-full bg-white border border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-2 ring-primary/20 transition-all"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">{language === 'ar' ? 'التاريخ إلى' : 'Date To'}</label>
            <input
              type="date"
              className="w-full bg-white border border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-2 ring-primary/20 transition-all"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            />
          </div>
          <SearchableSelect
            label={language === 'ar' ? 'الحالة' : 'Status'}
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'Draft', label: 'Draft' },
              { value: 'Awaiting Approval', label: 'Awaiting Approval' },
              { value: 'Awaiting Payment', label: 'Awaiting Payment' },
              { value: 'Paid', label: 'Paid' },
              { value: 'Void', label: 'Void' }
            ]}
          />
          <SearchableSelect
            label={language === 'ar' ? 'حالة الدفع' : 'Payment Status'}
            value={paymentStatusFilter}
            onChange={e => setPaymentStatusFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Payment Status' },
              { value: 'Unpaid', label: 'Unpaid' },
              { value: 'Partially Paid', label: 'Partially Paid' },
              { value: 'Paid', label: 'Paid' }
            ]}
          />
          <SearchableSelect
            label={language === 'ar' ? 'حالة التوصيل' : 'Delivery Status'}
            value={deliveryStatusFilter}
            onChange={e => setDeliveryStatusFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Delivery Status' },
              { value: 'Not Processed', label: 'Not Processed' },
              { value: 'Draft', label: 'Draft' },
              { value: 'Assigned', label: 'Assigned' },
              { value: 'Shipped', label: 'Shipped' },
              { value: 'Delivered', label: 'Delivered' }
            ]}
          />
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">{language === 'ar' ? 'المبلغ الإجمالي' : 'Total Amount'}</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={minTotalAmount} onChange={e => setMinTotalAmount(e.target.value)} placeholder="Min" className="w-full bg-white border border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-2 ring-primary/20" />
              <input type="number" value={maxTotalAmount} onChange={e => setMaxTotalAmount(e.target.value)} placeholder="Max" className="w-full bg-white border border-primary/10 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-2 ring-primary/20" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">{language === 'ar' ? 'الوسوم' : 'Tags'}</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <SearchableSelect
                  value={tagFilter}
                  onChange={e => setTagFilter(e.target.value)}
                  options={[
                    { value: '', label: language === 'ar' ? 'جميع الوسوم' : 'All Tags' },
                    ...availableTags.map(tag => ({ value: tag.name, label: tag.name }))
                  ]}
                  placeholder={language === 'ar' ? 'البحث عن وسم...' : 'Search for a tag...'}
                />
              </div>
              <button
                onClick={() => {
                  setRefFilter('');
                  setCustomerFilter('');
                  setTagFilter('');
                  setStatusFilter('all');
                  setDeliveryStatusFilter('all');
                  setPaymentStatusFilter('all');
                  setMinTotalAmount('');
                  setMaxTotalAmount('');
                  setDateRange({ start: '', end: '' });
                }}
                className="px-4 py-2.5 bg-red-50 text-red-500 rounded-xl text-[10px] font-bold uppercase tracking-wider hover:bg-red-100 transition-colors shadow-sm"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedInvoices.length > 0 && (
        <div className="flex items-center gap-4 bg-primary text-primary-foreground p-4 rounded-2xl mb-4 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center gap-2 pr-4 border-r border-white/10">
            <span className="text-xs font-bold italic serif">{selectedInvoices.length} {language === 'ar' ? 'محددة' : 'selected'}</span>
            <button
              onClick={() => setSelectedInvoices([])}
              className="text-[10px] uppercase font-bold opacity-50 hover:opacity-100"
            >
              {language === 'ar' ? 'إلغاء' : 'Clear'}
            </button>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-2 md:pb-0">
            {filter === 'draft' && (
              <button
                onClick={() => handleBulkAction('submit')}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
              >
                <Send className="w-3 h-3" />
                <span>{language === 'ar' ? 'إرسال للاعتماد' : 'Submit for Approval'}</span>
              </button>
            )}
            {(filter === 'draft' || filter === 'awaiting approval') && (
              <button
                onClick={() => handleBulkAction('approve')}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
              >
                <CheckCircle2 className="w-3 h-3" />
                <span>{language === 'ar' ? 'اعتماد' : 'Approve'}</span>
              </button>
            )}
            {(filter === 'draft' || filter === 'awaiting approval' || filter === 'awaiting payment') && (
              <button
                onClick={() => handleBulkAction('delete')}
                disabled={filter === 'awaiting payment' && selectedInvoices.some(id => (invoices.find(i => i.id === id)?.paymentReceived || 0) > 0)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap",
                  filter === 'awaiting payment' && selectedInvoices.some(id => (invoices.find(i => i.id === id)?.paymentReceived || 0) > 0)
                    ? "opacity-30 cursor-not-allowed bg-primary/20 text-primary/70"
                    : "hover:bg-red-500/20 text-red-400"
                )}
              >
                <Trash2 className="w-3 h-3" />
                <span>{filter === 'awaiting payment' ? (language === 'ar' ? 'إلغاء (Void)' : 'Void') : (language === 'ar' ? 'حذف' : 'Delete')}</span>
              </button>
            )}
            {filter === 'awaiting payment' && selectedInvoices.length > 1 && (
              <button
                onClick={() => {
                  const depositInvoices = invoices.filter(i => selectedInvoices.includes(i.id) && i.status === 'Awaiting Payment' && i.type === 'invoice');
                  setBatchDepositInvoices(depositInvoices);

                  // Make those selected invoices default to their remaining amount
                  const initAllocations: Record<string, number> = {};
                  depositInvoices.forEach(inv => {
                    initAllocations[inv.id] = inv.totalAmount - (inv.paymentReceived || 0);
                  });
                  setSelectedInvoicesForBatch(initAllocations);

                  setIsBatchDepositModalOpen(true);
                }}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap text-green-400"
              >
                <Plus className="w-3 h-3" />
                <span>{language === 'ar' ? 'إيداع' : 'Deposit'}</span>
              </button>
            )}
            <button
              onClick={() => handleBulkAction('print')}
              className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
            >
              <Printer className="w-3 h-3" />
              <span>{language === 'ar' ? 'طباعة' : 'Print'}</span>
            </button>
            <button
              onClick={() => handleBulkAction('email')}
              className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
            >
              <Mail className="w-3 h-3" />
              <span>{language === 'ar' ? 'بريد إلكتروني' : 'Email'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Invoices List */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-xl text-foreground">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse min-w-[720px]">
            <thead>
              <tr className="border-b border-primary/20 bg-primary/5">
                <th className="p-4 w-10">
                  <input
                    type="checkbox"
                    className="rounded border-border accent-primary"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedInvoices(filteredInvoices.map(i => i.id));
                      } else {
                        setSelectedInvoices([]);
                      }
                    }}
                    checked={selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0}
                  />
                </th>
                {columnOrder.filter(id => columnVisibility[id]).map(colId => {
                   let label = '';
                   switch(colId) {
                     case 'number': label = language === 'ar' ? 'رقم الفاتورة' : 'Invoice Number'; break;
                     case 'reference': label = language === 'ar' ? 'المرجع' : 'Reference'; break;
                     case 'contact': label = language === 'ar' ? 'العميل' : 'Customer'; break;
                     case 'date': label = language === 'ar' ? 'التاريخ' : 'Date'; break;
                     case 'dueDate': label = language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date'; break;
                     case 'paid': label = language === 'ar' ? 'المدفوع' : 'Paid'; break;
                     case 'due': label = language === 'ar' ? 'المستحق' : 'Due'; break;
                     case 'total': label = language === 'ar' ? 'الإجمالي' : 'Total'; break;
                     case 'status': label = language === 'ar' ? 'الحالة' : 'Status'; break;
                     case 'tag1': label = language === 'ar' ? 'وسم 1' : 'Tag 1'; break;
                     case 'tag2': label = language === 'ar' ? 'وسم 2' : 'Tag 2'; break;
                     case 'tag3': label = language === 'ar' ? 'وسم 3' : 'Tag 3'; break;
                     case 'tag4': label = language === 'ar' ? 'وسم 4' : 'Tag 4'; break;
                     case 'tag5': label = language === 'ar' ? 'وسم 5' : 'Tag 5'; break;
                   }
                   return (
                     <th
                       key={colId}
                       className={cn(
                        "p-4 text-[10px] font-bold uppercase tracking-wider opacity-50 italic serif text-foreground",
                        ['paid', 'due', 'total'].includes(colId) && "text-right"
                       )}
                     >
                       {label}
                     </th>
                   );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={sourceFilter ? 9 : 10} className="p-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto opacity-20" />
                  </td>
                </tr>
              ) : filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan={sourceFilter ? 9 : 10} className="p-12 text-center opacity-50 italic serif">
                    {language === 'ar' ? 'لم يتم العثور على فواتير تطابق معاييرك.' : 'No invoices found matching your criteria.'}
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice) => {
                  const contact = contacts.find(c => c.id === invoice.contactId);
                  const amountDue = invoice.totalAmount - (invoice.paymentReceived || 0);
                  return (
                    <tr
                      key={invoice.id}
                      onClick={() => handleSelectInvoice(invoice)}
                      className="border-b border-primary/20 hover:bg-primary/5 transition-all group cursor-pointer"
                    >
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="rounded border-primary"
                          checked={selectedInvoices.includes(invoice.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedInvoices(prev => [...prev, invoice.id]);
                            } else {
                              setSelectedInvoices(prev => prev.filter(id => id !== invoice.id));
                            }
                          }}
                        />
                      </td>
                      {columnOrder.filter(id => columnVisibility[id]).map(colId => {
                         switch(colId) {
                           case 'number':
                             return (
                               <td key={colId} className="p-4">
                                 <div className="flex items-center gap-2">
                                  <span className="font-mono text-[11px] font-bold">
                                    {invoice.type === 'credit_note' && !invoice.number.startsWith('CN') ? `CN-${invoice.number}` : invoice.number}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowThread(invoice.id);
                                    }}
                                    className="p-1 flex items-center hover:bg-black/10 rounded-full transition-all relative text-primary/40 hover:text-primary"
                                    title="Open Thread"
                                  >
                                    <MessageSquare className="w-3 h-3" />
                                    {invoice.threads && invoice.threads.length > 0 && (
                                      <span className="text-[10px] font-bold text-red-500 ml-1">
                                        {invoice.threads.length}
                                      </span>
                                    )}
                                  </button>
                                 </div>
                               </td>
                             );
                           case 'reference':
                             return <td key={colId} className="p-4 text-[11px] opacity-70 italic serif">{invoice.reference || ''}</td>;
                           case 'contact':
                             return (
                               <td key={colId} className="p-4" onClick={(e) => e.stopPropagation()}>
                                 <button
                                   onClick={() => {
                                     if (onNavigate && (contact?.id || invoice.contactId)) {
                                       onNavigate('contacts', contact?.id || invoice.contactId);
                                     }
                                   }}
                                   className="text-[11px] font-bold text-left hover:underline hover:text-primary transition-colors"
                                 >
                                   {(() => {
                                     const customerName = contact?.name || invoice.contactName || 'Walk-in Customer';
                                     // Coerce any value (e.g. a stray object from spread metadata)
                                     // to a string to avoid rendering a non-primitive child.
                                     return typeof customerName === 'string' ? customerName : 'Walk-in Customer';
                                   })()}
                                 </button>
                               </td>
                             );
                           case 'date':
                             return <td key={colId} className="p-4 text-[11px] opacity-70">{new Date(invoice.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>;
                           case 'dueDate':
                             return (
                               <td key={colId} className={cn(
                                 "p-4 text-[11px] font-bold",
                                 isOverdue(invoice.dueDate, invoice.status) ? "text-red-600" : "opacity-70 font-normal"
                               )}>
                                 {new Date(invoice.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                               </td>
                             );
                           case 'paid':
                             return (
                               <td key={colId} className="p-4 font-mono font-bold text-right text-[11px] text-emerald-600">
                                 {(invoice.paymentReceived || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                               </td>
                             );
                           case 'due':
                             return (
                               <td key={colId} className="p-4 font-mono font-bold text-right text-[11px] text-orange-600">
                                 {amountDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                               </td>
                             );
                           case 'total':
                             return (
                               <td key={colId} className="p-4 font-mono font-bold text-right text-[11px]">
                                 {(invoice.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                               </td>
                             );
                           case 'tag1':
                           case 'tag2':
                           case 'tag3':
                           case 'tag4':
                           case 'tag5': {
                             const tagIndex = parseInt(String(colId || '').replace('tag', '')) - 1;
                             const tagName = invoice.tags?.[tagIndex];
                             if (!tagName) return <td key={colId} className="p-4"></td>;

                             const tagDetails = availableTags.find((t: any) => t.name === tagName);
                             return (
                               <td key={colId} className="p-4">
                                 <span
                                   className="inline-flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-full text-[9px] font-bold border whitespace-nowrap"
                                   style={{
                                     backgroundColor: tagDetails ? `${tagDetails.color}15` : '#f3f4f6',
                                     color: tagDetails ? tagDetails.color : '#374151',
                                     borderColor: tagDetails ? `${tagDetails.color}30` : '#e5e7eb'
                                   }}
                                 >
                                   <div className="p-0.5 rounded-full bg-white/50">
                                     <Tag className="w-2 h-2" />
                                   </div>
                                   {tagName}
                                 </span>
                               </td>
                             );
                           }
                           case 'status': {
                             const statusDetails = getInvoiceStatusDetails(invoice);
                             return (
                               <td key={colId} className="p-4 text-right">
                                 <span className={cn(
                                   "inline-flex items-center gap-1 px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider shadow-sm border border-black/5",
                                   statusDetails.color
                                 )}>
                                   {statusDetails.icon}
                                   {statusDetails.label}
                                 </span>
                               </td>
                             );
                           }
                           default: return null;
                         }
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Discussion Thread Modal */}
      {showThread && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-primary/40 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowThread(null);
          }}
        >
          <div className="bg-white border border-primary w-full max-w-4xl rounded-[40px] p-8 shadow-2xl relative flex flex-col max-h-[calc(100dvh-2rem)] overflow-hidden">
            <button
              onClick={() => {
                setShowThread(null);
              }}
              className="absolute top-6 right-6 p-2 hover:bg-primary hover:text-primary-foreground rounded-full transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="mb-4">
              <h3 className="text-2xl font-bold italic serif text-primary">
                {language === 'ar' ? 'خيوط النقاش' : 'Discussion Threads'}
              </h3>
              <p className="text-sm opacity-60 text-primary">
                {language === 'ar' ? 'الفاتورة' : 'Invoice'}: {invoices.find(i => i.id === showThread)?.number}
              </p>
            </div>

            <div className="flex-1 overflow-hidden">
              <DiscussionsView
                orgId={orgId}
                documentId={showThread}
                collectionName="documents"
                language={language}
              />
            </div>
          </div>
        </div>
      )}
      {/* Invoice Modal (Simplified for brevity, similar to Quotes Modal) */}
      {isModalOpen && !isRepeatingModalOpen && !isCreditNoteModalOpen && (
        <div className="app-overlay-inset ui-editor-screen fixed inset-y-0 end-0 bg-background z-[110] flex flex-col overflow-hidden">
          <div className="flex flex-col h-full">
            <div className="ui-editor-header p-6 border-b border-primary/20 flex items-center justify-between bg-primary text-primary-foreground dark:bg-[#000000] dark:text-white">
              <h2 className="text-2xl font-bold italic serif tracking-tight">{isEditing ? 'Edit Invoice' : 'New Sales Invoice'}</h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setIsEditing(false);
                }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-primary-foreground dark:text-white"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleAddInvoice} className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              {isInvoiceEditReadOnly && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
                  This confirmed invoice is read-only. Copy it or use the available workflow actions to make changes.
                </div>
              )}
              <fieldset disabled={isInvoiceEditReadOnly} className="space-y-8 disabled:opacity-75">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold uppercase tracking-widest opacity-50">Customer</label>
                    <button
                      type="button"
                      onClick={() => setIsContactModalOpen(true)}
                      className="text-[10px] font-bold text-primary hover:underline"
                    >
                      Add New
                    </button>
                  </div>
                  <SearchableSelect
                    value={newInvoice.contactId}
                    onChange={(e) => handleContactChange(e.target.value)}
                    options={contacts.map(c => ({
                      value: c.id,
                      label: c.name,
                      subLabel: c.email
                    }))}
                    placeholder="Select Customer"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'التاريخ' : 'Date'}</label>
                  <input
                    type="date"
                    value={newInvoice.date}
                    onChange={(e) => setNewInvoice(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full bg-white border border-primary/20 px-4 py-3 rounded-xl outline-none text-sm font-bold"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date'}</label>
                  <input
                    type="date"
                    value={newInvoice.dueDate}
                    onChange={(e) => setNewInvoice(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="w-full bg-white border border-primary/20 px-4 py-3 rounded-xl outline-none text-sm font-bold"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'رقم الفاتورة' : 'Invoice Number'}</label>
                  <input
                    type="text"
                    value={newInvoice.number}
                    onChange={(e) => setNewInvoice(prev => ({ ...prev, number: e.target.value }))}
                    className="w-full bg-white border border-primary/20 px-4 py-3 rounded-xl outline-none text-sm font-bold font-mono"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'المرجع' : 'Reference'}</label>
                  <input
                    type="text"
                    value={newInvoice.reference}
                    onChange={(e) => setNewInvoice(prev => ({ ...prev, reference: e.target.value }))}
                    className="w-full bg-white border border-primary/20 px-4 py-3 rounded-xl outline-none text-sm font-bold"
                    placeholder="e.g. PO-123"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'النسق' : 'Branding Theme'}</label>
                  <SearchableSelect
                    value={newInvoice.brandingTheme}
                    onChange={(e) => setNewInvoice(prev => ({ ...prev, brandingTheme: e.target.value }))}
                    options={[
                      { value: 'Standard', label: language === 'ar' ? 'أساسي' : 'Standard' },
                      { value: 'Professional', label: 'Professional' },
                      { value: 'Minimal', label: 'Minimal' },
                      ...brandingThemes.map(theme => ({ value: theme.id, label: theme.name }))
                    ]}
                    placeholder="Select Theme"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'العملة' : 'Currency'}</label>
                  <SearchableSelect
                    value={newInvoice.currency}
                    onChange={(e) => setNewInvoice(prev => ({ ...prev, currency: e.target.value }))}
                    options={currencies.map(curr => ({ value: curr, label: curr }))}
                    placeholder="Select Currency"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">Amounts are</label>
                  <SearchableSelect
                    value={newInvoice.amountsAre}
                    onChange={(e) => setNewInvoice(prev => ({ ...prev, amountsAre: e.target.value as any }))}
                    options={[
                      { value: 'Tax Exclusive', label: 'Tax Exclusive' },
                      { value: 'Tax Inclusive', label: 'Tax Inclusive' },
                      { value: 'No Tax', label: 'No Tax' }
                    ]}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'الوسوم' : 'Tags'}</label>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {[1, 2, 3, 4, 5].map((idx) => (
                    <div key={idx} className="space-y-2">
                      <label className="block text-[10px] font-bold uppercase opacity-40">Tag {idx}</label>
                      <div className="relative group">
                        <SearchableSelect
                          value={newInvoice.tags?.[idx-1] || ''}
                          onChange={(e) => {
                            const newTags = [...(newInvoice.tags || [])];
                            while(newTags.length < idx) newTags.push('');
                            newTags[idx-1] = e.target.value;
                            setNewInvoice(prev => ({ ...prev, tags: newTags }));
                          }}
                          options={[
                            { value: '', label: 'None' },
                            ...availableTags.map(tag => ({ value: tag.name, label: tag.name }))
                          ]}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {availableTags.length === 0 && (
                  <p className="text-[10px] opacity-40 italic">No tags set up yet. Go to Accounting {'>'} Tags to create some.</p>
                )}
              </div>

              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-primary/20 bg-primary/5">
                        <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest opacity-40 w-32 text-primary border-r border-primary/10">SKU / Item</th>
                        <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest opacity-40 text-primary border-r border-primary/10">{language === 'ar' ? 'الوصف' : 'Description'}</th>
                        <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest opacity-40 w-20 text-center text-primary border-r border-primary/10">{language === 'ar' ? 'الكمية' : 'Qty'}</th>
                        <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest opacity-40 w-24 text-primary border-r border-primary/10">UOM</th>
                        <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest opacity-40 w-28 text-right text-primary border-r border-primary/10">Unit Price</th>
                        <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest opacity-40 w-20 text-center text-primary border-r border-primary/10">{language === 'ar' ? 'الخصم' : 'Disc'}</th>
                        <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest opacity-40 w-36 text-primary border-r border-primary/10">{language === 'ar' ? 'الحساب' : 'Account'}</th>
                        {trackingCategories.map(cat => (
                          <th key={cat.id} className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest opacity-40 w-32 text-primary border-r border-primary/10">{cat.name}</th>
                        ))}
                        <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest opacity-40 w-32 text-primary border-r border-primary/10">{language === 'ar' ? 'معدل الضريبة' : 'Tax Rate'}</th>
                        <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest opacity-40 w-28 text-right text-primary border-r border-primary/10">Tax</th>
                        <th className="py-4 px-2 text-[10px] font-bold uppercase tracking-widest opacity-40 w-32 text-right text-primary border-r border-primary/10">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                        <th className="py-4 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-primary/5">
                      {newInvoice.items.map((item, index) => {
                        const taxRateValue = getTaxRateValue(item.taxRate);
                        const subtotalBeforeDiscount = item.quantity * item.unitPrice;
                        const discountAmountLine = (item.discountType === 'amount')
                          ? (item.discount || 0)
                          : (subtotalBeforeDiscount * (item.discount || 0) / 100);
                        const subtotal = subtotalBeforeDiscount - discountAmountLine;
                        let taxAmount = 0;
                        let lineTotal = subtotal;

                        if (newInvoice.amountsAre === 'Tax Exclusive') {
                          taxAmount = subtotal * (taxRateValue / 100);
                          lineTotal = subtotal + taxAmount;
                        } else if (newInvoice.amountsAre === 'Tax Inclusive') {
                          taxAmount = subtotal - (subtotal / (1 + taxRateValue / 100));
                        } else {
                          taxAmount = 0;
                        }

                        return (
                          <tr key={index} className="group hover:bg-primary/5 transition-colors">
                            <td className="py-3 px-2 align-top border-r border-primary/10">
                              <SearchableSelect
                                className="bg-transparent border-0 p-0"
                                value={item.item}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const selectedItem = itemsList.find(i => i.id === val);
                                  const newItems = [...newInvoice.items];
                                  newItems[index].item = val;
                                  if (selectedItem) {
                                    newItems[index].description = selectedItem.description || selectedItem.name;
                                    newItems[index].unitPrice = selectedItem.unitPrice || 0;
                                    newItems[index].account = selectedItem.account || '';
                                    newItems[index].taxRate = selectedItem.taxRate || '0%';
                                    newItems[index].uom = getInvoiceLineUom({}, selectedItem);
                                  }
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                options={itemsList.map(i => ({
                                  value: i.id,
                                  label: i.name,
                                  subLabel: i.code ? `SKU: ${i.code}` : undefined
                                }))}
                                placeholder="Select Item"
                              />
                            </td>
                            <td className="py-3 px-2 align-top border-r border-primary/10">
                              <textarea
                                rows={1}
                                value={item.description}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].description = e.target.value;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                className="w-full bg-white border border-primary/20 px-2 py-2 rounded-lg text-xs outline-none focus:border-primary transition-all resize-none text-primary"
                                placeholder="Description..."
                              />
                            </td>
                            <td className="py-3 px-2 align-top border-r border-primary/10">
                              <input
                                type="number"
                                value={isNaN(item.quantity) ? '' : item.quantity}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].quantity = parseFloat(e.target.value) || 0;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                className="w-full bg-white border border-primary/20 px-2 py-2 rounded-lg text-xs text-center outline-none focus:border-primary transition-all font-mono text-primary"
                              />
                            </td>
                            <td className="py-3 px-2 align-top border-r border-primary/10">
                              {(() => {
                                const selectedItem = itemsList.find(candidate => candidate.id === item.item || candidate.code === item.item);
                                const defaultUom = getInvoiceLineUom(item, selectedItem);
                                const uomOptions = Array.from(new Set([
                                  defaultUom,
                                  selectedItem?.salesUomName,
                                  selectedItem?.stockUomName,
                                  selectedItem?.purchaseUomName,
                                ].filter(Boolean))) as string[];
                                return uomOptions.length > 1 ? (
                                  <select
                                    value={defaultUom}
                                    onChange={(event) => {
                                      const newItems = [...newInvoice.items];
                                      newItems[index].uom = event.target.value;
                                      setNewInvoice(prev => ({ ...prev, items: newItems }));
                                    }}
                                    className="w-full bg-white border border-primary/20 px-2 py-2 rounded-lg text-xs outline-none text-primary"
                                  >
                                    {uomOptions.map(uom => <option key={uom} value={uom}>{uom}</option>)}
                                  </select>
                                ) : <div className="px-2 py-2 text-xs font-semibold text-primary/70">{defaultUom || '—'}</div>;
                              })()}
                            </td>
                            <td className="py-3 px-2 align-top border-r border-primary/10">
                              <input
                                type="number"
                                value={isNaN(item.unitPrice) ? '' : item.unitPrice}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].unitPrice = parseFloat(e.target.value) || 0;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                className="w-full bg-white border border-primary/20 px-2 py-2 rounded-lg text-xs text-right outline-none focus:border-primary transition-all font-mono text-primary"
                              />
                            </td>
                            <td className="py-3 px-2 align-top border-r border-primary/10">
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  value={isNaN(item.discount || 0) ? '' : (item.discount || 0)}
                                  onChange={(e) => {
                                    const newItems = [...newInvoice.items];
                                    newItems[index].discount = parseFloat(e.target.value) || 0;
                                    setNewInvoice(prev => ({ ...prev, items: newItems }));
                                  }}
                                  className="flex-1 bg-white border border-primary/20 px-2 py-2 rounded-lg text-xs text-center outline-none focus:border-primary transition-all font-mono text-primary min-w-[50px]"
                                />
                                <SearchableSelect
                                  value={(item as any).discountType || 'percentage'}
                                  onChange={(e) => {
                                    const newItems = [...newInvoice.items];
                                    (newItems[index] as any).discountType = e.target.value;
                                    setNewInvoice(prev => ({ ...prev, items: newItems }));
                                  }}
                                  options={[
                                    { value: 'percentage', label: '%' },
                                    { value: 'amount', label: 'Amt' }
                                  ]}
                                  className="w-16"
                                />
                              </div>
                            </td>
                            <td className="py-3 px-2 align-top border-r border-primary/10">
                              <SearchableSelect
                                className="bg-transparent border-0 p-0"
                                value={item.account}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const newItems = [...newInvoice.items];
                                  newItems[index].account = val;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                options={accountsList.map(a => ({
                                  value: a.code,
                                  label: a.name,
                                  subLabel: a.code
                                }))}
                                placeholder="Select Account"
                              />
                            </td>
                            {trackingCategories.map(cat => (
                              <td key={cat.id} className="py-3 px-2 align-top border-r border-primary/10">
                                <SearchableSelect
                                  value={item.trackingCategory?.split('|').find(p => p.startsWith(`${cat.id}:`))?.split(':')[1] || ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const currentParts = item.trackingCategory ? item.trackingCategory.split('|') : [];
                                    const otherParts = currentParts.filter(p => !p.startsWith(`${cat.id}:`));
                                    const newParts = val ? [...otherParts, `${cat.id}:${val}`] : otherParts;
                                    const newItems = [...newInvoice.items];
                                    newItems[index].trackingCategory = newParts.join('|');
                                    setNewInvoice(prev => ({ ...prev, items: newItems }));
                                  }}
                                  options={[
                                    { value: '', label: 'None' },
                                    ...cat.options.map((opt: string) => ({ value: opt, label: opt }))
                                  ]}
                                  placeholder="Select"
                                />
                              </td>
                            ))}
                            <td className="py-3 px-2 align-top border-r border-primary/10">
                              <SearchableSelect
                                className="bg-transparent border-0 p-0"
                                value={item.taxRate}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const newItems = [...newInvoice.items];
                                  newItems[index].taxRate = val;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                options={[
                                  { value: '0%', label: 'Tax Exempt (0%)' },
                                  ...taxRates.filter(t => t.type !== 'purchases').map(tax => ({
                                    value: tax.name,
                                    label: tax.name,
                                    subLabel: `${tax.rate}%`
                                  }))
                                ]}
                                placeholder="Select Tax"
                              />
                            </td>
                            <td className="py-3 px-2 align-top text-right text-xs font-mono opacity-60 text-primary border-r border-primary/10">
                              <div className="py-2">{taxAmount.toFixed(2)}</div>
                            </td>
                            <td className="py-3 px-2 align-top text-right text-xs font-bold font-mono text-primary border-r border-primary/10">
                              <div className="py-2">{lineTotal.toFixed(2)}</div>
                            </td>
                            <td className="py-3 px-2 align-top text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  const newItems = newInvoice.items.filter((_, i) => i !== index);
                                  setNewInvoice(prev => ({ ...prev, items: newItems.length ? newItems : prev.items }));
                                }}
                                className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  onClick={() => setNewInvoice(prev => ({ ...prev, items: [...prev.items, { item: '', description: '', quantity: 1, unitPrice: 0, discount: 0, discountType: 'percentage', taxAmount: 0, total: 0, account: '', taxRate: '0%', trackingCategory: '' }] }))}
                  className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-60 hover:opacity-100 transition-opacity mt-4"
                >
                  <Plus className="w-4 h-4" />
                  Add another line
                </button>
              </div>

              <div className="flex flex-col items-end gap-2 pt-8 border-t border-primary/20 w-full max-w-sm ml-auto">
                <div className="flex justify-between w-full text-sm">
                  <span className="opacity-50 uppercase font-bold text-[10px] tracking-widest">{language === 'ar' ? 'المجموع الإجمالي' : 'Gross Total'}</span>
                  <span className="font-bold font-mono">
                    {formatCurrency(newInvoice.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0), newInvoice.currency || organizationCurrency)}
                  </span>
                </div>
                {newInvoice.items.reduce((sum, item) => {
                  const base = item.quantity * item.unitPrice;
                  const disc = (item as any).discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                  return sum + disc;
                }, 0) > 0 && (
                  <div className="flex justify-between w-full text-sm">
                    <span className="opacity-50 uppercase font-bold text-[10px] tracking-widest text-red-500">{language === 'ar' ? 'ناقص: الخصم' : 'Less: Discount'}</span>
                    <span className="font-bold font-mono text-red-500">
                      -{formatCurrency(newInvoice.items.reduce((sum, item) => {
                        const base = item.quantity * item.unitPrice;
                        const disc = (item as any).discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                        return sum + disc;
                      }, 0), newInvoice.currency || organizationCurrency)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between w-full text-sm pt-2 border-t border-primary/10">
                  <span className="opacity-50 uppercase font-bold text-[10px] tracking-widest">{language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                  <span className="font-bold font-mono">
                    {formatCurrency(newInvoice.items.reduce((sum, item) => {
                      const base = item.quantity * item.unitPrice;
                      const disc = (item as any).discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                      return sum + (base - disc);
                    }, 0), newInvoice.currency || organizationCurrency)}
                  </span>
                </div>
                <div className="flex justify-between w-full text-sm">
                  <span className="opacity-50 uppercase font-bold text-[10px] tracking-widest">{language === 'ar' ? 'إجمالي الضريبة' : 'Total Tax'}</span>
                  <span className="font-bold font-mono">
                    {formatCurrency(newInvoice.items.reduce((sum, item) => {
                      const taxRateValue = getTaxRateValue(item.taxRate);
                      const subLine = item.quantity * item.unitPrice;
                      const discAmountLine = (item as any).discountType === 'amount' ? (item.discount || 0) : (subLine * (item.discount || 0) / 100);
                      const subtotal = subLine - discAmountLine;
                      if (newInvoice.amountsAre === 'Tax Exclusive') return sum + (subtotal * (taxRateValue / 100));
                      if (newInvoice.amountsAre === 'Tax Inclusive') return sum + (subtotal - (subtotal / (1 + taxRateValue / 100)));
                      return sum;
                    }, 0), newInvoice.currency || organizationCurrency)}
                  </span>
                </div>
                <div className="flex justify-between w-full text-2xl pt-4 border-t-2 border-primary mt-2">
                  <span className="font-bold uppercase italic serif text-primary">{language === 'ar' ? 'المبلغ الإجمالي' : 'Total Amount'}</span>
                  <span className="font-bold font-mono text-primary">
                    {formatCurrency(newInvoice.items.reduce((sum, item) => {
                      const taxRateValue = getTaxRateValue(item.taxRate);
                      const subLine = item.quantity * item.unitPrice;
                      const discAmountLine = (item as any).discountType === 'amount' ? (item.discount || 0) : (subLine * (item.discount || 0) / 100);
                      const subtotal = subLine - discAmountLine;
                      if (newInvoice.amountsAre === 'Tax Exclusive') return sum + (subtotal * (1 + taxRateValue / 100));
                      return sum + subtotal;
                    }, 0), newInvoice.currency || organizationCurrency)}
                  </span>
                </div>
              </div>
              </fieldset>
              <div className="ui-editor-actions">
                <button
                  type="button"
                  data-editor-secondary="true"
                  onClick={() => setIsModalOpen(false)}
                  className="px-8 py-4 border border-primary/30 rounded-full font-bold hover:bg-primary/5 transition-colors"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                {!isInvoiceEditReadOnly && <button
                  type="submit"
                  disabled={isSaving}
                  className="px-12 py-4 bg-white border-2 border-primary text-primary rounded-full font-bold hover:bg-primary/5 transition-all flex items-center gap-2"
                >
                  {isSaving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    isEditing ? (language === 'ar' ? 'تحديث الفاتورة' : 'Update Invoice') : (language === 'ar' ? 'حفظ كمسودة' : 'Save as Draft')
                  )}
                </button>}
                {!isEditing && (
                  <button
                    type="button"
                    onClick={() => {
                      const form = document.querySelector('form');
                      if (form) {
                        const event = new Event('submit', { cancelable: true, bubbles: true });
                        // We need a way to pass the status override.
                        // Let's modify handleAddInvoice to check a ref or state.
                        (window as any)._invoiceStatusOverride = 'Awaiting Payment';
                        form.dispatchEvent(event);
                      }
                    }}
                    disabled={isSaving}
                    className="px-12 py-4 bg-primary text-primary-foreground rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                  >
                    {isSaving ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      language === 'ar' ? 'اعتماد' : 'Approve'
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Repeating Invoice Modal */}
      {isRepeatingModalOpen && (
        <div className="app-overlay-inset ui-editor-screen fixed inset-y-0 end-0 bg-background z-[110] flex flex-col overflow-hidden">
          <div className="flex flex-col h-full">
            <div className="ui-editor-header p-6 border-b border-primary flex items-center justify-between bg-primary text-primary-foreground dark:bg-[#000000] dark:text-white">
              <h2 className="text-2xl font-bold italic serif">{language === 'ar' ? 'فاتورة متكررة جديدة' : 'New Repeating Invoice'}</h2>
              <button
                onClick={() => setIsRepeatingModalOpen(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-primary-foreground dark:text-white"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleAddInvoice} className="ui-editor-form flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 bg-white/50 p-6 rounded-2xl border border-primary/10">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'كرر هذه الفاتورة كل' : 'Repeat this invoice every'}</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={repeatingConfig.frequency}
                      onChange={(e) => setRepeatingConfig(prev => ({ ...prev, frequency: parseInt(e.target.value) || 1 }))}
                      className="w-20 bg-white border border-primary px-3 py-2 rounded-xl outline-none text-sm font-bold"
                      min="1"
                    />
                    <SearchableSelect
                      value={repeatingConfig.frequencyUnit}
                      onChange={(e) => setRepeatingConfig(prev => ({ ...prev, frequencyUnit: e.target.value as any }))}
                      options={[
                        { value: 'Week', label: language === 'ar' ? 'أسبوع/أسابيع' : 'Week(s)' },
                        { value: 'Month', label: language === 'ar' ? 'شهر/شهور' : 'Month(s)' }
                      ]}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'تاريخ الفاتورة' : 'Invoice Date'}</label>
                  <input
                    type="date"
                    value={repeatingConfig.startDate}
                    onChange={(e) => setRepeatingConfig(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full bg-white border border-primary px-4 py-3 rounded-xl outline-none text-sm font-bold"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date'}</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{language === 'ar' ? 'استحقاق' : 'Due'}</span>
                    <input
                      type="number"
                      value={repeatingConfig.dueDateDays}
                      onChange={(e) => setRepeatingConfig(prev => ({ ...prev, dueDateDays: parseInt(e.target.value) || 0 }))}
                      className="w-16 bg-white border border-primary px-2 py-2 rounded-xl outline-none text-sm font-bold text-center"
                    />
                    <SearchableSelect
                      value={repeatingConfig.dueDateType}
                      onChange={(e) => setRepeatingConfig(prev => ({ ...prev, dueDateType: e.target.value as any }))}
                      options={[
                        { value: 'daysAfterInvoiceDate', label: language === 'ar' ? 'يوم/أيام بعد تاريخ الفاتورة' : 'day(s) after invoice date' },
                        { value: 'dayOfFollowingMonth', label: language === 'ar' ? 'يوم من الشهر التالي' : 'day of following month' },
                        { value: 'dayOfCurrentMonth', label: language === 'ar' ? 'يوم من الشهر الحالي' : 'day of current month' },
                        { value: 'daysAfterEndOfInvoiceMonth', label: language === 'ar' ? 'يوم/أيام بعد نهاية شهر الفاتورة' : 'day(s) after end of invoice month' }
                      ]}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'تاريخ الانتهاء (اختياري)' : 'End Date (Optional)'}</label>
                  <input
                    type="date"
                    value={repeatingConfig.endDate}
                    onChange={(e) => setRepeatingConfig(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full bg-white border border-primary px-4 py-3 rounded-xl outline-none text-sm font-bold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold uppercase tracking-widest opacity-50">Customer</label>
                    <button
                      type="button"
                      onClick={() => setIsContactModalOpen(true)}
                      className="text-[10px] font-bold text-primary hover:underline"
                    >
                      Add New
                    </button>
                  </div>
                  <SearchableSelect
                    value={newInvoice.contactId}
                    onChange={(e) => handleContactChange(e.target.value)}
                    options={contacts.map(c => ({ value: c.id, label: c.name, subLabel: c.email }))}
                    placeholder="Select Customer"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'المرجع' : 'Reference'}</label>
                  <input
                    type="text"
                    value={newInvoice.reference}
                    onChange={(e) => setNewInvoice(prev => ({ ...prev, reference: e.target.value }))}
                    className="w-full bg-white border border-primary px-4 py-3 rounded-xl outline-none text-sm font-bold"
                    placeholder="e.g. Monthly Service"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'العملة' : 'Currency'}</label>
                  <SearchableSelect
                    value={newInvoice.currency}
                    onChange={(e) => setNewInvoice(prev => ({ ...prev, currency: e.target.value }))}
                    options={currencies.map(curr => ({ value: curr, label: curr }))}
                    placeholder="Select Currency"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'حالة الفواتير الناتجة' : 'Status for generated invoices'}</label>
                  <SearchableSelect
                    value={repeatingConfig.approveStatus}
                    onChange={(e) => setRepeatingConfig(prev => ({ ...prev, approveStatus: e.target.value as any }))}
                    options={[
                      { value: 'Draft', label: language === 'ar' ? 'حفظ كمسودة' : 'Save as Draft' },
                      { value: 'Approved', label: language === 'ar' ? 'اعتماد' : 'Approve' },
                      { value: 'ApprovedForSending', label: language === 'ar' ? 'اعتماد للإرسال' : 'Approve for Sending' }
                    ]}
                  />
                </div>
              </div>

              {/* Items Table (Reused) */}
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-primary/20">
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-32 px-2">Item</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 px-2">{language === 'ar' ? 'الوصف' : 'Description'}</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-20 text-center px-2">{language === 'ar' ? 'الكمية' : 'Qty'}</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-28 text-right px-2">Unit Price</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-20 text-center px-2">{language === 'ar' ? 'الخصم' : 'Disc'}</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-36 px-2">{language === 'ar' ? 'الحساب' : 'Account'}</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-32 px-2">{language === 'ar' ? 'معدل الضريبة' : 'Tax Rate'}</th>
                        {trackingCategories.map(cat => (
                          <th key={cat.id} className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-32 px-2">{cat.name}</th>
                        ))}
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-32 text-right px-2">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                        <th className="pb-4 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-primary/5">
                      {newInvoice.items.map((item, index) => {
                        const taxRateValue = getTaxRateValue(item.taxRate);
                        const subtotalBeforeDiscount = item.quantity * item.unitPrice;
                        const discountAmountLine = (item.discountType === 'amount')
                          ? (item.discount || 0)
                          : (subtotalBeforeDiscount * (item.discount || 0) / 100);
                        const subtotal = subtotalBeforeDiscount - discountAmountLine;
                        const lineTotal = subtotal + (subtotal * (taxRateValue / 100));

                        return (
                          <tr key={index} className="group hover:bg-primary/5 transition-colors">
                            <td className="py-3 px-2 align-top">
                              <SearchableSelect
                                value={item.item}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const selectedItem = itemsList.find(i => i.id === val);
                                  const newItems = [...newInvoice.items];
                                  newItems[index].item = val;
                                  if (selectedItem) {
                                    newItems[index].description = selectedItem.description || selectedItem.name;
                                    newItems[index].unitPrice = selectedItem.unitPrice || 0;
                                    newItems[index].account = selectedItem.account || '';
                                    newItems[index].taxRate = selectedItem.taxRate || '0%';
                                    newItems[index].uom = getInvoiceLineUom({}, selectedItem);
                                  }
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                options={itemsList.map(i => ({ value: i.id, label: `${i.code} - ${i.name}` }))}
                                placeholder="Select"
                              />
                            </td>
                            <td className="py-3 px-2 align-top">
                              <textarea
                                rows={1}
                                value={item.description}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].description = e.target.value;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                className="w-full bg-white border border-primary/10 px-2 py-2 rounded-lg text-xs outline-none resize-none"
                                placeholder="Description..."
                              />
                            </td>
                            <td className="py-3 px-2 align-top">
                              <input
                                type="number"
                                value={isNaN(item.quantity) ? '' : item.quantity}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].quantity = parseFloat(e.target.value) || 0;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                className="w-full bg-white border border-primary/10 px-2 py-2 rounded-lg text-xs text-center outline-none font-mono"
                              />
                            </td>
                            <td className="py-3 px-2 align-top">
                              <input
                                type="number"
                                value={isNaN(item.unitPrice) ? '' : item.unitPrice}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].unitPrice = parseFloat(e.target.value) || 0;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                className="w-full bg-white border border-primary/10 px-2 py-2 rounded-lg text-xs text-right outline-none font-mono"
                              />
                            </td>
                             <td className="py-3 px-2 align-top">
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  value={isNaN(item.discount || 0) ? '' : (item.discount || 0)}
                                  onChange={(e) => {
                                    const newItems = [...newInvoice.items];
                                    newItems[index].discount = parseFloat(e.target.value) || 0;
                                    setNewInvoice(prev => ({ ...prev, items: newItems }));
                                  }}
                                  className="flex-1 bg-white border border-primary/10 px-2 py-2 rounded-lg text-xs text-center outline-none font-mono min-w-[50px] text-primary focus:border-primary"
                                />
                                <SearchableSelect
                                  value={(item as any).discountType || 'percentage'}
                                  onChange={(e) => {
                                    const newItems = [...newInvoice.items];
                                    (newItems[index] as any).discountType = e.target.value;
                                    setNewInvoice(prev => ({ ...prev, items: newItems }));
                                  }}
                                  options={[
                                    { value: 'percentage', label: '%' },
                                    { value: 'amount', label: 'Amt' }
                                  ]}
                                  className="w-16"
                                />
                              </div>
                            </td>
                            <td className="py-3 px-2 align-top">
                              <SearchableSelect
                                value={item.account}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].account = e.target.value;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                options={accountsList.map(a => ({ value: a.code, label: `${a.code} - ${a.name}` }))}
                                placeholder="Account*"
                              />
                            </td>
                            <td className="py-3 px-2 align-top">
                              <SearchableSelect
                                value={item.taxRate}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].taxRate = e.target.value;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                options={[
                                  { value: '0%', label: 'Tax Exempt (0%)' },
                                  ...taxRates.filter(t => t.type !== 'purchases').map(tax => ({ value: tax.name, label: `${tax.name} (${tax.rate}%)` }))
                                ]}
                              />
                            </td>
                            {trackingCategories.map(cat => (
                              <td key={cat.id} className="py-3 px-2 align-top">
                                <SearchableSelect
                                  value={item.trackingCategory?.split('|').find(p => p.startsWith(`${cat.id}:`))?.split(':')[1] || ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const newItems = [...newInvoice.items];
                                    const currentParts = newItems[index].trackingCategory ? newItems[index].trackingCategory.split('|') : [];
                                    const otherParts = currentParts.filter(p => !p.startsWith(`${cat.id}:`));
                                    const newParts = val ? [...otherParts, `${cat.id}:${val}`] : otherParts;
                                    newItems[index].trackingCategory = newParts.join('|');
                                    setNewInvoice(prev => ({ ...prev, items: newItems }));
                                  }}
                                  options={[
                                    { value: '', label: 'None' },
                                    ...cat.options.map((opt: string) => ({ value: opt, label: opt }))
                                  ]}
                                />
                              </td>
                            ))}
                            <td className="py-3 px-2 align-top text-right text-xs font-bold font-mono">
                              <div className="py-2">{lineTotal.toFixed(2)}</div>
                            </td>
                            <td className="py-3 px-2 align-top text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  const newItems = newInvoice.items.filter((_, i) => i !== index);
                                  setNewInvoice(prev => ({ ...prev, items: newItems.length ? newItems : prev.items }));
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  onClick={() => setNewInvoice(prev => ({ ...prev, items: [...prev.items, { item: '', description: '', quantity: 1, unitPrice: 0, discount: 0, discountType: 'percentage', taxAmount: 0, total: 0, account: '', taxRate: '0%', trackingCategory: '' }] }))}
                  className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-60 hover:opacity-100 transition-opacity mt-4"
                >
                  <Plus className="w-4 h-4" />
                  Add another line
                </button>
              </div>

              <div className="flex flex-col items-end gap-2 pt-8 border-t border-primary w-full max-w-sm ml-auto">
                <div className="flex justify-between w-full text-sm">
                  <span className="opacity-50 uppercase font-bold text-[10px] tracking-widest">Gross Total</span>
                  <span className="font-bold font-mono">
                    {formatCurrency(newInvoice.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0), newInvoice.currency || organizationCurrency)}
                  </span>
                </div>
                {newInvoice.items.reduce((sum, item) => {
                  const base = item.quantity * item.unitPrice;
                  const disc = (item as any).discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                  return sum + disc;
                }, 0) > 0 && (
                  <div className="flex justify-between w-full text-sm">
                    <span className="opacity-50 uppercase font-bold text-[10px] tracking-widest text-red-500">Less: Discount</span>
                    <span className="font-bold font-mono text-red-500">
                      -{formatCurrency(newInvoice.items.reduce((sum, item) => {
                        const base = item.quantity * item.unitPrice;
                        const disc = (item as any).discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                        return sum + disc;
                      }, 0), newInvoice.currency || organizationCurrency)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between w-full text-sm pt-2 border-t border-primary/10">
                  <span className="opacity-50 uppercase font-bold text-[10px] tracking-widest">{language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                  <span className="font-bold font-mono">
                    {formatCurrency(newInvoice.items.reduce((sum, item) => {
                      const base = item.quantity * item.unitPrice;
                      const disc = (item as any).discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                      return sum + (base - disc);
                    }, 0), newInvoice.currency || organizationCurrency)}
                  </span>
                </div>
                <div className="flex justify-between w-full text-sm">
                  <span className="opacity-50 uppercase font-bold text-[10px] tracking-widest">{language === 'ar' ? 'إجمالي الضريبة' : 'Total Tax'}</span>
                  <span className="font-bold font-mono">
                    {formatCurrency(newInvoice.items.reduce((sum, item) => {
                      const taxRateValue = getTaxRateValue(item.taxRate);
                      const subLine = item.quantity * item.unitPrice;
                      const discAmountLine = (item as any).discountType === 'amount' ? (item.discount || 0) : (subLine * (item.discount || 0) / 100);
                      const subtotal = subLine - discAmountLine;
                      return sum + (subtotal * (taxRateValue / 100));
                    }, 0), newInvoice.currency || organizationCurrency)}
                  </span>
                </div>
                <div className="flex justify-between w-full text-2xl pt-4 border-t-2 border-primary mt-2">
                  <span className="font-bold uppercase italic serif text-[#0073AA]">{language === 'ar' ? 'المبلغ الإجمالي' : 'Total Amount'}</span>
                  <span className="font-bold font-mono text-[#0073AA]">
                    {formatCurrency((newInvoice.items.reduce((sum, item) => {
                      const taxRateValue = getTaxRateValue(item.taxRate);
                      const subLine = item.quantity * item.unitPrice;
                      const discAmountLine = (item as any).discountType === 'amount' ? (item.discount || 0) : (subLine * (item.discount || 0) / 100);
                      const subtotal = subLine - discAmountLine;
                      return sum + (subtotal * (1 + taxRateValue / 100));
                    }, 0)), newInvoice.currency || organizationCurrency)}
                  </span>
                </div>
              </div>
              <div className="ui-editor-actions">
                <button
                  type="button"
                  data-editor-secondary="true"
                  onClick={() => setIsRepeatingModalOpen(false)}
                  className="px-8 py-4 border border-primary rounded-full font-bold hover:bg-primary/5 transition-colors"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-12 py-4 bg-primary text-primary-foreground rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  {isSaving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Save Repeating Invoice'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credit Note Modal */}
      {isCreditNoteModalOpen && (
        <div className={`app-overlay-inset ui-editor-screen fixed inset-y-0 end-0 z-[110] flex flex-col overflow-hidden ${isApplyCreditMode ? 'bg-yellow-50' : 'bg-background'}`}>
          <div className="flex flex-col h-full">
            <div className={`ui-editor-header p-6 border-b flex items-center justify-between ${isApplyCreditMode ? 'border-yellow-500 bg-yellow-400 text-yellow-950' : 'border-primary bg-primary text-primary-foreground dark:bg-[#000000] dark:text-white'}`}>
              <h2 className="text-2xl font-bold italic serif">
                {isApplyCreditMode ? `Apply Credit to Invoice ${newInvoice.reference}` : (newInvoice.reference?.toLowerCase().includes('refund') ? 'New Refund' : 'New Credit Note')}
              </h2>
              <button
                onClick={() => { setIsCreditNoteModalOpen(false); setIsApplyCreditMode(false); setCreditApplicationAmount(0); setCreditApplicationMax(0); setApplyCreditTargetId(null); }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-primary-foreground dark:text-white"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleAddInvoice} className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              {isApplyCreditMode && (
                <div className="rounded-2xl border-2 border-yellow-400 bg-yellow-100 p-5 text-yellow-950">
                  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div>
                      <p className="text-xs font-black uppercase tracking-widest">Credit note linked to invoice {newInvoice.reference}</p>
                      <p className="mt-1 text-sm">The copied customer, date, items, prices, taxes, and discounts are preserved. Choose how much to deduct.</p>
                    </div>
                    <div className="w-full md:w-64 space-y-1">
                      <label className="text-xs font-bold uppercase tracking-widest">Amount to deduct</label>
                      <input type="number" min="0.01" max={creditApplicationMax} step="0.01" value={creditApplicationAmount} onChange={(e) => { const value = Math.min(creditApplicationMax, Math.max(0, Number(e.target.value) || 0)); setCreditApplicationAmount(value); setNewInvoice(prev => ({ ...prev, creditApplicationAmount: value })); }} className="w-full rounded-xl border border-yellow-600 bg-white px-4 py-3 text-right font-mono font-bold outline-none" required />
                      <p className="text-[11px] font-semibold">Maximum outstanding balance: {formatCurrency(creditApplicationMax, newInvoice.currency || organizationCurrency)}</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold uppercase tracking-widest opacity-50">Customer</label>
                    <button
                      type="button"
                      onClick={() => setIsContactModalOpen(true)}
                      className="text-[10px] font-bold text-primary hover:underline"
                    >
                      Add New
                    </button>
                  </div>
                  <SearchableSelect
                    value={newInvoice.contactId}
                    onChange={(e) => handleContactChange(e.target.value)}
                    options={contacts.map(c => ({ value: c.id, label: c.name, subLabel: c.email }))}
                    placeholder="Select Customer"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'التاريخ' : 'Date'}</label>
                  <input
                    type="date"
                    value={newInvoice.date}
                    onChange={(e) => setNewInvoice(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full bg-white border border-primary px-4 py-3 rounded-xl outline-none text-sm font-bold text-primary"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">Credit Note #</label>
                  <input
                    type="text"
                    value={newInvoice.number}
                    onChange={(e) => setNewInvoice(prev => ({ ...prev, number: e.target.value }))}
                    className="w-full bg-white border border-primary px-4 py-3 rounded-xl outline-none text-sm font-bold font-mono"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'المرجع' : 'Reference'}</label>
                  <input
                    type="text"
                    value={newInvoice.reference}
                    onChange={(e) => setNewInvoice(prev => ({ ...prev, reference: e.target.value }))}
                    className="w-full bg-white border border-primary px-4 py-3 rounded-xl outline-none text-sm font-bold"
                  />
                </div>
              </div>

              {/* Items Table (Reused) */}
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-primary/20">
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-32 px-2">Item</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 px-2">{language === 'ar' ? 'الوصف' : 'Description'}</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-20 text-center px-2">{language === 'ar' ? 'الكمية' : 'Qty'}</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-28 text-right px-2">Unit Price</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-20 text-center px-2">{language === 'ar' ? 'الخصم' : 'Disc'}</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-36 px-2">{language === 'ar' ? 'الحساب' : 'Account'}</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-32 px-2">{language === 'ar' ? 'معدل الضريبة' : 'Tax Rate'}</th>
                        <th className="pb-4 text-[10px] font-bold uppercase tracking-widest opacity-40 w-32 text-right px-2">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                        <th className="pb-4 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-primary/5">
                      {newInvoice.items.map((item, index) => {
                        const taxRateValue = getTaxRateValue(item.taxRate);
                        const subtotalBeforeDiscount = item.quantity * item.unitPrice;
                        const discountAmountLine = (item.discountType === 'amount')
                          ? (item.discount || 0)
                          : (subtotalBeforeDiscount * (item.discount || 0) / 100);
                        const subtotal = subtotalBeforeDiscount - discountAmountLine;
                        let taxAmount = 0;
                        let lineTotal = subtotal;

                        if (newInvoice.amountsAre === 'Tax Exclusive') {
                          taxAmount = subtotal * (taxRateValue / 100);
                          lineTotal = subtotal + taxAmount;
                        } else if (newInvoice.amountsAre === 'Tax Inclusive') {
                          taxAmount = subtotal - (subtotal / (1 + taxRateValue / 100));
                        }

                        return (
                          <tr key={index} className="group hover:bg-primary/5 transition-colors">
                            <td className="py-3 px-2 align-top">
                              <SearchableSelect
                                value={item.item}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const selectedItem = itemsList.find(i => i.id === val);
                                  const newItems = [...newInvoice.items];
                                  newItems[index].item = val;
                                  if (selectedItem) {
                                    newItems[index].description = selectedItem.description || selectedItem.name;
                                    newItems[index].unitPrice = selectedItem.unitPrice || 0;
                                    newItems[index].account = selectedItem.account || '';
                                    newItems[index].taxRate = selectedItem.taxRate || '0%';
                                    newItems[index].uom = getInvoiceLineUom({}, selectedItem);
                                  }
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                options={itemsList.map(i => ({ value: i.id, label: `${i.code} - ${i.name}` }))}
                                placeholder="Select"
                              />
                            </td>
                            <td className="py-3 px-2 align-top">
                              <textarea
                                rows={1}
                                value={item.description}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].description = e.target.value;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                className="w-full bg-white border border-primary/10 px-2 py-2 rounded-lg text-xs outline-none resize-none"
                                placeholder="Description..."
                              />
                            </td>
                            <td className="py-3 px-2 align-top">
                              <input
                                type="number"
                                value={isNaN(item.quantity) ? '' : item.quantity}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].quantity = parseFloat(e.target.value) || 0;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                className="w-full bg-white border border-primary/10 px-2 py-2 rounded-lg text-xs text-center outline-none font-mono"
                              />
                            </td>
                            <td className="py-3 px-2 align-top">
                              <input
                                type="number"
                                value={isNaN(item.unitPrice) ? '' : item.unitPrice}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].unitPrice = parseFloat(e.target.value) || 0;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                className="w-full bg-white border border-primary/10 px-2 py-2 rounded-lg text-xs text-right outline-none font-mono"
                              />
                            </td>
                            <td className="py-3 px-2 align-top">
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  value={isNaN(item.discount || 0) ? '' : (item.discount || 0)}
                                  onChange={(e) => {
                                    const newItems = [...newInvoice.items];
                                    newItems[index].discount = parseFloat(e.target.value) || 0;
                                    setNewInvoice(prev => ({ ...prev, items: newItems }));
                                  }}
                                  className="flex-1 bg-white border border-primary/10 px-2 py-2 rounded-lg text-xs text-center outline-none font-mono min-w-[50px] text-primary focus:border-primary"
                                />
                                <SearchableSelect
                                  value={(item as any).discountType || 'percentage'}
                                  onChange={(e) => {
                                    const newItems = [...newInvoice.items];
                                    (newItems[index] as any).discountType = e.target.value;
                                    setNewInvoice(prev => ({ ...prev, items: newItems }));
                                  }}
                                  options={[
                                    { value: 'percentage', label: '%' },
                                    { value: 'amount', label: 'Amt' }
                                  ]}
                                  className="w-16"
                                />
                              </div>
                            </td>
                            <td className="py-3 px-2 align-top">
                              <SearchableSelect
                                value={item.account}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].account = e.target.value;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                options={accountsList.map(a => ({ value: a.code, label: `${a.code} - ${a.name}` }))}
                                placeholder="Account*"
                              />
                            </td>
                            <td className="py-3 px-2 align-top">
                              <SearchableSelect
                                value={item.taxRate}
                                onChange={(e) => {
                                  const newItems = [...newInvoice.items];
                                  newItems[index].taxRate = e.target.value;
                                  setNewInvoice(prev => ({ ...prev, items: newItems }));
                                }}
                                options={[
                                  { value: '0%', label: 'Tax Exempt (0%)' },
                                  ...taxRates.filter(t => t.type !== 'purchases').map(tax => ({ value: tax.name, label: `${tax.name} (${tax.rate}%)` }))
                                ]}
                              />
                            </td>
                            <td className="py-3 px-2 align-top text-right text-xs font-bold font-mono">
                              <div className="py-2">{lineTotal.toFixed(2)}</div>
                            </td>
                            <td className="py-3 px-2 align-top text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  const newItems = newInvoice.items.filter((_, i) => i !== index);
                                  setNewInvoice(prev => ({ ...prev, items: newItems.length ? newItems : prev.items }));
                                }}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  onClick={() => setNewInvoice(prev => ({ ...prev, items: [...prev.items, { item: '', description: '', quantity: 1, unitPrice: 0, discount: 0, discountType: 'percentage', taxAmount: 0, total: 0, account: '', taxRate: '0%', trackingCategory: '' }] }))}
                  className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-60 hover:opacity-100 transition-opacity mt-4"
                >
                  <Plus className="w-4 h-4" />
                  Add another line
                </button>
              </div>

              <div className="flex flex-col items-end gap-2 pt-8 border-t border-primary w-full max-w-sm ml-auto">
                <div className="flex justify-between w-full text-sm">
                  <span className="opacity-50 uppercase font-bold text-[10px] tracking-widest">Gross Total</span>
                  <span className="font-bold font-mono">
                    {formatCurrency(newInvoice.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0), newInvoice.currency || organizationCurrency)}
                  </span>
                </div>
                {newInvoice.items.reduce((sum, item) => {
                  const base = item.quantity * item.unitPrice;
                  const disc = (item as any).discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                  return sum + disc;
                }, 0) > 0 && (
                  <div className="flex justify-between w-full text-sm">
                    <span className="opacity-50 uppercase font-bold text-[10px] tracking-widest text-red-500">Less: Discount</span>
                    <span className="font-bold font-mono text-red-500">
                      -{formatCurrency(newInvoice.items.reduce((sum, item) => {
                        const base = item.quantity * item.unitPrice;
                        const disc = (item as any).discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                        return sum + disc;
                      }, 0), newInvoice.currency || organizationCurrency)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between w-full text-sm pt-2 border-t border-primary/10">
                  <span className="opacity-50 uppercase font-bold text-[10px] tracking-widest">{language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                  <span className="font-bold font-mono">
                    {formatCurrency(newInvoice.items.reduce((sum, item) => {
                      const base = item.quantity * item.unitPrice;
                      const disc = (item as any).discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                      return sum + (base - disc);
                    }, 0), newInvoice.currency || organizationCurrency)}
                  </span>
                </div>
                <div className="flex justify-between w-full text-sm">
                  <span className="opacity-50 uppercase font-bold text-[10px] tracking-widest">{language === 'ar' ? 'إجمالي الضريبة' : 'Total Tax'}</span>
                  <span className="font-bold font-mono">
                    {formatCurrency(newInvoice.items.reduce((sum, item) => {
                      const taxRateValue = getTaxRateValue(item.taxRate);
                      const subLine = item.quantity * item.unitPrice;
                      const discAmountLine = (item as any).discountType === 'amount' ? (item.discount || 0) : (subLine * (item.discount || 0) / 100);
                      const subtotal = subLine - discAmountLine;
                      return sum + (subtotal * (taxRateValue / 100));
                    }, 0), newInvoice.currency || organizationCurrency)}
                  </span>
                </div>
                <div className="flex justify-between w-full text-2xl pt-4 border-t-2 border-primary mt-2">
                  <span className="font-bold uppercase italic serif text-[#0073AA]">{language === 'ar' ? 'إجمالي الرصيد' : 'Total Credit'}</span>
                  <span className="font-bold font-mono text-[#0073AA]">
                    {formatCurrency((newInvoice.items.reduce((sum, item) => {
                      const taxRateValue = getTaxRateValue(item.taxRate);
                      const subLine = item.quantity * item.unitPrice;
                      const discAmountLine = (item as any).discountType === 'amount' ? (item.discount || 0) : (subLine * (item.discount || 0) / 100);
                      const subtotal = subLine - discAmountLine;
                      return sum + (subtotal * (1 + taxRateValue / 100));
                    }, 0)), newInvoice.currency || organizationCurrency)}
                  </span>
                </div>
              </div>
              <div className="ui-editor-actions">
                <button
                  type="button"
                  data-editor-secondary="true"
                  onClick={() => { setIsCreditNoteModalOpen(false); setIsApplyCreditMode(false); setCreditApplicationAmount(0); setCreditApplicationMax(0); setApplyCreditTargetId(null); }}
                  className="px-8 py-4 border border-primary rounded-full font-bold hover:bg-primary/5 transition-colors"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    const form = e.currentTarget.closest('form');
                    if (form) {
                      (window as any)._invoiceStatusOverride = 'Draft';
                      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                    }
                  }}
                  disabled={isSaving}
                  className="px-8 py-4 border border-primary rounded-full font-bold hover:bg-primary/5 transition-colors"
                >
                  Save as Draft
                </button>
                <button
                  type="submit"
                  onClick={(e) => {
                    const form = e.currentTarget.closest('form');
                    if (form) {
                      (window as any)._invoiceStatusOverride = 'Awaiting Payment';
                    }
                  }}
                  disabled={isSaving}
                  className="px-12 py-4 bg-primary text-primary-foreground rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  {isSaving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Approve'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Invoice Detail View (Simplified) */}
      {selectedInvoice && (
        <div className="app-overlay-inset fixed inset-y-0 end-0 z-[100] flex flex-col overflow-hidden bg-background safe-top print-only print:inset-0 print:p-0">
          {/* Top Header / Breadcrumbs */}
          <div className="border-b border-primary/10 px-8 py-4 print:hidden bg-card">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-[11px] text-[#0073AA] font-bold">
                  <button onClick={handleCloseInvoice} className="hover:underline tracking-widest uppercase flex-shrink-0">Sales</button>
                  <ChevronDown className="w-3 h-3 -rotate-90 text-primary/40 flex-shrink-0" />
                  <button onClick={handleCloseInvoice} className="hover:underline tracking-widest uppercase flex-shrink-0">Invoices</button>
                  <ChevronDown className="w-3 h-3 -rotate-90 text-primary/40 flex-shrink-0" />
                  {selectedInvoiceSourceQuoteId && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedInvoice(null);
                          onNavigate?.('quotes', String(selectedInvoiceSourceQuoteId));
                        }}
                        className="hover:underline tracking-widest uppercase flex-shrink-0"
                      >
                        Source Order {selectedInvoiceSourceQuoteNumber || selectedInvoiceSourceQuoteId}
                      </button>
                      <ChevronDown className="w-3 h-3 -rotate-90 text-primary/40 flex-shrink-0" />
                    </>
                  )}
                  <span className="tracking-widest uppercase text-primary/60 truncate">{selectedInvoice.number}</span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-2xl font-bold text-primary tracking-tight italic serif whitespace-nowrap">Invoice {selectedInvoice.number}</h2>
                  <StatusBadge status={selectedInvoiceStatus} language={language === 'ar' ? 'ar' : 'en'} />
                  {selectedInvoiceStatus === 'done' && (
                    <StatusBadge status={selectedInvoicePaymentStatus} language={language === 'ar' ? 'ar' : 'en'} />
                  )}
                  {(() => {
                    const linkedQuoteId = selectedInvoice.sourceQuoteId || selectedInvoice.quoteId;
                    const linkedQuote = linkedQuoteId ? allQuotes.find(q => q.id === linkedQuoteId) : null;
                    // Coerce to a string so a malformed quote payload can never surface a
                    // non-primitive (e.g. an Event-like {target} object) as a React child, which
                    // would throw "Objects are not valid as a React child" (React error #31).
                    const linkedQuoteNumber = selectedInvoice.sourceQuoteNumber || (typeof linkedQuote?.number === 'string'
                      ? linkedQuote.number
                      : typeof linkedQuote?.number === 'number'
                        ? String(linkedQuote.number)
                        : '');
                    if (!linkedQuoteId) return null;
                    return (
                      <div className="flex items-center gap-1 ml-2">
                        <LinkIcon className="w-3 h-3 text-primary/40" />
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedInvoice(null);
                            if (onNavigate) onNavigate('quotes', String(linkedQuoteId));
                          }}
                          className="text-xs font-bold text-[#0073AA] hover:underline flex items-center gap-1"
                          title="Open the quote this invoice was created from"
                        >
                          Source Order
                          {linkedQuoteNumber ? (
                            <span className="bg-[#0073AA]/10 px-1.5 py-0.5 rounded-full text-[9px]">
                              {linkedQuoteNumber}
                            </span>
                          ) : null}
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })()}
                  {deliveryNotes.filter(dn => dn.invoiceId === selectedInvoice.id ).length > 0 && (
                    <div className="flex items-center gap-1 ml-2 relative">
                       <Truck className="w-3 h-3 text-primary/40" />
                       <div className="relative group">
                        <button
                          onClick={() => setShowLinkedDeliveries(!showLinkedDeliveries)}
                          className="text-xs font-bold text-[#0073AA] hover:underline flex items-center gap-1"
                        >
                          View Linked Delivery Note{deliveryNotes.filter(dn => dn.invoiceId === selectedInvoice.id ).length > 1 ? 's' : ''}
                          <span className="bg-[#0073AA]/10 px-1.5 py-0.5 rounded-full text-[9px]">
                            {deliveryNotes.filter(dn => dn.invoiceId === selectedInvoice.id ).length}
                          </span>
                        </button>

                        {showLinkedDeliveries && (
                          <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-primary/10 rounded-2xl shadow-2xl z-[210] overflow-hidden animate-in fade-in zoom-in slide-in-from-top-2 duration-200">
                            <div className="p-3 bg-primary/5 border-b border-primary/5">
                              <p className="text-[10px] font-black text-primary/40 uppercase tracking-widest">Linked Delivery Notes</p>
                            </div>
                            <div className="max-h-64 overflow-y-auto custom-scrollbar">
                              {deliveryNotes.filter(dn => dn.invoiceId === selectedInvoice.id ).map(dn => (
                                <button
                                  key={dn.id}
                                  onClick={() => {
                                    setShowLinkedDeliveries(false);
                                    setSelectedInvoice(null);
                                    if (onNavigate) onNavigate('deliveries', dn.id);
                                  }}
                                  className="w-full text-left p-4 hover:bg-primary/5 border-b border-primary/5 last:border-0 flex items-center justify-between group/item"
                                >
                                  <div>
                                    <p className="text-xs font-bold text-primary">#{(dn as any).number || dn.id.slice(-8).toUpperCase()}</p>
                                    <p className="text-[9px] text-primary/40 font-medium">{dn.status}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-[10px] text-primary/40 font-bold uppercase tracking-widest">{(dn.items || []).length} Items</p>
                                    <ArrowRight className="w-3 h-3 text-[#0073AA] opacity-0 group-hover/item:opacity-100 transition-all inline ml-1" />
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                       </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3 w-full md:w-auto">
                {selectedInvoiceIsDraft && !selectedInvoiceIsAwaitingApproval && (
                  <button
                    onClick={async () => {
                      try {
                        const actionEntry = {
                          action: 'Approval Requested',
                          user: auth.currentUser?.displayName || 'Unknown User',
                          email: auth.currentUser?.email || 'No Email',
                          timestamp: new Date().toISOString(),
                          details: 'Invoice sent for approval'
                        };
                        if (hasLocalPostgresSession()) {
                          const response = await fetch(`/api/organizations/${orgId}/documents/${selectedInvoice.id}`, {
                            method: 'PATCH',
                            headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
                            body: JSON.stringify({ status: 'Awaiting Approval', actionLog: [...(selectedInvoice.actionLog || []), actionEntry] }),
                          });
                          if (!response.ok) throw new Error(await response.text());
                          const updatedInvoice = await response.json() as Invoice;
                          setInvoices(current => current.map(invoice => invoice.id === updatedInvoice.id ? updatedInvoice : invoice));
                          setSelectedInvoice(updatedInvoice);
                        } else {
                          await updateDoc(doc(db, 'documents', selectedInvoice.id), {
                            status: 'Awaiting Approval',
                            updatedAt: serverTimestamp(),
                            actionLog: arrayUnion(actionEntry)
                          });
                          setSelectedInvoice(prev => prev ? {
                            ...prev,
                            status: 'Awaiting Approval',
                            actionLog: prev.actionLog ? [...prev.actionLog, actionEntry] : [actionEntry]
                          } : null);
                        }
                        notify.confirm('Invoice sent for approval');
                      } catch (error) {
                        if (hasLocalPostgresSession()) {
                          console.error('Invoice approval request failed:', error);
                          notify.error('Failed to ask for approval');
                        } else {
                          handleFirestoreError(error, OperationType.UPDATE, 'documents');
                        }
                      }
                    }}
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg font-bold text-[10px] hover:opacity-90 transition-all uppercase tracking-widest active:scale-95 shadow-sm whitespace-nowrap"
                  >
                    Ask for Approval
                  </button>
                )}
                {selectedInvoiceIsDraft && (
                  <button
                    onClick={async () => {
                      try {
                        const actionEntry = {
                          action: 'Approved',
                          user: auth.currentUser?.displayName || 'Unknown User',
                          email: auth.currentUser?.email || 'No Email',
                          timestamp: new Date().toISOString(),
                          details: 'Invoice approved and moved to Awaiting Payment'
                        };
                        let finalNumber = selectedInvoice.number;
                        if (hasLocalPostgresSession()) {
                          const approvalResponse = await fetch(`/api/organizations/${orgId}/documents/${selectedInvoice.id}/approve`, {
                            method: 'POST',
                            headers: { ...localApiHeaders(), 'Content-Type': 'application/json' },
                            body: JSON.stringify({ actionEntry }),
                          });
                          if (!approvalResponse.ok) throw new Error(await approvalResponse.text());
                          const approvedInvoice = await approvalResponse.json() as Invoice;
                          finalNumber = approvedInvoice.number;
                          setInvoices(prev => prev.map(invoice => invoice.id === approvedInvoice.id ? approvedInvoice : invoice));
                          setOrgSettings(prev => prev ? {
                            ...prev,
                            nextInvoiceNumber: selectedInvoice.type === 'credit_note'
                              ? prev.nextInvoiceNumber
                              : (prev.nextInvoiceNumber || 1) + 1,
                          } : prev);
                        } else {
                          if (orgSettings && selectedInvoice.type !== 'credit_note' && (selectedInvoice.number.includes('DRAFT') || selectedInvoiceIsDraft)) {
                            finalNumber = `${orgSettings.invoicePrefix || 'INV-'}${orgSettings.nextInvoiceNumber || 1}`;
                            await updateDoc(doc(db, 'settings', orgId), {
                              nextInvoiceNumber: increment(1)
                            });
                            setOrgSettings(prev => ({
                              ...prev,
                              nextInvoiceNumber: (prev.nextInvoiceNumber || 1) + 1
                            }));
                          }
                          await updateDoc(doc(db, 'documents', selectedInvoice.id), {
                            status: 'Awaiting Payment',
                            number: finalNumber,
                            updatedAt: serverTimestamp(),
                            actionLog: arrayUnion(actionEntry)
                          });
                        }

                        // Post to journals
                        if (hasLocalPostgresSession() && selectedInvoice.type === 'invoice') {
                          const journalResponse = await fetch(`/api/organizations/${orgId}/documents/${selectedInvoice.id}/post-invoice-journal`, {
                            method: 'POST',
                            headers: localApiHeaders(),
                          });
                          if (!journalResponse.ok) throw new Error(await journalResponse.text());
                        } else if (selectedInvoice.type === 'credit_note') {
                          await postCreditNoteJournal(orgId, { ...selectedInvoice, status: 'Awaiting Payment', number: finalNumber }, auth.currentUser?.uid || 'system');

                          // If it's a credit note with a linked invoice, apply it
                          if (selectedInvoice.linkedInvoiceId) {
                            const targetInvoice = invoices.find(inv => inv.id === selectedInvoice.linkedInvoiceId);
                            if (targetInvoice) {
                              const paymentDataForInvoice = {
                                orgId,
                                accountId: 'credit_note_applied',
                                accountName: 'Applied Credit',
                                type: 'Payment Received',
                                date: selectedInvoice.date,
                                description: `Payment from Credit Note ${finalNumber}`,
                                amount: selectedInvoice.totalAmount,
                                reference: finalNumber,
                                sourceId: selectedInvoice.id,
                                invoiceId: targetInvoice.id,
                                createdAt: serverTimestamp(),
                                createdBy: auth.currentUser?.uid,
                                updatedAt: serverTimestamp()
                              };
                              await addDoc(collection(db, 'transactions'), paymentDataForInvoice);

                              const newTotalPaid = (targetInvoice.paymentReceived || 0) + selectedInvoice.totalAmount;
                              const newStatus = newTotalPaid >= targetInvoice.totalAmount ? 'Paid' : 'Awaiting Payment';

                              await updateDoc(doc(db, 'documents', targetInvoice.id), {
                                paymentReceived: newTotalPaid,
                                status: newStatus,
                                updatedAt: serverTimestamp(),
                                actionLog: arrayUnion({
                                  action: 'Credit Applied',
                                  user: auth.currentUser?.displayName || 'Unknown User',
                                  email: auth.currentUser?.email || 'No Email',
                                  timestamp: new Date().toISOString(),
                                  details: `Credit of ${(selectedInvoice?.totalAmount || 0).toLocaleString()} applied from Credit Note ${finalNumber}`
                                })
                              });
                            }
                          }
                        } else {
                          await postInvoiceJournal(orgId, { ...selectedInvoice, status: 'Awaiting Payment', number: finalNumber }, auth.currentUser?.uid || 'system');
                        }

                        setSelectedInvoice(prev => prev ? {
                          ...prev,
                          status: 'Awaiting Payment',
                          number: finalNumber,
                          actionLog: prev.actionLog ? [...prev.actionLog, actionEntry] : [actionEntry]
                        } : null);
                        notify.confirm(`Invoice ${finalNumber} approved`);
                      } catch (error) {
                        if (hasLocalPostgresSession()) {
                          console.error('Invoice approval failed:', error);
                          notify.error('Failed to approve invoice');
                          return;
                        }
                        handleFirestoreError(error, OperationType.UPDATE, 'documents');
                      }
                    }}
                    className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-green-700 transition-all uppercase tracking-widest active:scale-95 shadow-sm whitespace-nowrap"
                  >
                    Approve
                  </button>
                )}

                <button
                  onClick={() => setShowGetLinkModal(true)}
                  className="flex items-center gap-2 px-3 py-2 border border-primary/20 rounded-lg text-xs font-semibold text-primary/70 hover:text-primary hover:border-primary transition-all whitespace-nowrap"
                  title="Copy share link"
                >
                  <LinkIcon className="w-4 h-4" />
                  Get Link
                </button>

                {selectedInvoice.type === 'invoice' && (selectedInvoice.status === 'Awaiting Payment' || selectedInvoice.status === 'Paid') && (
                  <button
                    onClick={() => {
                      const initialDeliveryItems = (selectedInvoice.items || []).map(item => {
                        const totalShipped = deliveryNotes
                          .filter(dn => dn.invoiceId === selectedInvoice.id && (dn.status === 'Shipped' || dn.status === 'Delivered' || dn.status === 'Assigned'))
                          .reduce((sum, dn) => {
                            const dnItem = dn.items.find(i => i.itemCode === item.item || i.itemId === item.item);
                            return sum + (dnItem?.quantityShipped || 0);
                          }, 0);

                        return {
                          itemId: item.item,
                          itemCode: item.item,
                          name: item.description,
                          description: item.description,
                          quantityOrdered: item.quantity,
                          quantityShipped: totalShipped,
                          unitPrice: item.unitPrice || 0,
                          qtyToShipNow: Math.max(0, item.quantity - totalShipped)
                        };
                      });

                      setActiveDeliveryNote({
                        id: '',
                        invoiceId: selectedInvoice.id,
                        invoiceNumber: selectedInvoice.number,
                        orgId,
                        contactId: selectedInvoice.contactId,
                        contactName: selectedInvoice.contactName || '',
                        items: initialDeliveryItems,
                        status: 'Draft',
                        brandingTheme: selectedInvoice.brandingTheme || 'Standard',
                        createdAt: new Date().toISOString()
                      });
                      setIsDeliveryModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-primary/80 transition-all uppercase tracking-widest active:scale-95 shadow-sm whitespace-nowrap"
                    title="Create Delivery Note"
                  >
                    <Truck className="w-4 h-4" />
                    Delivery Note
                  </button>
                )}

                {selectedInvoice.type === 'invoice' && selectedInvoice.status !== 'Void' && selectedInvoice.status !== 'Deleted' && (
                  <button
                    onClick={() => {
                      setNewInvoice(prev => ({
                        ...prev,
                        contactId: selectedInvoice.contactId,
                        contactName: selectedInvoice.contactName || '',
                        contactEmail: selectedInvoice.contactEmail || '',
                        date: new Date().toISOString().split('T')[0],
                        reference: selectedInvoice.number,
                        currency: selectedInvoice.currency || getDefaultCurrency(orgSettings),
                        brandingTheme: selectedInvoice.brandingTheme || 'Standard',
                        amountsAre: selectedInvoice.amountsAre || 'Tax Exclusive',
                        items: [...selectedInvoice.items],
                        linkedInvoiceId: selectedInvoice.id,
                        type: 'credit_note',
                        status: 'Draft',
                      }));
                      setApplyCreditTargetId(selectedInvoice.id);
                      setIsCreditNoteModalOpen(true);
                    }}
                    className="flex items-center gap-2 px-3 py-2 border border-primary/20 rounded-lg text-xs font-semibold text-primary/70 hover:text-primary hover:border-primary transition-all whitespace-nowrap"
                    title="Refund"
                  >
                    <Undo className="w-4 h-4" />
                    Refund
                  </button>
                )}

                {selectedInvoice.type === 'invoice' && selectedInvoice.status !== 'Void' && selectedInvoice.status !== 'Deleted' &&
                  selectedInvoice.totalAmount - (selectedInvoice.paymentReceived || 0) > 0.01 && (
                  <button
                    onClick={() => {
                      setNewInvoice(prev => ({
                        ...prev,
                        contactId: selectedInvoice.contactId,
                        contactName: selectedInvoice.contactName || '',
                        contactEmail: selectedInvoice.contactEmail || '',
                        date: selectedInvoice.date || new Date().toISOString().split('T')[0],
                        reference: selectedInvoice.number,
                        currency: selectedInvoice.currency || getDefaultCurrency(orgSettings),
                        brandingTheme: selectedInvoice.brandingTheme || 'Standard',
                        amountsAre: selectedInvoice.amountsAre || 'Tax Exclusive',
                        items: selectedInvoice.items.map(item => ({ ...item })),
                        linkedInvoiceId: selectedInvoice.id,
                        type: 'credit_note',
                        status: 'Draft',
                      }));
                      const invoiceDue = Math.max(0, selectedInvoice.totalAmount - (selectedInvoice.paymentReceived || 0));
                      setIsApplyCreditMode(true);
                      setCreditApplicationMax(invoiceDue);
                      setCreditApplicationAmount(invoiceDue);
                      setNewInvoice(prev => ({ ...prev, creditApplicationAmount: invoiceDue }));
                      setApplyCreditTargetId(selectedInvoice.id);
                      setIsCreditNoteModalOpen(true);
                    }}
                    className="flex items-center gap-2 px-3 py-2 border border-yellow-500 bg-yellow-100 text-yellow-900 rounded-lg text-xs font-semibold hover:bg-yellow-200 transition-all whitespace-nowrap"
                    title="Apply Credit"
                  >
                    <Undo className="w-4 h-4" />
                    Apply Credit
                  </button>
                )}

                <button
                  onClick={() => setShowCopyModal(true)}
                  className="flex items-center gap-2 px-3 py-2 border border-primary/20 rounded-lg text-xs font-semibold text-primary/70 hover:text-primary hover:border-primary transition-all whitespace-nowrap"
                  title="Copy this invoice"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>

                <button
                  onClick={() => setShowQrModal(true)}
                  className="flex items-center gap-2 px-3 py-2 border border-primary/20 rounded-lg text-xs font-semibold text-primary/70 hover:text-primary hover:border-primary transition-all whitespace-nowrap"
                  title="Scan with Mobile"
                >
                  <QrCode className="w-4 h-4" />
                  Scan QR code
                </button>

                <button
                  onClick={() => handleEditInvoice(selectedInvoice)}
                  className="p-2 text-primary/40 hover:text-primary transition-colors"
                  title="Edit Status / Content"
                >
                  <Edit className="w-5 h-5" />
                </button>

                <button className="text-primary/40 hover:text-primary transition-colors hover:scale-110 active:scale-95" onClick={() => handlePrint(selectedInvoice, selectedInvoice.type === 'quote' ? 'quote' : 'invoice')}>
                  <Printer className="w-5 h-5" />
                </button>

                <button
                  onClick={handleDownloadPDF}
                  className="p-2 text-primary/40 hover:text-primary transition-colors"
                  title="Download PDF"
                >
                  <Download className="w-5 h-5" />
                </button>

                {((selectedInvoice.status === 'Awaiting Payment' || selectedInvoice.status === 'Awaiting Approval') && (selectedInvoice.paymentReceived || 0) <= 0) && (
                  <button
                    onClick={() => handleUpdateStatus(selectedInvoice.id, 'Void')}
                    className="flex items-center gap-2 px-3 py-2 border border-red-200 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-50 transition-all whitespace-nowrap"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Void
                  </button>
                )}
                {(selectedInvoice.status === 'Awaiting Payment' && (selectedInvoice.paymentReceived || 0) > 0) && (
                  <div className="flex items-center gap-2 px-3 py-2 border border-amber-200 text-amber-600 rounded-lg text-xs font-semibold bg-amber-50/50 cursor-help whitespace-nowrap" title="To void this invoice, you must remove the payments first.">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Cannot Void (Payments Exist)
                  </div>
                )}
                {selectedInvoiceIsDraft && (
                  <button
                    onClick={() => setInvoiceToDelete(selectedInvoice.id)}
                    className="flex items-center gap-2 px-3 py-2 border border-red-200 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-50 transition-all whitespace-nowrap"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                )}

                <button
                  onClick={handleCloseInvoice}
                  className="ml-4 p-1 hover:bg-primary/5 rounded-full transition-colors"
                >
                  <XCircle className="w-6 h-6 text-primary/40 hover:text-primary" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-8 md:p-16 custom-scrollbar text-primary print:overflow-visible print:p-0 print:h-auto">
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Main Card */}
              <div className="border border-primary/20 rounded-2xl shadow-xl overflow-hidden min-h-[600px] bg-card">
                {/* Metadata Grid */}
                <div className="p-8 grid grid-cols-1 md:grid-cols-6 gap-8 bg-muted/30">
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-primary/50 uppercase tracking-widest">Customer</p>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <p
                          onClick={() => {
                            if (onNavigate && selectedInvoice.contactId) {
                              onNavigate('customers', selectedInvoice.contactId);
                            }
                          }}
                          className="text-sm font-bold text-primary hover:underline cursor-pointer italic serif"
                        >
                          {contacts.find(c => c.id === selectedInvoice.contactId)?.name || selectedInvoice.contactName || 'Walk-in Customer'}
                        </p>
                      </div>
                      <div className="text-[10px] text-primary/60 font-medium">
                        {(() => {
                          const contact = contacts.find(c => c.id === selectedInvoice.contactId);
                          return contact?.email || (contact?.emails && contact.emails.length > 0 ? contact.emails[0] : null) || selectedInvoice.contactEmail || 'No email provided';
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-primary/50 uppercase tracking-widest">{language === 'ar' ? 'التاريخ' : 'Date'}</p>
                    <p className="text-sm text-primary font-bold">{new Date(selectedInvoice.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-primary/50 uppercase tracking-widest">{language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date'}</p>
                    <p className="text-sm text-primary font-bold">{new Date(selectedInvoice.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-primary/50 uppercase tracking-widest">{language === 'ar' ? 'المرجع' : 'Reference'}</p>
                    <p className="text-sm text-primary font-bold">{selectedInvoice.reference || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-primary/50 uppercase tracking-widest">{language === 'ar' ? 'النسق' : 'Branding Theme'}</p>
                    <p className="text-sm text-primary font-bold">{brandingThemes.find(t => t.id === selectedInvoice.brandingTheme)?.name || selectedInvoice.brandingTheme || 'Standard'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-primary/50 uppercase tracking-widest">Invoice number</p>
                    <p className="text-sm text-primary font-mono font-bold">{selectedInvoice.number}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-primary/50 uppercase tracking-widest">{language === 'ar' ? 'العملة' : 'Currency'}</p>
                    <p className="text-sm text-primary font-bold">{selectedInvoice.currency}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-primary/50 uppercase tracking-widest">Amounts</p>
                    <p className="text-sm text-primary font-bold">{selectedInvoice.amountsAre}</p>
                  </div>
                </div>

                <div className="px-8 py-4 text-right bg-white/10">
                  <p className="text-[10px] text-primary/40 font-bold uppercase tracking-widest italic">{selectedInvoice.amountsAre}</p>
                </div>

                {/* Items Table */}
                <div className="border-t border-primary/10">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-white/20 border-b border-primary/10">
                        <th className="py-4 px-8 text-[11px] font-bold text-primary/50 uppercase tracking-widest">Item (Code - Name)</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-primary/50 uppercase tracking-widest">{language === 'ar' ? 'الوصف' : 'Description'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-primary/50 uppercase tracking-widest text-right">{language === 'ar' ? 'الكمية' : 'Qty'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-primary/50 uppercase tracking-widest">UOM</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-primary/50 uppercase tracking-widest text-right">{language === 'ar' ? 'السعر' : 'Price'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-primary/50 uppercase tracking-widest">{language === 'ar' ? 'الخصم' : 'Disc'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-primary/50 uppercase tracking-widest text-right">Tax</th>
                        {trackingCategories.map(cat => (
                          <th key={cat.id} className="py-4 px-4 text-[11px] font-bold text-primary/50 uppercase tracking-widest">{cat.name}</th>
                        ))}
                        <th className="py-4 px-8 text-[11px] font-bold text-primary/50 uppercase tracking-widest text-right">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-primary/5">
                      {(selectedInvoice.items || []).map((item, idx) => {
                        const taxRateValue = getTaxRateValue(item.taxRate);
                        const subtotalBeforeDiscount = item.quantity * item.unitPrice;
                        const discountAmount = (item as any).discountType === 'amount'
                          ? (item.discount || 0)
                          : (subtotalBeforeDiscount * (item.discount || 0) / 100);
                        const subtotal = subtotalBeforeDiscount - discountAmount;
                        let taxAmount = 0;
                        let lineTotal = subtotal;

                        if (selectedInvoice.amountsAre === 'Tax Exclusive') {
                          taxAmount = subtotal * (taxRateValue / 100);
                          lineTotal = subtotal + taxAmount;
                        } else if (selectedInvoice.amountsAre === 'Tax Inclusive') {
                          taxAmount = subtotal - (subtotal / (1 + taxRateValue / 100));
                        }

                        const itemData = itemsList.find(i => i.id === item.item || i.code === item.item);
                        return (
                          <tr key={idx} className="hover:bg-white/40 transition-colors">
                            <td className="py-5 px-8 text-sm font-bold text-primary">
                              <button
                                onClick={() => {
                                  if (onNavigate && (itemData?.id || item.item)) {
                                    onNavigate('inventory', itemData?.id || item.item);
                                  }
                                }}
                                className="text-left hover:underline hover:text-primary transition-colors focus:outline-none"
                              >
                                {itemData?.code || item.item} {itemData?.name ? `- ${itemData.name}` : ''}
                              </button>
                            </td>
                            <td className="py-5 px-4 text-sm font-bold text-primary italic serif">{item.description}</td>
                            <td className="py-5 px-4 text-sm font-bold text-primary text-right">{(item.quantity || 0).toFixed(2)}</td>
                            <td className="py-5 px-4 text-sm font-bold text-primary">{getInvoiceLineUom(item, itemData) || '—'}</td>
                            <td className="py-5 px-4 text-sm font-bold text-primary text-right">{(item.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="py-5 px-4 text-sm font-bold text-primary">{(item.discount && item.discount > 0) ? ((item as any).discountType === 'amount' ? item.discount.toLocaleString() : `${item.discount}%`) : ''}</td>
                            <td className="py-5 px-4 text-sm font-bold text-primary text-right">{(taxAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            {trackingCategories.map(cat => {
                              const trackingParts = (item.trackingCategory || '').split('|');
                              const valPart = trackingParts.find(p => p.startsWith(`${cat.id}:`));
                              const val = valPart ? valPart.split(':')[1] : '-';
                              return (
                                <td key={cat.id} className="py-5 px-4 text-sm font-bold text-primary">
                                  {val}
                                </td>
                              );
                            })}
                            <td className="py-5 px-8 text-sm font-bold text-primary text-right">
                              {(lineTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Delivery Notes List */}
                {deliveryNotes.filter(dn => dn.invoiceId === selectedInvoice.id).length > 0 && (
                  <div className="px-8 py-6 border-t border-primary/10 bg-primary/5">
                    <div className="flex items-center gap-2 mb-4">
                      <Truck className="w-5 h-5 text-primary" />
                      <h3 className="text-sm font-bold uppercase tracking-widest text-primary tracking-wider">Shipments & Delivery Notes</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {deliveryNotes.filter(dn => dn.invoiceId === selectedInvoice.id).map(dn => (
                        <div key={dn.id} className="bg-white p-4 rounded-xl border border-primary/10 shadow-sm space-y-3">
                          <div className="flex justify-between items-start">
                            <div className="space-y-0.5">
                              <p className="text-[10px] font-bold text-primary uppercase tracking-widest leading-none">Status: {dn.status}</p>
                              <p className="text-xs font-bold text-primary">Delivery #{dn.id.slice(-6).toUpperCase()}</p>
                            </div>
                            <span className={cn(
                              "text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-tighter shadow-sm",
                              dn.status === 'Delivered' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-primary/20 text-primary border border-primary/20'
                            )}>
                              {dn.status}
                            </span>
                          </div>

                          <div className="space-y-1">
                            {(dn.items || []).map((it, idx) => (
                              <div key={idx} className="flex justify-between text-[11px] items-center">
                                <span className="opacity-60 truncate mr-2">{it.name || it.description}</span>
                                <span className="font-bold flex-shrink-0">x {it.quantityShipped}</span>
                              </div>
                            ))}
                          </div>

                          <div className="pt-3 border-t border-primary/5 flex justify-between items-center">
                            <div className="flex flex-col">
                              <span className="text-[9px] opacity-40 uppercase font-bold tracking-widest">Courier</span>
                              <span className="text-[11px] font-bold text-primary truncate max-w-[100px]">{dn.assignedPersonnelName || 'Unassigned'}</span>
                            </div>
                            <div className="flex gap-2">
                              {dn.status === 'Assigned' && (
                                <button
                                  onClick={() => {
                                    setActiveDeliveryNote(dn);
                                    setIsPODModalOpen(true);
                                  }}
                                  className="p-2 bg-primary text-white rounded-lg hover:bg-primary/80 transition-all shadow-md active:scale-95 group"
                                  title="Confirm Delivery (POD)"
                                >
                                  <UserCheck className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                </button>
                              )}
                              {dn.status === 'Delivered' && (
                                <div className="flex gap-1.5">
                                  {dn.signatureUrl && (
                                    <a
                                      href={dn.signatureUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="p-1.5 bg-primary/5 rounded-lg hover:bg-primary/10 transition-all text-primary border border-primary/5"
                                      title="View Signature"
                                    >
                                      <Signature className="w-3.5 h-3.5" />
                                    </a>
                                  )}
                                  {dn.photoUrl && (
                                    <a
                                      href={dn.photoUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="p-1.5 bg-primary/5 rounded-lg hover:bg-primary/10 transition-all text-primary border border-primary/5"
                                      title="View Package Photo"
                                    >
                                      <Camera className="w-3.5 h-3.5" />
                                    </a>
                                  )}
                                </div>
                              )}
                              <button
                                onClick={() => handlePrintDeliveryNote(dn)}
                                className="p-1.5 bg-primary/10 rounded-lg hover:bg-primary/20 transition-all text-primary border border-primary/10 active:scale-95"
                                title="Print Delivery Note"
                              >
                                <Printer className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bottom Totals Section */}
                <div className="p-8 flex flex-col md:flex-row justify-between items-start gap-12 border-t border-primary/10 bg-white/10">
                  <div className="space-y-6 flex-1 max-w-sm">
                    <div className="flex flex-col gap-4 print:hidden">
                      <input
                        type="file"
                        id="invoice-file-upload"
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={isUploading}
                      />
                      <div className="flex flex-col gap-4">
                        <button
                          onClick={() => document.getElementById('invoice-file-upload')?.click()}
                          disabled={isUploading}
                          className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-xl text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-all shadow-lg active:scale-95 disabled:opacity-50 relative overflow-hidden self-start"
                        >
                          {isUploading && uploadProgress !== null && (
                            <div
                              className="absolute inset-0 bg-white/10 transition-all duration-300 pointer-events-none"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          )}
                          <div className="flex items-center gap-2 relative z-10">
                            {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-paperclip"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>}
                            <span>
                              {isUploading
                                ? `${language === 'ar' ? 'جاري الرفع' : 'Uploading'} ${uploadProgress ? Math.round(uploadProgress) : 0}%`
                                : (language === 'ar' ? 'إرفاق ملفات' : 'Attach files')
                              }
                            </span>
                            {!isUploading && <ChevronDown className="w-3 h-3 ml-1" />}
                          </div>
                        </button>

                        {/* Attachments List */}
                        {selectedInvoice.attachments && selectedInvoice.attachments.length > 0 && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                            {(selectedInvoice.attachments || []).map((file, idx) => (
                              <div key={idx} className="flex items-center justify-between gap-3 p-3 bg-white/40 rounded-xl border border-primary/5 group hover:border-primary/20 transition-all">
                                <div className="flex items-center gap-3 overflow-hidden">
                                  <div className="p-2 bg-primary/5 rounded-lg">
                                    <FileText className="w-4 h-4 text-primary/40" />
                                  </div>
                                  <div className="flex flex-col overflow-hidden">
                                    <span className="text-xs font-bold text-primary truncate">{file.name}</span>
                                    <span className="text-[10px] text-primary/40 font-mono uppercase">
                                      {(file.size / 1024).toFixed(1)} KB
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 hover:bg-primary/5 rounded-lg text-primary/40 hover:text-primary transition-colors"
                                    title="Download"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </a>
                                  <button
                                    onClick={() => handleRemoveAttachment(file.url)}
                                    className="p-1.5 hover:bg-red-50 rounded-lg text-primary/40 hover:text-red-500 transition-colors"
                                    title="Remove"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedInvoice.termsAndConditions && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest opacity-40 mb-2">{language === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions'}</p>
                        <p className="text-xs opacity-60 leading-relaxed whitespace-pre-wrap">{selectedInvoice.termsAndConditions}</p>
                      </div>
                    )}
                    {activeInvoiceTheme?.paymentInstructions && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest opacity-40 mb-2">{language === 'ar' ? 'تعليمات الدفع' : 'Payment Instructions'}</p>
                        <p className="text-xs opacity-60 leading-relaxed whitespace-pre-wrap">{activeInvoiceTheme.paymentInstructions}</p>
                      </div>
                    )}
                  </div>

                  <div className="w-80 space-y-4">
                    <div className="flex justify-between items-center text-xs font-bold text-primary/60 uppercase tracking-widest">
                      <span>{language === 'ar' ? 'المجموع الإجمالي' : 'Gross Total'}</span>
                      <span className="font-mono">{selectedInvoice.currency} {selectedInvoice.items.reduce((sum, i) => sum + (i.quantity * i.unitPrice), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {selectedInvoice.items.reduce((sum, i) => {
                      const base = i.quantity * i.unitPrice;
                      const disc = (i as any).discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                      return sum + disc;
                    }, 0) > 0 && (
                      <div className="flex justify-between items-center text-xs font-bold text-primary/60 uppercase tracking-widest">
                        <span className="text-red-500">{language === 'ar' ? 'ناقص: الخصم' : 'Less: Discount'}</span>
                        <span className="font-mono text-red-500">-{selectedInvoice.currency} {selectedInvoice.items.reduce((sum, i) => {
                          const base = i.quantity * i.unitPrice;
                          const disc = (i as any).discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                          return sum + disc;
                        }, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-xs font-bold text-primary/60 uppercase tracking-widest pt-2 border-t border-primary/10">
                      <span>{language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                      <span className="font-mono">{selectedInvoice.currency} {(selectedInvoice.items.reduce((sum, i) => {
                        const base = i.quantity * i.unitPrice;
                        const disc = (i as any).discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                        return sum + (base - disc);
                      }, 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs font-bold text-primary/60 uppercase tracking-widest">
                      <span>{language === 'ar' ? 'إجمالي الضريبة' : 'Total Tax'}</span>
                      <span className="font-mono">{selectedInvoice.currency} {(selectedInvoice.totalAmount - selectedInvoice.items.reduce((sum, i) => {
                        const base = i.quantity * i.unitPrice;
                        const disc = (i as any).discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                        return sum + (base - disc);
                      }, 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {selectedInvoice.type === 'credit_note' && Number(selectedInvoice.appliedAmount || 0) > 0 && (
                      <div className="space-y-2 pt-4 border-t border-primary/10">
                        <div className="flex justify-between items-center text-xs font-bold text-primary">
                          <span>{language === 'ar' ? 'تم تطبيقه على الفاتورة' : 'Applied to Invoice'}</span>
                          <button
                            type="button"
                            className="font-mono underline hover:text-primary/70"
                            onClick={() => {
                              const targetId = selectedInvoice.appliedInvoiceId;
                              const target = invoices.find(invoice => invoice.id === targetId);
                              if (target) setSelectedInvoice(target);
                              else notify.error('Invoice not found');
                            }}
                          >
                            {selectedInvoice.appliedInvoiceNumber || invoices.find(invoice => invoice.id === selectedInvoice.appliedInvoiceId)?.number || selectedInvoice.appliedInvoiceId}
                          </button>
                        </div>
                        <div className="flex justify-between items-center text-xs font-bold text-red-500">
                          <span>{language === 'ar' ? 'ناقص: الرصيد المطبق' : 'Less: Credit Applied'}</span>
                          <span className="font-mono">- {selectedInvoice.currency} {Number(selectedInvoice.appliedAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    )}
                    {invoicePayments.length > 0 && (
                      <div className="space-y-3 pt-4 border-t border-primary/10">
                        {invoicePayments.map((payment, idx) => {
                          const isCreditNotePayment = payment.accountId === 'credit_note_applied';
                          const account = !isCreditNotePayment ? (accountsList.find(a => a.id === payment.accountId) ||
                                          bankAccountsList.find(a => a.id === payment.accountId)) : null;
                          const displayName = isCreditNotePayment ? (payment.reference || 'Applied Credit') : (account?.accountName || account?.name || 'Bank/Cash');

                          return (
                            <div
                              key={idx}
                              onClick={(e) => {
                                if (isCreditNotePayment && payment.sourceId) {
                                  e.stopPropagation();
                                  const cn = invoices.find(inv => inv.id === payment.sourceId);
                                  if (cn) {
                                    setSelectedInvoice(cn);
                                  } else {
                                    notify.error("Credit note not found");
                                  }
                                } else {
                                  setPaymentToDelete(payment);
                                }
                              }}
                              className={cn(
                                "group cursor-pointer space-y-0.5 p-2 -mx-2 rounded-xl transition-all",
                                isCreditNotePayment ? "hover:bg-primary/10" : "hover:bg-primary/5"
                              )}
                              title={isCreditNotePayment ? "Click to view credit note" : "Click to view details or remove"}
                            >
                              <div className="flex justify-between items-center text-xs text-primary">
                                <span className={cn(
                                  "font-bold transition-colors",
                                  isCreditNotePayment ? "group-hover:text-primary" : "group-hover:text-red-600"
                                )}>
                                  {language === 'ar' ? 'ناقص: ' : 'Less: '}{isCreditNotePayment ? (language === 'ar' ? 'رصيد مستخدم' : 'Credit Applied') : (language === 'ar' ? 'دفعة' : 'Payment')} ({new Date(payment.date).toLocaleDateString()})
                                </span>
                                <span className={cn(
                                  "font-mono text-right transition-colors",
                                  isCreditNotePayment ? "group-hover:text-primary" : "group-hover:text-red-600"
                                )}>
                                  - {(payment?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                              <div className={cn(
                                "text-[10px] italic font-medium",
                                isCreditNotePayment ? "text-primary underline" : "text-primary/50"
                              )}>
                                {displayName}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex justify-between items-center text-2xl font-bold pt-4 border-t-2 border-primary">
                      <span className="italic serif text-primary">{language === 'ar' ? 'إجمالي المستحق' : 'Total Due'}</span>
                      <span className="font-mono text-primary">
                        {selectedInvoice.currency} {Number(selectedInvoice.amountDue ?? (selectedInvoice.totalAmount - invoicePayments.reduce((sum, p) => sum + Number(p.amount || 0), 0))).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>

                    {(selectedInvoice.status === 'Awaiting Payment' || (selectedInvoice.type === 'credit_note' && selectedInvoice.status !== 'Paid' && selectedInvoice.status !== 'Void' && selectedInvoice.status !== 'Draft')) && (
                      <div className="flex justify-end mt-8">
                        <button
                          onClick={() => {
                            const amountDue = selectedInvoice.totalAmount - (selectedInvoice.paymentReceived || 0);
                            setPaymentData(prev => ({ ...prev, amount: Math.abs(amountDue) }));
                            setIsPaymentModalOpen(true);
                          }}
                          className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-lg active:scale-95"
                        >
                          <CreditCard className="w-4 h-4" />
                          {selectedInvoice.type === 'credit_note' ? (language === 'ar' ? 'إجراء دفعة' : 'Make Payment') : (language === 'ar' ? 'تسجيل دفعة' : 'Record Payment')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <DocumentChatterPanel
                orgId={orgId}
                entityId={selectedInvoice.id}
                entityType={selectedInvoice.type === 'credit_note' ? 'CreditNote' : 'Invoice'}
                locale={language === 'ar' ? 'ar' : 'en'}
                defaultTab="notes"
                collapsed={!showHistoryNotes}
                onCollapsedChange={(collapsed) => setShowHistoryNotes(!collapsed)}
                className="mb-12 mt-8 lg:w-full lg:max-h-none"
                relatedDocuments={selectedInvoiceSourceQuoteId ? [{
                  id: String(selectedInvoiceSourceQuoteId),
                  label: `Source Order ${selectedInvoiceSourceQuoteNumber || selectedInvoiceSourceQuoteId}`,
                  type: 'Quote',
                  status: selectedInvoiceSourceQuote?.status,
                }] : []}
                onRelatedDocumentOpen={(document) => {
                  setSelectedInvoice(null);
                  onNavigate?.('quotes', document.id);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Linked Delivery Notes Modal */}
      {showLinkedDeliveries && selectedInvoice && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowLinkedDeliveries(false);
          }}
        >
          <div className="bg-white rounded-[2rem] w-full max-w-xl max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain shadow-2xl border border-primary/10">
            <div className="p-8 border-b border-primary/5 flex items-center justify-between bg-primary/5">
              <div>
                <h3 className="text-xl font-bold text-primary italic serif">Linked Delivery Notes</h3>
                <p className="text-xs text-primary/40 font-bold uppercase tracking-widest mt-1">Delivery notes created from Invoice {selectedInvoice.number}</p>
              </div>
              <button
                onClick={() => setShowLinkedDeliveries(false)}
                className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all shadow-sm"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-8 space-y-3 max-h-[60vh] overflow-y-auto custom-scrollbar">
              {deliveryNotes.filter(dn => dn.invoiceId === selectedInvoice.id ).map(dn => (
                <button
                  key={dn.id}
                  onClick={() => {
                    setShowLinkedDeliveries(false);
                    setSelectedInvoice(null);
                    if (onNavigate) onNavigate('deliveries', dn.id);
                  }}
                  className="w-full flex items-center justify-between p-5 rounded-2xl border border-primary/5 hover:border-primary hover:bg-primary/5 transition-all text-left shadow-sm group active:scale-[0.99]"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/5 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Truck className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-primary tracking-tight">#{(dn as any).number || dn.id.slice(-8).toUpperCase()}</p>
                      <p className="text-[10px] text-primary/50 font-bold uppercase tracking-widest">{dn.status}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <p className="text-[10px] text-primary/40 font-bold uppercase tracking-widest">
                      {(dn.items || []).length} Items
                    </p>
                    <div className="flex items-center gap-1 text-[9px] font-bold text-[#0073AA] uppercase tracking-widest">
                       View <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Get Link Modal */}
      {showGetLinkModal && selectedInvoice && (
        <div className="fixed inset-0 bg-primary/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl border border-primary/20 animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-primary/10 flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-widest italic serif">{language === 'ar' ? 'مشاركة الرابط' : 'Share Link'}</h3>
              <button
                onClick={() => setShowGetLinkModal(false)}
                className="p-1 hover:bg-primary/5 rounded-full transition-colors"
              >
                <XCircle className="w-5 h-5 text-primary/40" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <p className="text-xs font-bold text-primary/60 leading-relaxed">
                {language === 'ar' ? 'يمكن لأي شخص لديه هذا الرابط عرض الفاتورة عبر الإنترنت.' : 'Anyone with this link can view the invoice online.'}
              </p>

              <div className="relative">
                <input
                  type="text"
                  readOnly
                  value={getPortalUrl('invoice', selectedInvoice.id)}
                  className="w-full bg-white/50 border border-primary/10 rounded-xl px-4 py-3 text-[11px] font-mono pr-20 outline-none focus:border-primary/30 transition-all font-bold"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(getPortalUrl('invoice', selectedInvoice.id));
                    notify.confirm('Link copied to clipboard');
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 bg-primary text-primary-foreground rounded-lg text-[10px] font-bold uppercase tracking-widest hover:opacity-90 transition-all active:scale-95"
                >
                  {language === 'ar' ? 'نسخ' : 'Copy'}
                </button>
              </div>

              <div className="pt-4">
                <button
                  onClick={() => setShowGetLinkModal(false)}
                  className="w-full py-4 text-primary font-bold text-xs uppercase tracking-widest hover:bg-primary/5 rounded-xl transition-all"
                >
                  {language === 'ar' ? 'إغلاق' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQrModal && selectedInvoice && (
        <div className="fixed inset-0 bg-primary/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl border border-primary/20 animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-primary/10 flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-widest italic serif">{language === 'ar' ? 'مسح رمز QR' : 'Scan QR Code'}</h3>
              <button
                onClick={() => setShowQrModal(false)}
                className="p-1 hover:bg-primary/5 rounded-full transition-colors"
              >
                <XCircle className="w-5 h-5 text-primary/40" />
              </button>
            </div>
            <div className="p-8 flex flex-col items-center gap-8">
              <p className="text-xs font-bold text-primary/60 leading-relaxed text-center">
                {language === 'ar' ? 'امسح رمز QR هذا بأي جهاز محمول لعرض هذه الفاتورة مباشرة في البوابة.' : 'Scan this QR code with any mobile device to view this invoice live in the portal.'}
              </p>

              <div className="p-6 bg-white rounded-2xl shadow-inner border border-primary/10 flex items-center justify-center">
                <QRCodeSVG
                  value={getPortalUrl('invoice', selectedInvoice.id)}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>

              <div className="w-full space-y-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-center opacity-40">{language === 'ar' ? 'رابط الفاتورة' : 'Invoice Link'}</p>
                <p className="text-[9px] font-mono text-center font-bold break-all opacity-60">
                  {getPortalUrl('invoice', selectedInvoice.id)}
                </p>
              </div>

              <button
                onClick={() => setShowQrModal(false)}
                className="w-full py-4 bg-primary text-primary-foreground font-bold text-xs uppercase tracking-widest rounded-xl transition-all shadow-lg active:scale-95"
              >
                {language === 'ar' ? 'تم' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Invoice Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-primary/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl border border-primary/20 animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-primary/10 flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-widest italic serif">{language === 'ar' ? 'نسخ إلى...' : 'Copy to...'}</h3>
              <button
                onClick={() => setShowCopyModal(false)}
                className="p-1 hover:bg-primary/5 rounded-full transition-colors"
              >
                <XCircle className="w-5 h-5 text-primary/40" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <button
                onClick={() => handleCopyInvoice('invoice')}
                disabled={isSaving}
                className="w-full flex items-center justify-between p-4 bg-white border border-primary/10 rounded-xl hover:border-primary transition-all group active:scale-95 disabled:opacity-50"
              >
                <div className="text-left">
                  <p className="text-sm font-bold text-primary">{language === 'ar' ? 'مسودة فاتورة' : 'Draft Invoice'}</p>
                  <p className="text-[10px] text-primary/40 uppercase tracking-widest font-bold">{language === 'ar' ? 'رقم تسلسلي جديد' : 'New sequential number'}</p>
                </div>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4 text-primary/20 group-hover:text-primary transition-colors" />}
              </button>
              <button
                onClick={() => handleCopyInvoice('quote')}
                disabled={isSaving}
                className="w-full flex items-center justify-between p-4 bg-white border border-primary/10 rounded-xl hover:border-primary transition-all group active:scale-95 disabled:opacity-50"
              >
                <div className="text-left">
                  <p className="text-sm font-bold text-primary">{language === 'ar' ? 'مسودة عرض سعر' : 'Draft Quotation'}</p>
                  <p className="text-[10px] text-primary/40 uppercase tracking-widest font-bold">{language === 'ar' ? 'رقم تسلسلي جديد' : 'New sequential number'}</p>
                </div>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4 text-primary/20 group-hover:text-primary transition-colors" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {isEmailModalOpen && (
        <div className="fixed inset-0 bg-primary/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-white border border-primary w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-primary flex items-center justify-between bg-primary text-primary-foreground dark:bg-[#000000]">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5" />
                <h2 className="text-xl font-bold italic serif">{language === 'ar' ? 'إرسال الفاتورة' : 'Send Invoice'}</h2>
              </div>
              <button
                onClick={() => setIsEmailModalOpen(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-primary-foreground dark:text-white"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-8 space-y-6 overflow-y-auto custom-scrollbar">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'إلى' : 'To'}</label>
                <input
                  type="email"
                  value={emailData.to}
                  onChange={(e) => setEmailData(prev => ({ ...prev, to: e.target.value }))}
                  placeholder="customer@example.com"
                  className="w-full bg-white border border-primary p-4 rounded-2xl outline-none focus:ring-2 focus:ring-primary/10 transition-all font-bold"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'الموضوع' : 'Subject'}</label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full bg-white border border-primary p-4 rounded-2xl outline-none focus:ring-2 focus:ring-primary/10 transition-all font-bold"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'الرسالة' : 'Message'}</label>
                <textarea
                  value={emailData.body}
                  onChange={(e) => setEmailData(prev => ({ ...prev, body: e.target.value }))}
                  rows={12}
                  className="w-full bg-white border border-primary p-4 rounded-2xl outline-none focus:ring-2 focus:ring-primary/10 transition-all font-medium text-sm leading-relaxed resize-none"
                />
              </div>
            </div>

            <div className="p-6 border-t border-primary bg-white flex justify-end gap-3">
              <button
                onClick={() => setIsEmailModalOpen(false)}
                className="px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-primary/5 transition-colors"
              >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
              <button
                onClick={handleConfirmSendEmail}
                className="px-8 py-3 bg-primary text-primary-foreground rounded-full text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-opacity flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                {language === 'ar' ? 'إرسال الفاتورة' : 'Send Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {isPaymentModalOpen && (
        <div className="fixed inset-0 bg-primary/20 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-background border border-primary/20 w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col">
            <div className="p-6 border-b border-primary/10 flex items-center justify-between bg-primary text-primary-foreground">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5" />
                <h2 className="text-xl font-bold italic serif">{language === 'ar' ? 'تسجيل دفعة' : 'Record Payment'}</h2>
              </div>
              <button
                onClick={() => setIsPaymentModalOpen(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-8 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'تاريخ الدفع' : 'Date of Payment'}</label>
                  <input
                    type="date"
                    value={paymentData.date}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full bg-white border border-primary/20 p-4 rounded-2xl outline-none focus:ring-2 focus:ring-primary/10 transition-all font-bold"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'المبلغ المدفوع' : 'Amount Paid'}</label>
                  <input
                    type="number"
                    value={isNaN(paymentData.amount) ? '' : paymentData.amount}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value === '' ? 0 : parseFloat(e.target.value) }))}
                    className="w-full bg-white border border-primary/20 p-4 rounded-2xl outline-none focus:ring-2 focus:ring-primary/10 transition-all font-mono font-bold"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'تم الدفع إلى (الحساب)' : 'Paid To (Account)'}</label>
                <SearchableSelect
                  value={paymentData.accountId}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, accountId: e.target.value }))}
                  options={[
                    ...bankAccountsList.map(acc => ({ value: acc.id, label: `${acc.accountName} (${acc.accountNumber})`, group: language === 'ar' ? 'حسابات بنكية' : 'Bank Accounts' })),
                    ...accountsList.filter(acc => acc.enablePayments).map(acc => ({ value: acc.id, label: `${acc.code} - ${acc.name}`, group: language === 'ar' ? 'دليل الحسابات' : 'Chart of Accounts' }))
                  ]}
                  placeholder={language === 'ar' ? 'اختر الحساب' : 'Select Account'}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'المرجع' : 'Reference'}</label>
                <input
                  type="text"
                  value={paymentData.reference}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="e.g. TXN-123"
                  className="w-full bg-white border border-primary/20 p-4 rounded-2xl outline-none focus:ring-2 focus:ring-primary/10 transition-all font-bold"
                />
              </div>
            </div>

            <div className="p-6 border-t border-primary/10 bg-primary/5 flex justify-end gap-3">
              <button
                onClick={() => setIsPaymentModalOpen(false)}
                className="px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-primary/5 transition-colors text-primary"
              >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
              <button
                onClick={handleRecordPayment}
                disabled={isSaving}
                className="px-8 py-3 bg-green-600 text-white rounded-full text-xs font-bold uppercase tracking-widest hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {language === 'ar' ? 'إضافة دفعة' : 'Add Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Invoice Confirmation Modal */}
      {invoiceToDelete && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-primary/40 backdrop-blur-sm z-[250] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setInvoiceToDelete(null);
          }}
        >
          <div className="bg-background border border-primary/20 w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-3xl shadow-2xl">
            <div className="p-8 text-center space-y-6">
              <div className="w-20 h-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
                <Trash2 className="w-10 h-10" />
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl font-bold italic serif">{language === 'ar' ? 'حذف الفاتورة؟' : 'Delete Invoice?'}</h3>
                <p className="text-sm opacity-60 leading-relaxed">
                  {language === 'ar' ? 'هل أنت متأكد أنك تريد حذف الفاتورة' : 'Are you sure you want to delete invoice'} <span className="font-bold text-primary">{invoices.find(i => i.id === invoiceToDelete)?.number}</span>؟
                  {language === 'ar' ? 'سيؤدي هذا الإجراء إلى تحديد الفاتورة كمحذوفة.' : 'This action will mark the invoice as deleted.'}
                </p>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                <button
                  onClick={() => handleDeleteInvoice(invoiceToDelete)}
                  disabled={isSaving}
                  className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-red-700 transition-all flex items-center justify-center gap-2"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  {language === 'ar' ? 'حذف الفاتورة' : 'Delete Invoice'}
                </button>
                <button
                  onClick={() => setInvoiceToDelete(null)}
                  disabled={isSaving}
                  className="w-full py-4 bg-white border border-primary/20 text-primary rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-primary/5 transition-all"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Payment Confirmation Modal */}
      {paymentToDelete && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-primary/40 backdrop-blur-sm z-[250] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPaymentToDelete(null);
          }}
        >
          <div className="bg-background border border-primary/20 w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-3xl shadow-2xl">
            <div className="p-8 text-center space-y-6">
              <div className="w-20 h-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
                <Trash2 className="w-10 h-10" />
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl font-bold italic serif">{language === 'ar' ? 'إزالة الدفعة؟' : 'Remove Payment?'}</h3>
                <p className="text-sm opacity-60 leading-relaxed">
                  {language === 'ar' ? 'سيؤدي هذا إلى إزالة الدفعة البالغة' : 'This will remove the payment of'} <span className="font-bold text-primary">${(paymentToDelete?.amount || 0).toLocaleString()}</span> {language === 'ar' ? 'المسجلة في' : 'recorded on'} {(() => {
                    const d = new Date(paymentToDelete.date);
                    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                  })()}.
                  {language === 'ar' ? 'سيزداد المبلغ المستحق على هذه الفاتورة.' : 'The amount due on this invoice will increase.'}
                </p>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                <button
                  onClick={() => handleRemovePayment(paymentToDelete)}
                  disabled={isSaving}
                  className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-red-700 transition-all flex items-center justify-center gap-2"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  {language === 'ar' ? 'إزالة وإعادة' : 'Remove and Redo'}
                </button>
                <button
                  onClick={() => setPaymentToDelete(null)}
                  disabled={isSaving}
                  className="w-full py-4 bg-white border border-primary/20 text-primary rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-primary/5 transition-all"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Reminders Modal */}
      {isRemindersModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-primary/20 backdrop-blur-sm z-[250] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsRemindersModalOpen(false);
          }}
        >
          <div className="bg-background w-full max-w-4xl max-h-[calc(100dvh-2rem)] rounded-3xl overflow-hidden shadow-2xl border border-primary/20 animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-primary/10 flex items-center justify-between bg-primary/5 text-primary">
              <h2 className="text-2xl font-bold italic serif">{language === 'ar' ? 'تذكيرات الفاتورة' : 'Invoice Reminders'}</h2>
              <button
                onClick={() => setIsRemindersModalOpen(false)}
                className="p-2 hover:bg-primary/10 rounded-full transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-8 overflow-y-auto max-h-[70vh] space-y-8 custom-scrollbar">
              <div className="bg-white border border-primary/20 p-8 rounded-2xl space-y-8 shadow-inner">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="enableReminders"
                    checked={remindersSettings.enabled}
                    onChange={(e) => setRemindersSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="w-5 h-5 rounded border-primary text-primary focus:ring-primary cursor-pointer"
                  />
                  <label htmlFor="enableReminders" className="text-lg font-bold italic serif cursor-pointer select-none text-primary">
                    {language === 'ar' ? 'إرسال بريد إلكتروني للعملاء عندما تكون الفاتورة...' : 'Email customers when an invoice is...'}
                  </label>
                </div>

                <div className="flex flex-wrap items-center gap-4 py-4">
                  {remindersSettings.reminders.map((reminder, idx) => (
                    <div key={reminder.id} className="flex items-center gap-4 text-primary">
                      <div
                        onClick={() => handleToggleReminder(reminder.id)}
                        className={cn(
                          "bg-white border p-6 rounded-xl w-48 text-center space-y-3 shadow-sm hover:shadow-md transition-all group cursor-pointer relative",
                          reminder.active ? "border-green-500 bg-green-50/10" : "border-primary/20 opacity-40 grayscale"
                        )}
                      >
                        {reminder.active && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-0.5">
                            <Check className="w-3 h-3" />
                          </div>
                        )}
                        <Mail className="w-6 h-6 mx-auto opacity-30 group-hover:opacity-100 transition-opacity" />
                        <p className="font-bold text-sm">
                          {reminder.days} {language === 'ar' ? 'أيام' : 'days'} {reminder.type === 'before_due' ? (language === 'ar' ? 'متبقية للاستحقاق' : 'due in') : (language === 'ar' ? 'متأخرة' : 'overdue')}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditReminder(reminder);
                          }}
                          className="text-xs font-bold text-primary hover:underline"
                        >
                          Edit
                        </button>
                      </div>
                      {idx < remindersSettings.reminders.length - 1 && (
                        <div className="h-[1px] w-8 bg-primary/20 hidden sm:block" />
                      )}
                    </div>
                  ))}
                  <div className="h-[1px] w-8 bg-primary/20 hidden sm:block" />
                  <button
                    onClick={handleAddReminder}
                    className="bg-white/50 border border-dashed border-primary/40 p-6 rounded-xl w-48 text-center space-y-3 hover:border-primary hover:bg-white transition-all group"
                  >
                    <Plus className="w-6 h-6 mx-auto opacity-30 group-hover:scale-110 transition-transform" />
                    <p className="font-bold text-xs opacity-50">+ Add reminder</p>
                  </button>
                </div>

                <div className="space-y-5 pt-8 border-t border-primary/10">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="includeButton"
                      checked={remindersSettings.includeOnlineButton}
                      onChange={(e) => setRemindersSettings(prev => ({ ...prev, includeOnlineButton: e.target.checked }))}
                      className="w-5 h-5 rounded border-primary text-primary focus:ring-primary"
                    />
                    <label htmlFor="includeButton" className="text-sm font-bold opacity-70 cursor-pointer text-primary">{language === 'ar' ? 'تضمين زر للفاتورة عبر الإنترنت وملخص التفاصيل' : 'Include button to online invoice and detail summary'}</label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="includePdf"
                      checked={remindersSettings.includePdfLink}
                      onChange={(e) => setRemindersSettings(prev => ({ ...prev, includePdfLink: e.target.checked }))}
                      className="w-5 h-5 rounded border-primary text-primary focus:ring-primary"
                    />
                    <label htmlFor="includePdf" className="text-sm font-bold opacity-70 cursor-pointer text-primary">{language === 'ar' ? 'تضمين رابط لملف PDF الخاص بالفاتورة' : 'Include a link to the invoice PDF'}</label>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="minAmountCheck"
                        checked={remindersSettings.minimumAmount > 0}
                        onChange={(e) => setRemindersSettings(prev => ({ ...prev, minimumAmount: e.target.checked ? 1.00 : 0 }))}
                        className="w-5 h-5 rounded border-primary text-primary focus:ring-primary"
                      />
                      <label htmlFor="minAmountCheck" className="text-sm font-bold opacity-70 cursor-pointer text-primary">
                        {language === 'ar' ? 'لا ترسل تذكيرات للمبالغ المستحقة على فاتورة أقل من' : "Don't send reminders for amounts owing on an invoice under"}
                      </label>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        value={remindersSettings.minimumAmount}
                        onChange={(e) => setRemindersSettings(prev => ({ ...prev, minimumAmount: parseFloat(e.target.value) || 0 }))}
                        className="w-24 bg-white border border-primary/20 px-3 py-1.5 rounded-lg text-sm font-bold text-center focus:ring-2 focus:ring-primary/10 text-primary"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-6 flex items-center justify-between opacity-50 italic text-xs serif border-t border-primary/10">
                  <span>{language === 'ar' ? 'إرسال الردود إلى' : 'Send replies to'} <strong>{remindersSettings.replyToEmail}</strong></span>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-primary/10 bg-primary/5 flex justify-end gap-3">
              <button
                onClick={() => setIsRemindersModalOpen(false)}
                className="px-8 py-3 rounded-full text-sm font-bold hover:bg-primary/5 transition-all text-primary"
              >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
              <button
                onClick={handleSaveReminders}
                disabled={isSaving}
                className="bg-primary text-primary-foreground px-10 py-3 rounded-full hover:opacity-90 disabled:opacity-50 transition-all font-bold flex items-center gap-2"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Reminder Modal */}
      {isEditReminderModalOpen && editingReminder && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-primary/20 backdrop-blur-sm z-[300] flex items-center justify-center p-4 text-primary"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsEditReminderModalOpen(false);
          }}
        >
          <div className="bg-background w-full max-w-3xl max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-3xl shadow-2xl border border-primary/20 animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-primary/10 flex items-center justify-between bg-primary/5 text-primary">
              <h2 className="text-2xl font-bold italic serif">{language === 'ar' ? 'تعديل التذكير' : 'Edit Reminder'}</h2>
              <button
                onClick={() => setIsEditReminderModalOpen(false)}
                className="p-2 hover:bg-primary/10 rounded-full transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-8 space-y-6">
              <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-widest opacity-60">
                <span>{language === 'ar' ? 'إرسال إذا كانت الفاتورة' : 'Send if an invoice is'}</span>
                <div className="flex-1">
                  <SearchableSelect
                    value={editingReminder.type || 'overdue'}
                    onChange={(e) => setEditingReminder({ ...editingReminder, type: e.target.value as any })}
                    options={[
                      { value: 'after_due_date', label: language === 'ar' ? 'أيام بعد تاريخ الاستحقاق' : 'days after the due date' },
                      { value: 'after_invoice_date', label: language === 'ar' ? 'أيام بعد تاريخ الفاتورة' : 'days after the invoice date' },
                      { value: 'before_due_date', label: language === 'ar' ? 'أيام قبل تاريخ الاستحقاق' : 'days before the due date' }
                    ]}
                    className="w-full"
                  />
                </div>
                <input
                  type="number"
                  value={editingReminder.days}
                  onChange={(e) => setEditingReminder({ ...editingReminder, days: parseInt(e.target.value) || 0 })}
                  className="w-16 bg-white border border-primary/20 px-3 py-2 rounded-lg text-center font-bold text-primary"
                />
                <span>{language === 'ar' ? 'أيام' : 'days'}</span>
              </div>

              <div className="space-y-4">
                <div className="bg-white border border-primary/20 rounded-lg p-1">
                  <input
                    type="text"
                    value={editingReminder.subject}
                    onChange={(e) => setEditingReminder({ ...editingReminder, subject: e.target.value })}
                    placeholder="Subject"
                    className="w-full px-4 py-3 text-sm font-bold border-none focus:ring-0 rounded-lg bg-white text-primary"
                  />
                </div>

                <div className="relative">
                  <textarea
                    value={editingReminder.message}
                    onChange={(e) => setEditingReminder({ ...editingReminder, message: e.target.value })}
                    className="w-full h-80 bg-white border border-primary/20 rounded-lg p-6 text-sm font-medium leading-relaxed focus:ring-2 focus:ring-primary/5 resize-none text-primary"
                  />
                  <div className="absolute top-4 right-4 text-right">
                    <button className="flex items-center gap-1 text-xs font-bold text-primary hover:underline">
                      {language === 'ar' ? 'إدراج نائب' : 'Insert placeholder'}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-primary/10 bg-primary/5 flex items-center justify-between">
              <button
                onClick={() => handleDeleteReminder(editingReminder.id)}
                className="text-red-600 font-bold hover:underline px-4 py-2"
              >
                {language === 'ar' ? 'حذف' : 'Delete'}
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsEditReminderModalOpen(false)}
                  className="px-8 py-3 rounded-full text-sm font-bold hover:bg-primary/10 transition-all text-primary"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                <button
                  onClick={handleSaveEditReminder}
                  className="bg-primary text-primary-foreground px-10 py-3 rounded-xl hover:opacity-90 transition-all font-bold"
                >{language === 'ar' ? 'حفظ' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isBatchDepositModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-primary/20 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsBatchDepositModalOpen(false);
          }}
        >
          <div className="bg-background border border-primary/20 w-full max-w-5xl h-[85vh] max-h-[calc(100dvh-2rem)] rounded-3xl shadow-2xl overflow-hidden flex flex-col">
            <div className="p-6 border-b border-primary/10 flex items-center justify-between bg-primary text-primary-foreground">
              <div className="flex items-center gap-3">
                <Banknote className="w-6 h-6" />
                <h2 className="text-2xl font-bold italic serif tracking-tight">{language === 'ar' ? 'دفعة مجمعة جديدة' : 'New Batch Payment'}</h2>
              </div>
              <button
                onClick={() => setIsBatchDepositModalOpen(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <XCircle className="w-8 h-8" />
              </button>
            </div>

            <div className="flex-1 flex overflow-hidden">
              {/* Left Sidebar: Invoice Selection */}
              <div className="w-1/3 border-r border-primary/20 bg-primary/5 flex flex-col">
                <div className="p-4 space-y-4 border-b border-primary/10">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40 text-primary" />
                    <input
                      type="text"
                      placeholder={language === 'ar' ? 'البحث عن رقم فاتورة أو عميل...' : "Search invoice number or customer..."}
                      value={batchSearch}
                      onChange={(e) => setBatchSearch(e.target.value)}
                      className="w-full bg-white border border-primary/20 pl-10 pr-4 py-2.5 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary/5 outline-none transition-all text-primary"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <SearchableSelect
                      value={batchStatusFilter}
                      onChange={(e) => setBatchStatusFilter(e.target.value)}
                      options={[
                        { value: 'all', label: language === 'ar' ? 'جميع الحالات' : 'All Statuses' },
                        { value: 'Awaiting Payment', label: language === 'ar' ? 'في انتظار الدفع' : 'Awaiting Payment' }
                      ]}
                      className="flex-1"
                    />
                    <button
                      onClick={() => {
                        const allAvailable = batchDepositInvoices.filter(inv => {
                          const matchesSearch = inv.number.toLowerCase().includes(batchSearch.toLowerCase()) ||
                                              inv.contactName?.toLowerCase().includes(batchSearch.toLowerCase());
                          const matchesStatus = batchStatusFilter === 'all' || inv.status === batchStatusFilter;
                          return matchesSearch && matchesStatus;
                        });
                        const newAllocations = { ...selectedInvoicesForBatch };
                        allAvailable.forEach(inv => {
                          if (!newAllocations[inv.id]) {
                            newAllocations[inv.id] = inv.totalAmount - (inv.paymentReceived || 0);
                          }
                        });
                        setSelectedInvoicesForBatch(newAllocations);
                      }}
                      className="px-3 py-2 bg-primary text-primary-foreground rounded-xl text-xs font-bold hover:opacity-90 transition-opacity"
                    >
                      {language === 'ar' ? 'إضافة الكل' : 'Add All'}
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-2">
                  {batchDepositInvoices
                    .filter(inv => {
                      const matchesSearch = inv.number.toLowerCase().includes(batchSearch.toLowerCase()) ||
                                          inv.contactName?.toLowerCase().includes(batchSearch.toLowerCase());
                      const matchesStatus = batchStatusFilter === 'all' || inv.status === batchStatusFilter;
                      return matchesSearch && matchesStatus;
                    })
                    .map(inv => (
                      <div
                        key={inv.id}
                        onClick={() => {
                          if (selectedInvoicesForBatch[inv.id]) {
                            const { [inv.id]: _, ...rest } = selectedInvoicesForBatch;
                            setSelectedInvoicesForBatch(rest);
                          } else {
                            setSelectedInvoicesForBatch(prev => ({
                              ...prev,
                              [inv.id]: inv.totalAmount - (inv.paymentReceived || 0)
                            }));
                          }
                        }}
                        className={cn(
                          "p-3 rounded-2xl border transition-all cursor-pointer hover:shadow-md group",
                          selectedInvoicesForBatch[inv.id]
                            ? "bg-primary text-primary-foreground border-primary scale-[0.98]"
                            : "bg-white border-primary/10 hover:border-primary/30"
                        )}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-bold text-sm tracking-tight">{inv.number}</span>
                          <span className={cn(
                            "text-[10px] font-bold px-2 py-0.5 rounded-full border",
                            selectedInvoicesForBatch[inv.id] ? "border-white/20" : "border-primary/10 opacity-50"
                          )}>
                            {inv.status}
                          </span>
                        </div>
                        <p className="text-xs opacity-60 font-medium mb-2 truncate">{inv.contactName}</p>
                        <div className="flex justify-between items-end border-t border-primary/10 pt-2 mt-2">
                          <div className="flex flex-col">
                            <span className="text-[10px] opacity-40 uppercase font-bold">{language === 'ar' ? 'الرصيد' : 'Balance'}</span>
                            <span className="text-xs font-bold">{(inv.totalAmount - (inv.paymentReceived || 0)).toLocaleString()} {inv.currency}</span>
                          </div>
                          {selectedInvoicesForBatch[inv.id] && (
                            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white">
                              <Check className="w-3 h-3" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Right Area: Allocation and Settings */}
              <div className="flex-1 bg-background p-8 overflow-y-auto custom-scrollbar flex flex-col">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-3xl font-bold italic serif tracking-tight text-primary">{language === 'ar' ? 'تخصيص الدفع' : 'Payment Allocation'}</h3>
                  <div className="flex flex-col items-end text-primary">
                    <span className="text-[10px] uppercase font-bold opacity-40 tracking-widest">{language === 'ar' ? 'الإجمالي' : 'TOTAL'}</span>
                    <span className="text-4xl font-bold font-mono tracking-tighter">
                      {Object.values(selectedInvoicesForBatch).reduce((sum, val) => sum + val, 0).toLocaleString()} <span className="text-xl">QAR</span>
                    </span>
                  </div>
                </div>

                {Object.keys(selectedInvoicesForBatch).length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4 opacity-30 text-primary">
                    <Banknote className="w-16 h-16 stroke-[1]" />
                    <div>
                      <p className="text-lg font-bold italic serif">{language === 'ar' ? 'لم يتم اختيار أي فاتورة' : 'No Invoices Selected'}</p>
                      <p className="text-sm">{language === 'ar' ? 'اختر فواتير من اليسار لتسجيل إيداع مجمع' : 'Select invoices from the left to record a batch deposit'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 flex-1">
                    <div className="flex justify-end mb-4">
                      <button
                        onClick={() => {
                          const totalSpecified = batchDepositSettings.note ? parseFloat(batchDepositSettings.note) || 0 : 0;
                        }}
                        className="text-xs font-bold text-primary hover:underline flex items-center gap-1.5"
                      >
                        <ArrowRight className="w-3 h-3 rotate-45" />
                        {language === 'ar' ? 'توزيع تلقائي' : 'Auto Distribute'}
                      </button>
                    </div>
                    <div className="space-y-3">
                      {Object.entries(selectedInvoicesForBatch).map(([id, amount]) => {
                        const inv = invoices.find(i => i.id === id);
                        if (!inv) return null;
                        const balance = inv.totalAmount - (inv.paymentReceived || 0);
                        return (
                          <div key={id} className="bg-white border border-primary/20 p-5 rounded-2xl flex items-center justify-between shadow-sm hover:shadow-md transition-shadow group text-primary">
                            <div className="flex flex-col gap-0.5">
                              <div className="flex items-center gap-2">
                                <span className="font-bold">{inv.number}</span>
                                <span className="text-[10px] opacity-40 font-bold">•</span>
                                <span className="text-xs font-bold opacity-60 truncate max-w-[150px]">{inv.contactName}</span>
                              </div>
                              <span className="text-[10px] opacity-40 font-bold uppercase tracking-wider">{language === 'ar' ? 'الرصيد' : 'Balance'}: {(balance || 0).toLocaleString()} {inv.currency}</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="relative">
                                <input
                                  type="number"
                                  value={amount}
                                  onChange={(e) => setSelectedInvoicesForBatch(prev => ({ ...prev, [id]: parseFloat(e.target.value) || 0 }))}
                                  className="bg-primary/5 border border-primary/20 pl-4 pr-12 py-3 rounded-xl w-40 text-right font-mono font-bold focus:bg-white focus:ring-2 focus:ring-primary/10 transition-all outline-none"
                                />
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-bold opacity-30 pointer-events-none">QAR</span>
                              </div>
                              <button
                                onClick={() => {
                                  const { [id]: _, ...rest } = selectedInvoicesForBatch;
                                  setSelectedInvoicesForBatch(rest);
                                }}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-8 pt-8 border-t border-primary/20 grid grid-cols-2 gap-8 bg-primary/5 p-10 rounded-[40px] shadow-inner">
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest opacity-50 px-2">{language === 'ar' ? 'حساب الإيداع' : 'DEPOSIT ACCOUNT'}</label>
                      <SearchableSelect
                        value={batchDepositSettings.accountId}
                        onChange={(e) => setBatchDepositSettings(prev => ({ ...prev, accountId: e.target.value }))}
                        options={[
                          { value: '', label: 'Select Account' },
                          ...bankAccountsList.map(acc => ({ value: acc.id, label: `${acc.accountName} (${acc.accountNumber})`, group: 'Bank Accounts' })),
                          ...accountsList.filter(acc => acc.enablePayments).map(acc => ({ value: acc.id, label: `${acc.code} - ${acc.name}`, group: 'Chart of Accounts' }))
                        ]}
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest opacity-50 px-2">{language === 'ar' ? 'التاريخ' : 'DATE'}</label>
                      <input
                        type="date"
                        value={batchDepositSettings.date}
                        onChange={(e) => setBatchDepositSettings(prev => ({ ...prev, date: e.target.value }))}
                        className="w-full bg-white border border-primary/20 p-4 rounded-2xl outline-none focus:ring-2 focus:ring-primary/10 transition-all font-bold shadow-sm text-primary"
                      />
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest opacity-50 px-2">{language === 'ar' ? 'المرجع / ملاحظة' : 'REFERENCE / NOTE'}</label>
                      <input
                        type="text"
                        value={batchDepositSettings.reference}
                        onChange={(e) => setBatchDepositSettings(prev => ({ ...prev, reference: e.target.value }))}
                        placeholder="Batch reference..."
                        className="w-full bg-white border border-primary/20 p-4 rounded-2xl outline-none focus:ring-2 focus:ring-primary/10 transition-all font-bold shadow-sm text-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest opacity-50 px-2">{language === 'ar' ? 'إجمالي الدفع' : 'TOTAL PAYMENT'}</label>
                      <div className="w-full bg-primary text-primary-foreground p-4 rounded-2xl font-mono font-bold flex justify-between items-center shadow-md">
                        <span className="opacity-40 text-[10px] tracking-widest uppercase">{language === 'ar' ? 'محسوب' : 'Calculated'}</span>
                        <span className="text-xl tracking-tight leading-none">
                          {Object.values(selectedInvoicesForBatch).reduce((sum, val) => sum + val, 0).toLocaleString()} <span className="text-xs opacity-50">QAR</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 border-t border-primary/20 bg-white flex justify-end gap-3 shadow-2xl">
              <button
                onClick={() => setIsBatchDepositModalOpen(false)}
                className="px-10 py-4 rounded-2xl text-xs font-bold uppercase tracking-widest hover:bg-primary/5 transition-all text-primary border border-primary/20"
              >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
              <button
                disabled={isSaving || Object.keys(selectedInvoicesForBatch).length === 0 || !batchDepositSettings.accountId}
                onClick={handleSaveBatchDeposit}
                className="bg-primary text-primary-foreground px-14 py-4 rounded-2xl hover:opacity-90 disabled:opacity-30 transition-all font-bold flex items-center gap-3 shadow-xl"
              >
                {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wallet className="w-5 h-5" />}
                <span className="uppercase tracking-widest text-xs">{language === 'ar' ? 'دفع' : 'Pay'} {Object.keys(selectedInvoicesForBatch).length} {language === 'ar' ? 'فواتير' : 'Invoices'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Note Creation Modal */}
      {isDeliveryModalOpen && activeDeliveryNote && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-primary/20 backdrop-blur-sm z-[250] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsDeliveryModalOpen(false);
              setActiveDeliveryNote(null);
            }
          }}
        >
          <div className="bg-background border border-primary/20 w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)]">
            <div className="p-6 border-b border-primary/10 flex items-center justify-between bg-primary text-primary-foreground">
              <div className="flex items-center gap-3">
                <Truck className="w-6 h-6" />
                <h3 className="text-xl font-bold italic serif">{language === 'ar' ? 'إنشاء مذكرة تسليم' : 'Create Delivery Note'}</h3>
              </div>
              <button
                onClick={() => {
                  setIsDeliveryModalOpen(false);
                  setActiveDeliveryNote(null);
                }}
                className="p-1 hover:bg-white/10 rounded-full transition-all"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-1">
                  <p className="text-[10px] font-bold opacity-40 uppercase tracking-widest leading-none text-primary">{language === 'ar' ? 'الفاتورة' : 'Invoice'}</p>
                  <p className="text-sm font-bold text-primary italic serif">{activeDeliveryNote.invoiceNumber}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-bold opacity-40 uppercase tracking-widest leading-none text-primary">{language === 'ar' ? 'العميل' : 'Customer'}</p>
                  <p className="text-sm font-bold text-primary italic serif">{activeDeliveryNote.contactName}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold uppercase tracking-widest text-primary">{language === 'ar' ? 'الأصناف المراد شحنها' : 'Items to Ship'}</h4>
                </div>
                <div className="border border-primary/10 rounded-2xl overflow-hidden shadow-sm bg-white">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-primary/5">
                      <tr>
                        <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-widest opacity-40 text-primary">{language === 'ar' ? 'الصنف' : 'Item'}</th>
                        <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-widest opacity-40 text-center text-primary">{language === 'ar' ? 'تم الطلب' : 'Ordered'}</th>
                        <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-widest opacity-40 text-center text-primary">{language === 'ar' ? 'تم الشحن' : 'Shipped'}</th>
                        <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-widest opacity-40 text-center text-primary">{language === 'ar' ? 'للشحن' : 'To Ship'}</th>
                        <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-widest opacity-40 text-primary">{language === 'ar' ? 'رقم اللوط / الصلاحية' : 'Lot # / Expiry'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-primary/5">
                      {(activeDeliveryNote.items || []).map((item, idx) => (
                        <tr key={idx} className="text-primary">
                          <td className="py-3 px-4">
                            <p className="text-xs font-bold leading-tight">{item.itemCode}</p>
                            <p className="text-[10px] opacity-60 truncate max-w-[200px]">{item.description}</p>
                          </td>
                          <td className="py-3 px-4 text-center text-xs font-bold">{item.quantityOrdered}</td>
                          <td className="py-3 px-4 text-center text-xs font-bold text-primary">{item.quantityShipped}</td>
                          <td className="py-3 px-4 text-center">
                            <input
                              type="number"
                              min="0"
                              max={item.quantityOrdered - item.quantityShipped}
                              value={item.qtyToShipNow}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                const maxVal = item.quantityOrdered - item.quantityShipped;
                                const finalVal = Math.min(val, maxVal);

                                const newItems = [...activeDeliveryNote.items];
                                newItems[idx] = { ...item, qtyToShipNow: finalVal };
                                setActiveDeliveryNote({ ...activeDeliveryNote, items: newItems });
                              }}
                              className="w-20 bg-white border border-primary/20 p-1.5 rounded-lg text-center font-bold text-sm focus:ring-2 focus:ring-primary/10 text-primary"
                            />
                          </td>
                          <td className="py-3 px-4">
                          <div className="space-y-1">
                            <SearchableSelect
                              value={item.lotNumber || ''}
                              onChange={(e) => {
                                const lotNo = e.target.value;
                                const lot = lotNumbers.find(l => l.lotNumber === lotNo);

                                const newItems = [...activeDeliveryNote.items];
                                newItems[idx] = {
                                  ...item,
                                  lotNumber: lotNo,
                                  expiryDate: lot?.expiryDate || ''
                                };
                                setActiveDeliveryNote({ ...activeDeliveryNote, items: newItems });
                              }}
                              options={[
                                { value: '', label: 'Select Lot...' },
                                ...lotNumbers
                                  .filter(l => l.productId === (item.itemId || item.itemCode))
                                  .map(l => ({ value: l.lotNumber, label: l.lotNumber }))
                              ]}
                              className="w-full"
                            />
                            {item.expiryDate && (
                              <p className="text-[9px] text-primary/50 font-bold uppercase tracking-tighter leading-none">Exp: {item.expiryDate}</p>
                            )}
                          </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold opacity-40 uppercase tracking-widest leading-none text-primary">{language === 'ar' ? 'تعيين موظف التوصيل' : 'Assign Delivery Personnel'}</label>
                  <SearchableSelect
                    value={activeDeliveryNote.assignedPersonnelId}
                    onChange={(e) => {
                      const user = deliveryPersonnel.find(u => u.id === e.target.value);
                      const userProfile = user?.email ? allUsers[user.email.toLowerCase()] : null;
                      const displayName = user?.fullName || userProfile?.displayName || userProfile?.username || user?.displayName || user?.name || user?.email?.split('@')[0] || '';

                      setActiveDeliveryNote({
                        ...activeDeliveryNote,
                        assignedPersonnelId: e.target.value,
                        assignedPersonnelName: displayName,
                        assignedPersonnelEmail: user?.email || ''
                      });
                    }}
                    options={[
                      { value: '', label: language === 'ar' ? 'اختر الموظف' : 'Select Personnel' },
                      ...deliveryPersonnel.map(person => {
                        const userProfile = person.email ? allUsers[person.email.toLowerCase()] : null;
                        const displayName = person.fullName || userProfile?.displayName || userProfile?.username || person.displayName || person.name || person.email?.split('@')[0] || person.email;
                        return { value: person.id, label: displayName };
                      })
                    ]}
                    className="w-full"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold opacity-40 uppercase tracking-widest leading-none text-primary">{language === 'ar' ? 'النسق' : 'Branding Theme'}</label>
                  <SearchableSelect
                    value={activeDeliveryNote.brandingTheme}
                    onChange={(e) => setActiveDeliveryNote({ ...activeDeliveryNote, brandingTheme: e.target.value })}
                    options={[
                      { value: 'Standard', label: language === 'ar' ? 'أساسي' : 'Standard' },
                      ...brandingThemes.map(theme => ({ value: theme.id, label: theme.name }))
                    ]}
                    className="w-full"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold opacity-40 uppercase tracking-widest leading-none text-primary">{language === 'ar' ? 'المستودع' : 'Warehouse'}</label>
                  <SearchableSelect
                    value={activeDeliveryNote.warehouseId || ''}
                    onChange={(e) => {
                      const selected = [...warehouses, ...stores].find(w => w.id === e.target.value);
                      setActiveDeliveryNote({
                        ...activeDeliveryNote,
                        warehouseId: e.target.value,
                        warehouseName: selected?.name || selected?.storeName || ''
                      });
                    }}
                    options={[
                      { value: '', label: language === 'ar' ? 'اختر المستودع' : 'Select Warehouse' },
                      ...warehouses.map(warehouse => ({ value: warehouse.id, label: warehouse.name, group: 'Warehouses' })),
                      ...stores.map(store => ({ value: store.id, label: store.name || store.storeName, group: 'Store Locations' }))
                    ]}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <div className="p-8 border-t border-primary/10 bg-primary/5 flex justify-end gap-3 shadow-xl">
              <button
                onClick={() => {
                  setIsDeliveryModalOpen(false);
                  setActiveDeliveryNote(null);
                }}
                className="px-8 py-3 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-primary/10 transition-all text-primary border border-primary/20"
              >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
              <button
                onClick={handleSaveDeliveryNote}
                disabled={isSaving}
                className="bg-primary text-primary-foreground px-10 py-3 rounded-xl hover:opacity-90 disabled:opacity-50 transition-all font-bold flex items-center gap-2 shadow-lg"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                <Truck className="w-4 h-4" />
                <span>{language === 'ar' ? 'إنشاء شحنة' : 'Create Shipment'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proof of Delivery (POD) Modal */}
      {isPODModalOpen && activeDeliveryNote && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-primary/20 backdrop-blur-md z-[300] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsPODModalOpen(false);
          }}
        >
          <div className="bg-background border border-primary/20 w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)]">
            <div className="p-6 border-b border-primary/10 flex items-center justify-between bg-primary text-primary-foreground shadow-md dark:bg-[#000000]">
              <div className="flex items-center gap-3">
                <UserCheck className="w-6 h-6" />
                <h3 className="text-xl font-bold italic serif tracking-tight">{language === 'ar' ? 'تأكيد التوصيل' : 'Confirm Delivery'}</h3>
              </div>
              <button onClick={() => setIsPODModalOpen(false)} className="p-1 hover:bg-white/10 rounded-full transition-all text-primary-foreground dark:text-white">
                <XCircle className="w-8 h-8" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6 custom-scrollbar">
              <div className="space-y-4 text-primary">
                <div>
                  <label className="text-[10px] font-bold opacity-50 uppercase tracking-widest mb-1.5 block">{language === 'ar' ? 'اسم المستلم (مطلوب)' : 'Receiver Name (Required)'}</label>
                  <input
                    type="text"
                    required
                    value={podData.receiverName}
                    onChange={(e) => setPodData({ ...podData, receiverName: e.target.value })}
                    placeholder="Enter receiver's name..."
                    className="w-full bg-white border border-primary/20 p-4 rounded-xl font-bold text-sm focus:ring-4 focus:ring-primary/10 outline-none shadow-sm text-primary"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold opacity-50 uppercase tracking-widest mb-1.5 block">{language === 'ar' ? 'التوقيع الرقمي (مطلوب)' : 'Digital Signature (Required)'}</label>
                  <div className="border-2 border-dashed border-primary/20 rounded-2xl bg-white overflow-hidden shadow-inner relative group">
                    <SignatureCanvas
                      ref={sigCanvas}
                      penColor="black"
                      canvasProps={{
                        className: 'w-full h-48 cursor-crosshair'
                      }}
                    />
                    <button
                      onClick={() => sigCanvas.current?.clear()}
                      className="absolute top-2 right-2 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all opacity-0 group-hover:opacity-100"
                      title={language === 'ar' ? 'مسح' : 'Clear'}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold opacity-50 uppercase tracking-widest mb-1.5 block">{language === 'ar' ? 'صورة الشحنة' : 'Package Photo'}</label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      id="pod-photo-capture"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setPodData({ ...podData, photo: file });
                      }}
                    />
                    <button
                      onClick={() => document.getElementById('pod-photo-capture')?.click()}
                      className={cn(
                        "w-full flex items-center justify-center gap-3 p-4 rounded-xl border-2 border-dashed transition-all",
                        podData.photo ? "bg-green-50 border-green-500 text-green-700 font-bold" : "bg-white border-primary/20 text-primary/40 hover:border-primary hover:text-primary"
                      )}
                    >
                      {podData.photo ? (
                        <>
                          <CheckCircle2 className="w-5 h-5" />
                          <span>{language === 'ar' ? 'تم التقاط الصورة' : 'Photo Captured'}</span>
                        </>
                      ) : (
                        <>
                          <Camera className="w-5 h-5" />
                          <span>{language === 'ar' ? 'التقاط/رفع صورة الشحنة' : 'Take/Upload Package Photo'}</span>
                        </>
                      )}
                    </button>
                    {podData.photo && (
                       <button
                        onClick={() => setPodData({ ...podData, photo: null })}
                        className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 shadow-lg"
                       >
                         <X className="w-4 h-4" />
                       </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold opacity-50 uppercase tracking-widest mb-1.5 block">{language === 'ar' ? 'ملاحظات التوصيل' : 'Delivery Notes'}</label>
                  <textarea
                    value={podData.notes}
                    onChange={(e) => setPodData({ ...podData, notes: e.target.value })}
                    placeholder={language === 'ar' ? 'أي تعليمات توصيل محددة أو مشاكل...' : "Any specific delivery instructions or issues..."}
                    className="w-full h-24 bg-white border border-primary/20 p-4 rounded-xl text-sm font-medium focus:ring-4 focus:ring-primary/10 outline-none shadow-sm resize-none text-primary"
                  />
                </div>
              </div>
            </div>

            <div className="p-8 border-t border-primary/10 bg-primary/5 flex flex-col gap-3 shadow-2xl">
              <button
                onClick={handleConfirmPOD}
                disabled={isSubmittingPOD}
                className="w-full bg-primary text-primary-foreground py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:opacity-90 transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {isSubmittingPOD ? <Loader2 className="w-5 h-5 animate-spin" /> : <Signature className="w-5 h-5" />}
                <span>{language === 'ar' ? 'تأكيد وتوقيع التوصيل' : 'Confirm & Sign Delivery'}</span>
              </button>
              <button
                onClick={() => setIsPODModalOpen(false)}
                disabled={isSubmittingPOD}
                className="w-full py-4 text-primary opacity-40 font-bold uppercase tracking-widest text-[10px] hover:opacity-100 transition-colors"
              >
                {language === 'ar' ? 'العودة' : 'Go Back'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Modal */}
      {isPrintModalOpen && docToPrint && (
        <div className="fixed inset-0 bg-primary/20 backdrop-blur-sm z-[250] flex flex-col items-center overflow-auto p-8 print:p-0 print:bg-white print:block">
          <div className="flex flex-col gap-6 w-full max-w-[850px] print:w-full print:max-w-none">
            <div className="flex items-center justify-between bg-white/80 backdrop-blur-lg p-4 rounded-2xl border border-primary/10 shadow-lg print:hidden">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                  <Printer className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-sm tracking-tight">Print Preview</h3>
                  <p className="text-[10px] opacity-50 font-bold uppercase tracking-widest">{docToPrint.number}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-xl font-bold text-xs hover:opacity-90 transition-all shadow-md active:scale-95"
                >
                  <Printer className="w-4 h-4" />
                  Print Now
                </button>
                <button
                  onClick={() => setIsPrintModalOpen(false)}
                  className="p-2.5 hover:bg-red-50 text-red-600 rounded-xl transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="bg-white shadow-2xl rounded-sm overflow-hidden print:shadow-none print:rounded-none">
              <DocumentPrintLayout
                organization={organization}
                document={docToPrint}
                contact={contacts.find(c => c.id === docToPrint.contactId)}
                type={printType}
                language={language}
              />
            </div>

            <div className="flex justify-center pb-12 print:hidden">
               <p className="text-[10px] items-center gap-2 flex font-bold uppercase tracking-widest opacity-40">
                  <span className="w-2 h-2 bg-primary/20 rounded-full animate-pulse" />
                  Standard Odoo-Matching Print Layout
               </p>
            </div>
          </div>
        </div>
      )}

      {/* New Contact Modal */}
      {isContactModalOpen && (
        <div className="fixed inset-0 bg-primary/20 backdrop-blur-sm z-[220] flex items-center justify-center p-4">
          <div className="bg-background border border-primary/20 w-full max-w-md rounded-3xl p-8 shadow-2xl text-primary">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold italic serif">{language === 'ar' ? 'إضافة جهة اتصال جديدة' : 'Add New Contact'}</h2>
              <button onClick={() => setIsContactModalOpen(false)} className="p-1 hover:bg-primary/10 rounded-full transition-all">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddContact} className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1 opacity-50">{language === 'ar' ? 'الاسم' : 'Name'} *</label>
                <input
                  type="text"
                  required
                  value={newContact.name}
                  onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-white border border-primary/20 p-2 rounded-lg text-sm outline-none text-primary"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1 opacity-50">{language === 'ar' ? 'البريد الإلكتروني (اختياري)' : 'Email (optional)'}</label>
                <input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-white border border-primary/20 p-2 rounded-lg text-sm outline-none text-primary"
                />
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsContactModalOpen(false)}
                  className="flex-1 py-2 rounded-full border border-primary/20 text-xs font-bold hover:bg-primary/5 transition-all text-primary"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                <button
                  type="submit"
                  className="flex-1 py-2 rounded-full bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 transition-all shadow-md"
                >
                  {language === 'ar' ? 'حفظ جهة الاتصال' : 'Save Contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
